/**
 * Translation utilities for plan names, auth pages, header, bottom nav, journal, favorites, and common strings
 */
import type { Language } from '@/types';
import type { AuthErrorKey } from './authErrors';
import { selectPlural } from './locale';

/**
 * Header translation keys
 */
export type HeaderTranslationKey =
  | 'history'
  | 'profile'
  | 'logout'
  | 'login'
  | 'getStarted'
  | 'language';

/**
 * Header translations for all 12 supported languages
 */
const HEADER_TRANSLATIONS: Record<HeaderTranslationKey, Record<Language, string>> = {
  history: {
    me: 'Istorija',
    en: 'History',
    sr: 'Istorija',
    hr: 'Povijest',
    bs: 'Historija',
    sl: 'Zgodovina',
    mk: 'Историја',
    sq: 'Historia',
    bg: 'История',
    ro: 'Istoric',
    el: 'Ιστορικό',
    tr: 'Geçmiş',
  },
  profile: {
    me: 'Profil',
    en: 'Profile',
    sr: 'Profil',
    hr: 'Profil',
    bs: 'Profil',
    sl: 'Profil',
    mk: 'Профил',
    sq: 'Profili',
    bg: 'Профил',
    ro: 'Profil',
    el: 'Προφίλ',
    tr: 'Profil',
  },
  logout: {
    me: 'Odjava',
    en: 'Logout',
    sr: 'Odjava',
    hr: 'Odjava',
    bs: 'Odjava',
    sl: 'Odjava',
    mk: 'Одјава',
    sq: 'Dil',
    bg: 'Изход',
    ro: 'Deconectare',
    el: 'Αποσύνδεση',
    tr: 'Çıkış',
  },
  login: {
    me: 'Prijava',
    en: 'Login',
    sr: 'Prijava',
    hr: 'Prijava',
    bs: 'Prijava',
    sl: 'Prijava',
    mk: 'Најава',
    sq: 'Hyrje',
    bg: 'Вход',
    ro: 'Autentificare',
    el: 'Σύνδεση',
    tr: 'Giriş',
  },
  getStarted: {
    me: 'Započni',
    en: 'Get Started',
    sr: 'Započni',
    hr: 'Započni',
    bs: 'Započni',
    sl: 'Začni',
    mk: 'Започни',
    sq: 'Fillo',
    bg: 'Започни',
    ro: 'Începe',
    el: 'Ξεκίνα',
    tr: 'Başla',
  },
  language: {
    me: 'Jezik',
    en: 'Language',
    sr: 'Jezik',
    hr: 'Jezik',
    bs: 'Jezik',
    sl: 'Jezik',
    mk: 'Јазик',
    sq: 'Gjuha',
    bg: 'Език',
    ro: 'Limbă',
    el: 'Γλώσσα',
    tr: 'Dil',
  },
};

/**
 * Get translated header string for the given language
 * @param key - The translation key
 * @param language - The target language
 * @returns The translated string
 */
export function getHeaderTranslation(key: HeaderTranslationKey, language: Language): string {
  return HEADER_TRANSLATIONS[key][language];
}

/**
 * Bottom navigation translation keys (Luka's APPNAV: Početna · Akustične · Vođene · Dnevnik · Omiljene · Pretplata · Profil).
 * The short labels fit 7 tabs at 320 px; `acousticSessions` and `guidedSessions` are the full names used as
 * aria-labels and as desktop header links (each contains its short label).
 */
export type BottomNavTranslationKey =
  | 'home'
  | 'acoustic'
  | 'guided'
  | 'journal'
  | 'favorites'
  | 'subscription'
  | 'profile'
  | 'acousticSessions'
  | 'guidedSessions';

/**
 * Bottom navigation translations for all 12 supported languages
 */
const BOTTOM_NAV_TRANSLATIONS: Record<BottomNavTranslationKey, Record<Language, string>> = {
  home: {
    me: 'Početna',
    en: 'Home',
    sr: 'Početna',
    hr: 'Početna',
    bs: 'Početna',
    sl: 'Domov',
    mk: 'Почетна',
    sq: 'Kreu',
    bg: 'Начало',
    ro: 'Acasă',
    el: 'Αρχική',
    tr: 'Ana Sayfa',
  },
  acoustic: {
    me: 'Akustične',
    en: 'Acoustic',
    sr: 'Akustične',
    hr: 'Akustične',
    bs: 'Akustične',
    sl: 'Akustične',
    mk: 'Акустични',
    sq: 'Akustike',
    bg: 'Акустични',
    ro: 'Acustice',
    el: 'Ηχητικές',
    tr: 'Akustik',
  },
  guided: {
    me: 'Vođene',
    en: 'Guided',
    sr: 'Vođene',
    hr: 'Vođene',
    bs: 'Vođene',
    sl: 'Vodene',
    mk: 'Водени',
    sq: 'Me zë',
    bg: 'Водени',
    ro: 'Ghidate',
    el: 'Με οδηγό',
    tr: 'Rehberli',
  },
  journal: {
    me: 'Dnevnik',
    en: 'Journal',
    sr: 'Dnevnik',
    hr: 'Dnevnik',
    bs: 'Dnevnik',
    sl: 'Dnevnik',
    mk: 'Дневник',
    sq: 'Ditari',
    bg: 'Дневник',
    ro: 'Jurnal',
    el: 'Ημερολόγιο',
    tr: 'Günlük',
  },
  favorites: {
    me: 'Omiljene',
    en: 'Favorites',
    sr: 'Omiljene',
    hr: 'Favoriti',
    bs: 'Omiljene',
    sl: 'Najljubše',
    mk: 'Омилени',
    sq: 'Favoritet',
    bg: 'Любими',
    ro: 'Favorite',
    el: 'Αγαπημένα',
    tr: 'Favoriler',
  },
  subscription: {
    me: 'Pretplata',
    en: 'Plans',
    sr: 'Pretplata',
    hr: 'Pretplata',
    bs: 'Pretplata',
    sl: 'Naročnina',
    mk: 'Претплата',
    sq: 'Abonimi',
    bg: 'Планове',
    ro: 'Planuri',
    el: 'Πακέτα',
    tr: 'Abonelik',
  },
  profile: {
    me: 'Profil',
    en: 'Profile',
    sr: 'Profil',
    hr: 'Profil',
    bs: 'Profil',
    sl: 'Profil',
    mk: 'Профил',
    sq: 'Profili',
    bg: 'Профил',
    ro: 'Profil',
    el: 'Προφίλ',
    tr: 'Profil',
  },
  acousticSessions: {
    me: 'Akustične sesije',
    en: 'Acoustic sessions',
    sr: 'Akustične sesije',
    hr: 'Akustične sesije',
    bs: 'Akustične sesije',
    sl: 'Akustične seje',
    mk: 'Акустични сесии',
    sq: 'Seancat akustike',
    bg: 'Акустични сесии',
    ro: 'Sesiuni acustice',
    el: 'Ηχητικές συνεδρίες',
    tr: 'Akustik seanslar',
  },
  guidedSessions: {
    me: 'Vođene sesije',
    en: 'Guided sessions',
    sr: 'Vođene sesije',
    hr: 'Vođene sesije',
    bs: 'Vođene sesije',
    sl: 'Vodene seje',
    mk: 'Водени сесии',
    sq: 'Seancat me zë',
    bg: 'Водени сесии',
    ro: 'Sesiuni ghidate',
    el: 'Συνεδρίες με οδηγό',
    tr: 'Rehberli seanslar',
  },
};

/**
 * Get translated bottom navigation string for the given language
 * @param key - The translation key
 * @param language - The target language
 * @returns The translated string
 */
export function getBottomNavTranslation(key: BottomNavTranslationKey, language: Language): string {
  return BOTTOM_NAV_TRANSLATIONS[key][language];
}

/**
 * Not Found (404) page translation keys
 */
export type NotFoundTranslationKey =
  | 'pageTitle'
  | 'pageDesc'
  | 'goHome'
  | 'goBack';

/**
 * Not Found (404) translations for all 12 supported languages
 */
const NOT_FOUND_TRANSLATIONS: Record<NotFoundTranslationKey, Record<Language, string>> = {
  pageTitle: {
    me: 'Stranica nije pronađena',
    en: 'Page Not Found',
    sr: 'Stranica nije pronađena',
    hr: 'Stranica nije pronađena',
    bs: 'Stranica nije pronađena',
    sl: 'Stran ni najdena',
    mk: 'Страницата не е пронајдена',
    sq: 'Faqja nuk u gjet',
    bg: 'Страницата не е намерена',
    ro: 'Pagina nu a fost găsită',
    el: 'Η σελίδα δεν βρέθηκε',
    tr: 'Sayfa Bulunamadı',
  },
  pageDesc: {
    me: 'Stranica koju tražiš ne postoji ili je premještena.',
    en: "The page you're looking for doesn't exist or has been moved.",
    sr: 'Stranica koju tražiš ne postoji ili je premeštena.',
    hr: 'Stranica koju tražiš ne postoji ili je premještena.',
    bs: 'Stranica koju tražiš ne postoji ili je premještena.',
    sl: 'Stran, ki jo iščeš, ne obstaja ali je bila premaknjena.',
    mk: 'Страницата што ја бараш не постои или е преместена.',
    sq: 'Faqja që kërkon nuk ekziston ose është zhvendosur.',
    bg: 'Страницата, която търсиш, не съществува или е преместена.',
    ro: 'Pagina pe care o cauți nu există sau a fost mutată.',
    el: 'Η σελίδα που αναζητάς δεν υπάρχει ή έχει μετακινηθεί.',
    tr: 'Aradığın sayfa mevcut değil veya taşınmış.',
  },
  goHome: {
    me: 'Idi na početnu',
    en: 'Go Home',
    sr: 'Idi na početnu',
    hr: 'Idi na početnu',
    bs: 'Idi na početnu',
    sl: 'Na domačo stran',
    mk: 'Оди на почетна',
    sq: 'Shko në fillim',
    bg: 'Към началото',
    ro: 'Acasă',
    el: 'Αρχική σελίδα',
    tr: 'Ana Sayfaya Git',
  },
  goBack: {
    me: 'Vrati se nazad',
    en: 'Go Back',
    sr: 'Vrati se nazad',
    hr: 'Vrati se natrag',
    bs: 'Vrati se nazad',
    sl: 'Pojdi nazaj',
    mk: 'Врати се назад',
    sq: 'Kthehu mbrapa',
    bg: 'Върни се назад',
    ro: 'Înapoi',
    el: 'Επιστροφή',
    tr: 'Geri Dön',
  },
};

/**
 * Get translated Not Found page string for the given language
 * @param key - The translation key
 * @param language - The target language
 * @returns The translated string
 */
export function getNotFoundTranslation(key: NotFoundTranslationKey, language: Language): string {
  return NOT_FOUND_TRANSLATIONS[key][language];
}

/**
 * Auth page translation keys
 */
export type AuthTranslationKey =
  // Login page
  | 'welcomeBack'
  | 'signInToContinue'
  | 'email'
  | 'enterYourEmail'
  | 'password'
  | 'enterYourPassword'
  | 'signIn'
  | 'orContinueWith'
  | 'dontHaveAccount'
  | 'signUp'
  // Register page
  | 'createAccount'
  | 'startFreeTrial'
  | 'fullName'
  | 'enterYourName'
  | 'createPassword'
  | 'confirmPassword'
  | 'confirmYourPassword'
  | 'atLeast8Characters'
  | 'oneUppercaseLetter'
  | 'oneLowercaseLetter'
  | 'oneNumber'
  | 'nameMustBe2Characters'
  | 'passwordDoesNotMeetRequirements'
  | 'passwordTooLong'
  | 'passwordsDoNotMatch'
  | 'alreadyHaveAccount'
  | 'termsAndPrivacy'
  | 'termsLink'
  | 'privacyLink'
  // Forgot password
  | 'forgotPassword'
  | 'forgotPasswordDesc'
  | 'sendResetLink'
  | 'resetLinkSent'
  | 'resetLinkSentDesc'
  | 'backToLogin'
  // Reset password
  | 'resetPassword'
  | 'resetPasswordDesc'
  | 'newPassword'
  | 'enterNewPassword'
  | 'confirmNewPassword'
  | 'passwordResetSuccess'
  | 'passwordResetSuccessDesc'
  | 'invalidResetLink'
  | 'invalidResetLinkDesc'
  // Email confirmation
  | 'emailConfirmed'
  | 'emailConfirmedDesc'
  | 'emailConfirmExpired'
  | 'emailConfirmExpiredDesc'
  | 'emailConfirmInvalid'
  | 'emailConfirmInvalidDesc'
  | 'checkYourEmail'
  | 'checkYourEmailDesc'
  | 'resendConfirmation'
  | 'confirmationResent'
  // Accessibility
  | 'showPassword'
  | 'hidePassword'
  | 'confirmingEmail';

/**
 * Auth translations for all 12 supported languages
 */
const AUTH_TRANSLATIONS: Record<AuthTranslationKey, Record<Language, string>> = {
  // Login page translations
  welcomeBack: {
    me: 'Drago nam je što si opet tu',
    en: 'Welcome Back',
    sr: 'Drago nam je što si opet tu',
    hr: 'Drago nam je što si opet tu',
    bs: 'Drago nam je što si opet tu',
    sl: 'Lepo, da si spet tu',
    mk: 'Добредојде повторно',
    sq: 'Mirë se erdhe përsëri',
    bg: 'Радваме се, че отново си тук',
    ro: 'Bine ai revenit',
    el: 'Καλώς ήρθες ξανά',
    tr: 'Tekrar hoş geldin',
  },
  signInToContinue: {
    me: 'Prijavi se da nastaviš sa praksom',
    en: 'Sign in to continue your practice',
    sr: 'Prijavi se da nastaviš sa praksom',
    hr: 'Prijavi se i nastavi s praksom',
    bs: 'Prijavi se da nastaviš s praksom',
    sl: 'Prijavi se in nadaljuj s prakso',
    mk: 'Најави се за да продолжиш со праксата',
    sq: 'Identifikohu për të vazhduar praktikën',
    bg: 'Влез, за да продължиш практиката си',
    ro: 'Conectează-te pentru a-ți continua practica',
    el: 'Συνδέσου για να συνεχίσεις την πρακτική σου',
    tr: 'Pratiğine devam etmek için giriş yap',
  },
  email: {
    me: 'Email',
    en: 'Email',
    sr: 'Email',
    hr: 'Email',
    bs: 'Email',
    sl: 'E-pošta',
    mk: 'Е-пошта',
    sq: 'Email',
    bg: 'Имейл',
    ro: 'Email',
    el: 'Email',
    tr: 'E-posta',
  },
  enterYourEmail: {
    me: 'Unesi email',
    en: 'Enter your email',
    sr: 'Unesi email',
    hr: 'Unesi email',
    bs: 'Unesi email',
    sl: 'Vnesi e-pošto',
    mk: 'Внеси ја својата е-пошта',
    sq: 'Shkruaj email-in tënd',
    bg: 'Въведи своя имейл',
    ro: 'Introdu emailul tău',
    el: 'Γράψε το email σου',
    tr: 'E-postanı gir',
  },
  password: {
    me: 'Lozinka',
    en: 'Password',
    sr: 'Lozinka',
    hr: 'Lozinka',
    bs: 'Lozinka',
    sl: 'Geslo',
    mk: 'Лозинка',
    sq: 'Fjalëkalimi',
    bg: 'Парола',
    ro: 'Parolă',
    el: 'Κωδικός πρόσβασης',
    tr: 'Şifre',
  },
  enterYourPassword: {
    me: 'Unesi lozinku',
    en: 'Enter your password',
    sr: 'Unesi lozinku',
    hr: 'Unesi lozinku',
    bs: 'Unesi lozinku',
    sl: 'Vnesi geslo',
    mk: 'Внеси ја својата лозинка',
    sq: 'Shkruaj fjalëkalimin tënd',
    bg: 'Въведи своята парола',
    ro: 'Introdu parola ta',
    el: 'Γράψε τον κωδικό σου',
    tr: 'Şifreni gir',
  },
  signIn: {
    me: 'Prijavi se',
    en: 'Sign In',
    sr: 'Prijavi se',
    hr: 'Prijavi se',
    bs: 'Prijavi se',
    sl: 'Prijava',
    mk: 'Најави се',
    sq: 'Identifikohu',
    bg: 'Вход',
    ro: 'Conectare',
    el: 'Σύνδεση',
    tr: 'Giriş Yap',
  },
  orContinueWith: {
    me: 'Ili nastavi sa',
    en: 'Or continue with',
    sr: 'Ili nastavi sa',
    hr: 'Ili nastavi s',
    bs: 'Ili nastavi sa',
    sl: 'Ali nadaljuj z',
    mk: 'Или продолжи со',
    sq: 'Ose vazhdo me',
    bg: 'Или продължи с',
    ro: 'Sau continuă cu',
    el: 'Ή συνέχισε με',
    tr: 'Veya şununla devam et',
  },
  dontHaveAccount: {
    me: 'Nemaš nalog?',
    en: "Don't have an account?",
    sr: 'Nemaš nalog?',
    hr: 'Nemaš račun?',
    bs: 'Nemaš nalog?',
    sl: 'Nimaš računa?',
    mk: 'Немаш сметка?',
    sq: 'Nuk ke llogari?',
    bg: 'Нямаш акаунт?',
    ro: 'Nu ai cont?',
    el: 'Δεν έχεις λογαριασμό;',
    tr: 'Hesabın yok mu?',
  },
  signUp: {
    me: 'Registruj se',
    en: 'Sign up',
    sr: 'Registruj se',
    hr: 'Registriraj se',
    bs: 'Registruj se',
    sl: 'Registriraj se',
    mk: 'Регистрирај се',
    sq: 'Regjistrohu',
    bg: 'Регистрация',
    ro: 'Înregistrare',
    el: 'Εγγραφή',
    tr: 'Kayıt Ol',
  },
  // Register page translations
  createAccount: {
    me: 'Kreiraj nalog',
    en: 'Create Account',
    sr: 'Kreiraj nalog',
    hr: 'Kreiraj račun',
    bs: 'Kreiraj nalog',
    sl: 'Ustvari račun',
    mk: 'Креирај сметка',
    sq: 'Krijo llogari',
    bg: 'Създай акаунт',
    ro: 'Creează cont',
    el: 'Δημιουργία λογαριασμού',
    tr: 'Hesap Oluştur',
  },
  startFreeTrial: {
    me: 'Započni besplatni probni period od 5 dana, bez kartice.',
    en: 'Start your 5-day free trial, no card required.',
    sr: 'Započni besplatni probni period od 5 dana, bez kartice.',
    hr: 'Započni besplatno probno razdoblje od 5 dana, bez kartice.',
    bs: 'Započni besplatni probni period od 5 dana, bez kartice.',
    sl: 'Začni 5-dnevni brezplačni preizkus, brez kartice.',
    mk: 'Започни бесплатен пробен период од 5 дена, без картичка.',
    sq: 'Fillo periudhën provuese falas 5-ditore, pa kartë.',
    bg: 'Започни безплатен пробен период от 5 дни, без карта.',
    ro: 'Începe perioada de probă gratuită de 5 zile, fără card.',
    el: 'Ξεκίνα τη δωρεάν δοκιμή 5 ημερών, χωρίς κάρτα.',
    tr: '5 günlük ücretsiz denemeni başlat, kart gerekmez.',
  },
  fullName: {
    me: 'Ime i prezime',
    en: 'Full Name',
    sr: 'Ime i prezime',
    hr: 'Ime i prezime',
    bs: 'Ime i prezime',
    sl: 'Polno ime',
    mk: 'Целосно име',
    sq: 'Emri i plotë',
    bg: 'Пълно име',
    ro: 'Nume complet',
    el: 'Πλήρες όνομα',
    tr: 'Ad Soyad',
  },
  enterYourName: {
    me: 'Unesi ime',
    en: 'Enter your name',
    sr: 'Unesi ime',
    hr: 'Unesi ime',
    bs: 'Unesi ime',
    sl: 'Vnesi ime',
    mk: 'Внеси го своето име',
    sq: 'Shkruaj emrin tënd',
    bg: 'Въведи своето име',
    ro: 'Introdu numele tău',
    el: 'Γράψε το όνομά σου',
    tr: 'Adını gir',
  },
  createPassword: {
    me: 'Kreiraj lozinku',
    en: 'Create a password',
    sr: 'Kreiraj lozinku',
    hr: 'Kreiraj lozinku',
    bs: 'Kreiraj lozinku',
    sl: 'Ustvari geslo',
    mk: 'Креирај лозинка',
    sq: 'Krijo një fjalëkalim',
    bg: 'Създай парола',
    ro: 'Creează o parolă',
    el: 'Δημιούργησε κωδικό πρόσβασης',
    tr: 'Bir şifre oluştur',
  },
  confirmPassword: {
    me: 'Potvrdi lozinku',
    en: 'Confirm Password',
    sr: 'Potvrdi lozinku',
    hr: 'Potvrdi lozinku',
    bs: 'Potvrdi lozinku',
    sl: 'Potrdi geslo',
    mk: 'Потврди лозинка',
    sq: 'Konfirmo fjalëkalimin',
    bg: 'Потвърди паролата',
    ro: 'Confirmă parola',
    el: 'Επιβεβαίωσε τον κωδικό',
    tr: 'Şifreyi onayla',
  },
  confirmYourPassword: {
    me: 'Potvrdi lozinku',
    en: 'Confirm your password',
    sr: 'Potvrdi lozinku',
    hr: 'Potvrdi lozinku',
    bs: 'Potvrdi lozinku',
    sl: 'Potrdi geslo',
    mk: 'Потврди ја својата лозинка',
    sq: 'Konfirmo fjalëkalimin tënd',
    bg: 'Потвърди своята парола',
    ro: 'Confirmă parola',
    el: 'Επιβεβαίωσε τον κωδικό σου',
    tr: 'Şifreni onayla',
  },
  atLeast8Characters: {
    me: 'Najmanje 8 karaktera',
    en: 'At least 8 characters',
    sr: 'Najmanje 8 karaktera',
    hr: 'Najmanje 8 znakova',
    bs: 'Najmanje 8 karaktera',
    sl: 'Najmanj 8 znakov',
    mk: 'Најмалку 8 карактери',
    sq: 'Të paktën 8 karaktere',
    bg: 'Поне 8 знака',
    ro: 'Cel puțin 8 caractere',
    el: 'Τουλάχιστον 8 χαρακτήρες',
    tr: 'En az 8 karakter',
  },
  oneUppercaseLetter: {
    me: 'Jedno veliko slovo',
    en: 'One uppercase letter',
    sr: 'Jedno veliko slovo',
    hr: 'Jedno veliko slovo',
    bs: 'Jedno veliko slovo',
    sl: 'Ena velika črka',
    mk: 'Една голема буква',
    sq: 'Një shkronjë e madhe',
    bg: 'Една главна буква',
    ro: 'O literă majusculă',
    el: 'Ένα κεφαλαίο γράμμα',
    tr: 'Bir büyük harf',
  },
  oneLowercaseLetter: {
    me: 'Jedno malo slovo',
    en: 'One lowercase letter',
    sr: 'Jedno malo slovo',
    hr: 'Jedno malo slovo',
    bs: 'Jedno malo slovo',
    sl: 'Ena mala črka',
    mk: 'Една мала буква',
    sq: 'Një shkronjë e vogël',
    bg: 'Една малка буква',
    ro: 'O literă mică',
    el: 'Ένα πεζό γράμμα',
    tr: 'Bir küçük harf',
  },
  oneNumber: {
    me: 'Jedan broj',
    en: 'One number',
    sr: 'Jedan broj',
    hr: 'Jedan broj',
    bs: 'Jedan broj',
    sl: 'Ena številka',
    mk: 'Еден број',
    sq: 'Një numër',
    bg: 'Една цифра',
    ro: 'Un număr',
    el: 'Ένας αριθμός',
    tr: 'Bir rakam',
  },
  nameMustBe2Characters: {
    me: 'Ime mora imati najmanje 2 karaktera',
    en: 'Name must be at least 2 characters',
    sr: 'Ime mora imati najmanje 2 karaktera',
    hr: 'Ime mora imati najmanje 2 znaka',
    bs: 'Ime mora imati najmanje 2 karaktera',
    sl: 'Ime mora imeti najmanj 2 znaka',
    mk: 'Името мора да има најмалку 2 карактери',
    sq: 'Emri duhet të ketë të paktën 2 karaktere',
    bg: 'Името трябва да е поне 2 знака',
    ro: 'Numele trebuie să aibă cel puțin 2 caractere',
    el: 'Το όνομα πρέπει να έχει τουλάχιστον 2 χαρακτήρες',
    tr: 'Ad en az 2 karakter olmalıdır',
  },
  passwordDoesNotMeetRequirements: {
    me: 'Lozinka ne ispunjava zahtjeve',
    en: 'Password does not meet requirements',
    sr: 'Lozinka ne ispunjava zahteve',
    hr: 'Lozinka ne ispunjava zahtjeve',
    bs: 'Lozinka ne ispunjava zahtjeve',
    sl: 'Geslo ne izpolnjuje zahtev',
    mk: 'Лозинката не ги исполнува барањата',
    sq: 'Fjalëkalimi nuk i plotëson kërkesat',
    bg: 'Паролата не отговаря на изискванията',
    ro: 'Parola nu îndeplinește cerințele',
    el: 'Ο κωδικός δεν πληροί τις απαιτήσεις',
    tr: 'Şifre gereksinimleri karşılamıyor',
  },
  // No character count: the limit is 72 bytes, fewer characters in Cyrillic/Greek.
  passwordTooLong: {
    me: 'Lozinka je preduga',
    en: 'Password is too long',
    sr: 'Lozinka je predugačka',
    hr: 'Lozinka je preduga',
    bs: 'Lozinka je predugačka',
    sl: 'Geslo je predolgo',
    mk: 'Лозинката е предолга',
    sq: 'Fjalëkalimi është shumë i gjatë',
    bg: 'Паролата е твърде дълга',
    ro: 'Parola este prea lungă',
    el: 'Ο κωδικός είναι πολύ μεγάλος',
    tr: 'Şifre çok uzun',
  },
  passwordsDoNotMatch: {
    me: 'Lozinke se ne poklapaju',
    en: 'Passwords do not match',
    sr: 'Lozinke se ne poklapaju',
    hr: 'Lozinke se ne podudaraju',
    bs: 'Lozinke se ne poklapaju',
    sl: 'Gesli se ne ujemata',
    mk: 'Лозинките не се совпаѓаат',
    sq: 'Fjalëkalimet nuk përputhen',
    bg: 'Паролите не съвпадат',
    ro: 'Parolele nu se potrivesc',
    el: 'Οι κωδικοί δεν ταιριάζουν',
    tr: 'Şifreler eşleşmiyor',
  },
  alreadyHaveAccount: {
    me: 'Već imaš nalog?',
    en: 'Already have an account?',
    sr: 'Već imaš nalog?',
    hr: 'Već imaš račun?',
    bs: 'Već imaš nalog?',
    sl: 'Že imaš račun?',
    mk: 'Веќе имаш сметка?',
    sq: 'Ke tashmë një llogari?',
    bg: 'Вече имаш акаунт?',
    ro: 'Ai deja cont?',
    el: 'Έχεις ήδη λογαριασμό;',
    tr: 'Zaten hesabın var mı?',
  },
  termsAndPrivacy: {
    me: 'Kreiranjem naloga prihvataš naše {terms} i {privacy}.',
    en: 'By creating an account, you agree to our {terms} and {privacy}.',
    sr: 'Kreiranjem naloga prihvataš naše {terms} i {privacy}.',
    hr: 'Kreiranjem računa prihvaćaš naše {terms} i {privacy}.',
    bs: 'Kreiranjem naloga prihvataš naše {terms} i {privacy}.',
    sl: 'Z ustvarjanjem računa se strinjaš z našimi {terms} in {privacy}.',
    mk: 'Со креирање сметка се согласуваш со нашите {terms} и {privacy}.',
    sq: 'Duke krijuar një llogari, pranon {terms} dhe {privacy}.',
    bg: 'Със създаването на акаунт приемаш нашите {terms} и {privacy}.',
    ro: 'Prin crearea unui cont, ești de acord cu {terms} și {privacy}.',
    el: 'Δημιουργώντας λογαριασμό, συμφωνείς με τους {terms} και την {privacy}.',
    tr: 'Hesap oluşturarak {terms} ve {privacy} kabul etmiş olursun.',
  },
  termsLink: {
    me: 'Uslove korištenja',
    en: 'Terms of Service',
    sr: 'Uslove korišćenja',
    hr: 'Uvjete korištenja',
    bs: 'Uslove korištenja',
    sl: 'Pogoji uporabe',
    mk: 'Услови за користење',
    sq: 'Kushtet e Shërbimit',
    bg: 'Условия за ползване',
    ro: 'Termenii și condițiile',
    el: 'Όρους Χρήσης',
    tr: 'Hizmet Şartlarını',
  },
  privacyLink: {
    me: 'Politiku privatnosti',
    en: 'Privacy Policy',
    sr: 'Politiku privatnosti',
    hr: 'Politiku privatnosti',
    bs: 'Politiku privatnosti',
    sl: 'Politiko zasebnosti',
    mk: 'Политика за приватност',
    sq: 'Politikën e Privatësisë',
    bg: 'Политика за поверителност',
    ro: 'Politica de confidențialitate',
    el: 'Πολιτική Απορρήτου',
    tr: 'Gizlilik Politikasını',
  },
  // Forgot password
  forgotPassword: {
    me: 'Ne sjećaš se lozinke?', en: 'Forgot your password?', sr: 'Ne sećaš se lozinke?',
    hr: 'Ne sjećaš se lozinke?', bs: 'Ne sjećaš se lozinke?', sl: 'Se ne spomniš gesla?',
    mk: 'Ја заборави лозинката?', sq: 'E ke harruar fjalëkalimin?', bg: 'Забрави ли паролата си?',
    ro: 'Ai uitat parola?', el: 'Ξέχασες τον κωδικό σου;', tr: 'Şifreni mi unuttun?',
  },
  forgotPasswordDesc: {
    me: 'Unesi email adresu i poslaćemo ti link za resetovanje lozinke.',
    en: 'Enter your email and we\'ll send you a link to reset your password.',
    sr: 'Unesi email adresu i poslaćemo ti link za resetovanje lozinke.',
    hr: 'Unesi email adresu i poslat ćemo ti link za resetiranje lozinke.',
    bs: 'Unesi email adresu i poslaćemo ti link za resetovanje lozinke.',
    sl: 'Vnesi e-poštni naslov in poslali ti bomo povezavo za ponastavitev gesla.',
    mk: 'Внеси ја својата е-пошта и ќе ти испратиме линк за ресетирање на лозинката.',
    sq: 'Shkruaj email-in tënd dhe do të të dërgojmë një link për të rivendosur fjalëkalimin.',
    bg: 'Въведи имейла си и ще ти изпратим линк за промяна на паролата.',
    ro: 'Introdu adresa de email și îți vom trimite un link pentru resetarea parolei.',
    el: 'Γράψε το email σου και θα σου στείλουμε έναν σύνδεσμο για επαναφορά κωδικού.',
    tr: 'E-postanı gir, sana şifre sıfırlama bağlantısı gönderelim.',
  },
  sendResetLink: {
    me: 'Pošalji link za resetovanje', en: 'Send Reset Link', sr: 'Pošalji link za resetovanje',
    hr: 'Pošalji link za resetiranje', bs: 'Pošalji link za resetovanje', sl: 'Pošlji povezavo za ponastavitev',
    mk: 'Испрати линк за ресетирање', sq: 'Dërgo linkun e rivendosjes', bg: 'Изпрати линк за промяна',
    ro: 'Trimite linkul de resetare', el: 'Αποστολή συνδέσμου', tr: 'Sıfırlama bağlantısı gönder',
  },
  resetLinkSent: {
    me: 'Link je poslan!', en: 'Reset link sent!', sr: 'Link je poslan!',
    hr: 'Link je poslan!', bs: 'Link je poslan!', sl: 'Povezava je bila poslana!',
    mk: 'Линкот е испратен!', sq: 'Linku u dërgua!', bg: 'Линкът е изпратен!',
    ro: 'Linkul a fost trimis!', el: 'Ο σύνδεσμος εστάλη!', tr: 'Bağlantı gönderildi!',
  },
  resetLinkSentDesc: {
    me: 'Ako nalog postoji, poslaćemo email sa uputstvima za resetovanje lozinke.',
    en: 'If an account exists, we\'ll send an email with password reset instructions.',
    sr: 'Ako nalog postoji, poslaćemo email sa uputstvima za resetovanje lozinke.',
    hr: 'Ako račun postoji, poslat ćemo email s uputama za resetiranje lozinke.',
    bs: 'Ako nalog postoji, poslaćemo email sa uputstvima za resetovanje lozinke.',
    sl: 'Če račun obstaja, bomo poslali e-pošto z navodili za ponastavitev gesla.',
    mk: 'Ако постои сметка, ќе испратиме е-пошта со упатства за ресетирање на лозинката.',
    sq: 'Nëse llogaria ekziston, do të të dërgojmë email me udhëzimet për rivendosjen e fjalëkalimit.',
    bg: 'Ако акаунтът съществува, ще изпратим имейл с инструкции за промяна на паролата.',
    ro: 'Dacă contul există, vom trimite un email cu instrucțiuni de resetare.',
    el: 'Αν υπάρχει λογαριασμός, θα στείλουμε email με οδηγίες επαναφοράς κωδικού.',
    tr: 'Hesap varsa, şifre sıfırlama talimatlarını içeren bir e-posta göndereceğiz.',
  },
  backToLogin: {
    me: 'Nazad na prijavu', en: 'Back to Login', sr: 'Nazad na prijavu',
    hr: 'Natrag na prijavu', bs: 'Nazad na prijavu', sl: 'Nazaj na prijavo',
    mk: 'Назад на најава', sq: 'Kthehu te identifikimi', bg: 'Обратно към вход',
    ro: 'Înapoi la conectare', el: 'Πίσω στη σύνδεση', tr: 'Girişe dön',
  },
  // Reset password
  resetPassword: {
    me: 'Resetuj lozinku', en: 'Reset Password', sr: 'Resetuj lozinku',
    hr: 'Resetiraj lozinku', bs: 'Resetuj lozinku', sl: 'Ponastavi geslo',
    mk: 'Ресетирај лозинка', sq: 'Rivendos fjalëkalimin', bg: 'Промени паролата',
    ro: 'Resetează parola', el: 'Επαναφορά κωδικού', tr: 'Şifreyi sıfırla',
  },
  resetPasswordDesc: {
    me: 'Unesi novu lozinku za svoj nalog.', en: 'Enter a new password for your account.',
    sr: 'Unesi novu lozinku za svoj nalog.', hr: 'Unesi novu lozinku za svoj račun.',
    bs: 'Unesi novu lozinku za svoj nalog.', sl: 'Vnesi novo geslo za svoj račun.',
    mk: 'Внеси нова лозинка за својата сметка.', sq: 'Shkruaj fjalëkalimin e ri për llogarinë tënde.',
    bg: 'Въведи нова парола за своя акаунт.', ro: 'Introdu o parolă nouă pentru contul tău.',
    el: 'Γράψε νέο κωδικό για τον λογαριασμό σου.', tr: 'Hesabın için yeni bir şifre gir.',
  },
  newPassword: {
    me: 'Nova lozinka', en: 'New Password', sr: 'Nova lozinka',
    hr: 'Nova lozinka', bs: 'Nova lozinka', sl: 'Novo geslo',
    mk: 'Нова лозинка', sq: 'Fjalëkalimi i ri', bg: 'Нова парола',
    ro: 'Parolă nouă', el: 'Νέος κωδικός', tr: 'Yeni şifre',
  },
  enterNewPassword: {
    me: 'Unesi novu lozinku', en: 'Enter new password', sr: 'Unesi novu lozinku',
    hr: 'Unesi novu lozinku', bs: 'Unesi novu lozinku', sl: 'Vnesi novo geslo',
    mk: 'Внеси нова лозинка', sq: 'Shkruaj fjalëkalimin e ri', bg: 'Въведи нова парола',
    ro: 'Introdu parola nouă', el: 'Γράψε νέο κωδικό', tr: 'Yeni şifreyi gir',
  },
  confirmNewPassword: {
    me: 'Potvrdi novu lozinku', en: 'Confirm new password', sr: 'Potvrdi novu lozinku',
    hr: 'Potvrdi novu lozinku', bs: 'Potvrdi novu lozinku', sl: 'Potrdi novo geslo',
    mk: 'Потврди ја новата лозинка', sq: 'Konfirmo fjalëkalimin e ri', bg: 'Потвърди новата парола',
    ro: 'Confirmă parola nouă', el: 'Επιβεβαίωσε τον νέο κωδικό', tr: 'Yeni şifreyi onayla',
  },
  passwordResetSuccess: {
    me: 'Lozinka je resetovana!', en: 'Password Reset!', sr: 'Lozinka je resetovana!',
    hr: 'Lozinka je resetirana!', bs: 'Lozinka je resetovana!', sl: 'Geslo je ponastavljeno!',
    mk: 'Лозинката е ресетирана!', sq: 'Fjalëkalimi u rivendos!', bg: 'Паролата е променена!',
    ro: 'Parola a fost resetată!', el: 'Ο κωδικός επαναφέρθηκε!', tr: 'Şifre sıfırlandı!',
  },
  passwordResetSuccessDesc: {
    me: 'Tvoja lozinka je uspješno promijenjena. Sada se možeš prijaviti.',
    en: 'Your password has been changed successfully. You can now sign in.',
    sr: 'Tvoja lozinka je uspešno promenjena. Sada se možeš prijaviti.',
    hr: 'Tvoja lozinka je uspješno promijenjena. Sada se možeš prijaviti.',
    bs: 'Tvoja lozinka je uspješno promijenjena. Sada se možeš prijaviti.',
    sl: 'Tvoje geslo je bilo uspešno spremenjeno. Zdaj se lahko prijaviš.',
    mk: 'Твојата лозинка е успешно променета. Сега можеш да се најавиш.',
    sq: 'Fjalëkalimi yt u ndryshua me sukses. Tani mund të identifikohesh.',
    bg: 'Паролата ти е променена успешно. Сега можеш да влезеш.',
    ro: 'Parola ta a fost schimbată cu succes. Acum te poți conecta.',
    el: 'Ο κωδικός σου άλλαξε επιτυχώς. Μπορείς τώρα να συνδεθείς.',
    tr: 'Şifren başarıyla değiştirildi. Artık giriş yapabilirsin.',
  },
  invalidResetLink: {
    me: 'Nevažeći link', en: 'Invalid Link', sr: 'Nevažeći link',
    hr: 'Nevažeća poveznica', bs: 'Nevažeći link', sl: 'Neveljavna povezava',
    mk: 'Невалиден линк', sq: 'Link i pavlefshëm', bg: 'Невалиден линк',
    ro: 'Link invalid', el: 'Άκυρος σύνδεσμος', tr: 'Geçersiz bağlantı',
  },
  invalidResetLinkDesc: {
    me: 'Ovaj link za resetovanje je nevažeći ili je istekao.',
    en: 'This reset link is invalid or has expired.',
    sr: 'Ovaj link za resetovanje je nevažeći ili je istekao.',
    hr: 'Ova poveznica za resetiranje je nevažeća ili je istekla.',
    bs: 'Ovaj link za resetovanje je nevažeći ili je istekao.',
    sl: 'Ta povezava za ponastavitev je neveljavna ali je potekla.',
    mk: 'Овој линк за ресетирање е невалиден или истечен.',
    sq: 'Ky link rivendosjeje është i pavlefshëm ose ka skaduar.',
    bg: 'Този линк за промяна е невалиден или е изтекъл.',
    ro: 'Acest link de resetare este invalid sau a expirat.',
    el: 'Αυτός ο σύνδεσμος επαναφοράς είναι άκυρος ή έχει λήξει.',
    tr: 'Bu sıfırlama bağlantısı geçersiz veya süresi dolmuş.',
  },
  // Email confirmation
  emailConfirmed: {
    me: 'Email je potvrđen!', en: 'Email Confirmed!', sr: 'Email je potvrđen!',
    hr: 'Email je potvrđen!', bs: 'Email je potvrđen!', sl: 'E-pošta je potrjena!',
    mk: 'Е-поштата е потврдена!', sq: 'Email-i u konfirmua!', bg: 'Имейлът е потвърден!',
    ro: 'Emailul a fost confirmat!', el: 'Το email επιβεβαιώθηκε!', tr: 'E-posta onaylandı!',
  },
  emailConfirmedDesc: {
    me: 'Tvoja email adresa je uspješno potvrđena.', en: 'Your email address has been confirmed successfully.',
    sr: 'Tvoja email adresa je uspešno potvrđena.', hr: 'Tvoja email adresa je uspješno potvrđena.',
    bs: 'Tvoja email adresa je uspješno potvrđena.', sl: 'Tvoj e-poštni naslov je bil uspešno potrjen.',
    mk: 'Твојата е-пошта е успешно потврдена.', sq: 'Adresa jote e email-it u konfirmua me sukses.',
    bg: 'Твоят имейл адрес е потвърден успешно.', ro: 'Adresa ta de email a fost confirmată cu succes.',
    el: 'Η διεύθυνση email σου επιβεβαιώθηκε επιτυχώς.', tr: 'E-posta adresin başarıyla onaylandı.',
  },
  emailConfirmExpired: {
    me: 'Link je istekao', en: 'Link Expired', sr: 'Link je istekao',
    hr: 'Poveznica je istekla', bs: 'Link je istekao', sl: 'Povezava je potekla',
    mk: 'Линкот истече', sq: 'Linku ka skaduar', bg: 'Линкът е изтекъл',
    ro: 'Linkul a expirat', el: 'Ο σύνδεσμος έληξε', tr: 'Bağlantı süresi doldu',
  },
  emailConfirmExpiredDesc: {
    me: 'Link za potvrdu je istekao. Zatraži novi.', en: 'The confirmation link has expired. Please request a new one.',
    sr: 'Link za potvrdu je istekao. Zatraži novi.', hr: 'Poveznica za potvrdu je istekla. Zatraži novu.',
    bs: 'Link za potvrdu je istekao. Zatraži novi.', sl: 'Povezava za potrditev je potekla. Zahtevaj novo.',
    mk: 'Линкот за потврда истече. Побарај нов.', sq: 'Linku i konfirmimit ka skaduar. Kërko një të ri.',
    bg: 'Линкът за потвърждение е изтекъл. Поискай нов.', ro: 'Linkul de confirmare a expirat. Cere unul nou.',
    el: 'Ο σύνδεσμος επιβεβαίωσης έληξε. Ζήτησε νέο.', tr: 'Onay bağlantısının süresi doldu. Yeni bir tane iste.',
  },
  emailConfirmInvalid: {
    me: 'Nevažeći link', en: 'Invalid Link', sr: 'Nevažeći link',
    hr: 'Nevažeća poveznica', bs: 'Nevažeći link', sl: 'Neveljavna povezava',
    mk: 'Невалиден линк', sq: 'Link i pavlefshëm', bg: 'Невалиден линк',
    ro: 'Link invalid', el: 'Άκυρος σύνδεσμος', tr: 'Geçersiz bağlantı',
  },
  emailConfirmInvalidDesc: {
    me: 'Link za potvrdu je nevažeći ili je već iskorišten.',
    en: 'The confirmation link is invalid or has already been used.',
    sr: 'Link za potvrdu je nevažeći ili je već iskorišćen.',
    hr: 'Poveznica za potvrdu je nevažeća ili je već iskorištena.',
    bs: 'Link za potvrdu je nevažeći ili je već iskorišten.',
    sl: 'Povezava za potrditev je neveljavna ali je že bila uporabljena.',
    mk: 'Линкот за потврда е невалиден или веќе е искористен.',
    sq: 'Linku i konfirmimit është i pavlefshëm ose tashmë është përdorur.',
    bg: 'Линкът за потвърждение е невалиден или вече е използван.',
    ro: 'Linkul de confirmare este invalid sau a fost deja utilizat.',
    el: 'Ο σύνδεσμος επιβεβαίωσης είναι άκυρος ή έχει ήδη χρησιμοποιηθεί.',
    tr: 'Onay bağlantısı geçersiz veya zaten kullanılmış.',
  },
  checkYourEmail: {
    me: 'Provjeri email', en: 'Check Your Email', sr: 'Proveri email',
    hr: 'Provjeri email', bs: 'Provjeri email', sl: 'Preveri e-pošto',
    mk: 'Провери ја е-поштата', sq: 'Kontrollo email-in tënd', bg: 'Провери имейла си',
    ro: 'Verifică emailul', el: 'Έλεγξε το email σου', tr: 'E-postanı kontrol et',
  },
  checkYourEmailDesc: {
    me: 'Poslali smo ti email za potvrdu. Klikni na link u emailu da potvrdiš svoju adresu.',
    en: 'We\'ve sent you a confirmation email. Click the link in the email to verify your address.',
    sr: 'Poslali smo ti email za potvrdu. Klikni na link u emailu da potvrdiš svoju adresu.',
    hr: 'Poslali smo ti email za potvrdu. Klikni na poveznicu u emailu i potvrdi svoju adresu.',
    bs: 'Poslali smo ti email za potvrdu. Klikni na link u emailu da potvrdiš svoju adresu.',
    sl: 'Poslali smo ti e-pošto za potrditev. Klikni povezavo v e-pošti in potrdi svoj naslov.',
    mk: 'Ти испративме е-пошта за потврда. Кликни на линкот за да ја потврдиш адресата.',
    sq: 'Të kemi dërguar një email konfirmimi. Kliko linkun për të verifikuar adresën tënde.',
    bg: 'Изпратихме ти имейл за потвърждение. Кликни върху линка, за да потвърдиш адреса си.',
    ro: 'Ți-am trimis un email de confirmare. Dă clic pe linkul din email pentru a-ți verifica adresa.',
    el: 'Σου στείλαμε email επιβεβαίωσης. Πάτησε τον σύνδεσμο για να επιβεβαιώσεις τη διεύθυνσή σου.',
    tr: 'Sana bir onay e-postası gönderdik. Adresini doğrulamak için e-postadaki bağlantıya tıkla.',
  },
  resendConfirmation: {
    me: 'Pošalji ponovo', en: 'Resend', sr: 'Pošalji ponovo',
    hr: 'Pošalji ponovo', bs: 'Pošalji ponovo', sl: 'Pošlji znova',
    mk: 'Испрати повторно', sq: 'Ridërgo', bg: 'Изпрати отново',
    ro: 'Retrimite', el: 'Αποστολή ξανά', tr: 'Tekrar gönder',
  },
  confirmationResent: {
    me: 'Email za potvrdu je ponovo poslan.', en: 'Confirmation email has been resent.',
    sr: 'Email za potvrdu je ponovo poslan.', hr: 'Email za potvrdu je ponovo poslan.',
    bs: 'Email za potvrdu je ponovo poslan.', sl: 'E-pošta za potrditev je bila znova poslana.',
    mk: 'Е-поштата за потврда е повторно испратена.', sq: 'Email-i i konfirmimit u ridërgua.',
    bg: 'Имейлът за потвърждение е изпратен отново.', ro: 'Emailul de confirmare a fost retrimis.',
    el: 'Το email επιβεβαίωσης εστάλη ξανά.', tr: 'Onay e-postası yeniden gönderildi.',
  },
  // Accessibility
  showPassword: {
    me: 'Prikaži lozinku', en: 'Show password', sr: 'Prikaži lozinku',
    hr: 'Prikaži lozinku', bs: 'Prikaži lozinku', sl: 'Pokaži geslo',
    mk: 'Прикажи лозинка', sq: 'Shfaq fjalëkalimin', bg: 'Покажи паролата',
    ro: 'Arată parola', el: 'Εμφάνιση κωδικού', tr: 'Şifreyi göster',
  },
  hidePassword: {
    me: 'Sakrij lozinku', en: 'Hide password', sr: 'Sakrij lozinku',
    hr: 'Sakrij lozinku', bs: 'Sakrij lozinku', sl: 'Skrij geslo',
    mk: 'Сокриј лозинка', sq: 'Fshih fjalëkalimin', bg: 'Скрий паролата',
    ro: 'Ascunde parola', el: 'Απόκρυψη κωδικού', tr: 'Şifreyi gizle',
  },
  confirmingEmail: {
    me: 'Potvrđivanje email adrese…', en: 'Confirming your email…',
    sr: 'Potvrđivanje email adrese…', hr: 'Potvrđivanje email adrese…',
    bs: 'Potvrđivanje email adrese…', sl: 'Potrjevanje e-pošte…',
    mk: 'Потврдување на е-пошта…', sq: 'Po konfirmohet email-i yt…',
    bg: 'Потвърждаване на имейла…', ro: 'Se confirmă emailul…',
    el: 'Επιβεβαίωση του email σου…', tr: 'E-postan onaylanıyor…',
  },
};

/**
 * Get translated auth string for the given language
 * @param key - The translation key
 * @param language - The target language
 * @returns The translated string
 */
/** All auth translation keys (runtime list, for exhaustive testing). */
export const AUTH_TRANSLATION_KEYS = Object.keys(
  AUTH_TRANSLATIONS
) as AuthTranslationKey[];

export function getAuthTranslation(key: AuthTranslationKey, language: Language): string {
  const entry = AUTH_TRANSLATIONS[key];
  // Fall back to English so a missing language never renders blank text.
  return entry[language] ?? entry.en;
}

/**
 * Favorites page translation keys
 */
export type FavoritesTranslationKey =
  | 'signInToView'
  | 'signInDesc'
  | 'signIn'
  | 'loadError'
  | 'pageTitle'
  | 'pageSubtitle'
  | 'noFavorites'
  | 'noFavoritesDesc'
  | 'browseSessions';

/**
 * Favorites translations for all 12 supported languages
 */
const FAVORITES_TRANSLATIONS: Record<FavoritesTranslationKey, Record<Language, string>> = {
  signInToView: {
    me: 'Prijavi se za pregled omiljenih',
    en: 'Sign in to view favorites',
    sr: 'Prijavi se za pregled omiljenih',
    hr: 'Prijavi se za pregled favorita',
    bs: 'Prijavi se za pregled omiljenih',
    sl: 'Prijavi se za ogled priljubljenih',
    mk: 'Најави се за преглед на омилените',
    sq: 'Identifikohu për të parë të preferuarat',
    bg: 'Влез, за да видиш любимите',
    ro: 'Conectează-te pentru a vedea favoritele',
    el: 'Συνδέσου για να δεις τα αγαπημένα',
    tr: 'Favorileri görmek için giriş yap',
  },
  signInDesc: {
    me: 'Prijavi se i prati svoje omiljene sesije.',
    en: 'Keep track of your favorite sessions by signing in.',
    sr: 'Prijavi se i prati svoje omiljene sesije.',
    hr: 'Prijavi se i prati svoje omiljene sesije.',
    bs: 'Prijavi se i prati svoje omiljene sesije.',
    sl: 'Prijavi se in spremljaj svoje priljubljene seje.',
    mk: 'Најави се и следи ги своите омилени сесии.',
    sq: 'Identifikohu dhe ndiq seancat e tua të preferuara.',
    bg: 'Влез и следи любимите си сесии.',
    ro: 'Conectează-te și urmărește-ți sesiunile preferate.',
    el: 'Συνδέσου και παρακολούθησε τις αγαπημένες σου συνεδρίες.',
    tr: 'Giriş yap ve favori seanslarını takip et.',
  },
  signIn: {
    me: 'Prijava',
    en: 'Sign In',
    sr: 'Prijava',
    hr: 'Prijava',
    bs: 'Prijava',
    sl: 'Prijava',
    mk: 'Најава',
    sq: 'Identifikohu',
    bg: 'Вход',
    ro: 'Conectare',
    el: 'Σύνδεση',
    tr: 'Giriş Yap',
  },
  loadError: {
    me: 'Učitavanje omiljenih nije uspjelo. Pokušaj ponovo.',
    en: 'Failed to load favorites. Please try again.',
    sr: 'Učitavanje omiljenih nije uspelo. Pokušaj ponovo.',
    hr: 'Učitavanje favorita nije uspjelo. Pokušaj ponovno.',
    bs: 'Učitavanje omiljenih nije uspjelo. Pokušaj ponovo.',
    sl: 'Nalaganje priljubljenih ni uspelo. Poskusi znova.',
    mk: 'Вчитувањето на омилените не успеа. Обиди се повторно.',
    sq: 'Ngarkimi i të preferuarave dështoi. Provo përsëri.',
    bg: 'Неуспешно зареждане на любимите. Опитай отново.',
    ro: 'Încărcarea favoritelor a eșuat. Încearcă din nou.',
    el: 'Αποτυχία φόρτωσης αγαπημένων. Δοκίμασε ξανά.',
    tr: 'Favoriler yüklenemedi. Tekrar dene.',
  },
  pageTitle: {
    me: 'Tvoje omiljene',
    en: 'Your Favorites',
    sr: 'Tvoje omiljene',
    hr: 'Tvoji favoriti',
    bs: 'Tvoje omiljene',
    sl: 'Tvoje priljubljene',
    mk: 'Твоите омилени',
    sq: 'Të preferuarat e tua',
    bg: 'Твоите любими',
    ro: 'Favoritele tale',
    el: 'Τα αγαπημένα σου',
    tr: 'Favorilerin',
  },
  pageSubtitle: {
    me: 'Sačuvane sesije za brzi pristup',
    en: "Sessions you've saved for quick access",
    sr: 'Sačuvane sesije za brz pristup',
    hr: 'Spremljene sesije za brzi pristup',
    bs: 'Sačuvane sesije za brzi pristup',
    sl: 'Shranjene seje za hiter dostop',
    mk: 'Зачувани сесии за брз пристап',
    sq: 'Seancat që ke ruajtur për akses të shpejtë',
    bg: 'Запазени сесии за бърз достъп',
    ro: 'Sesiunile pe care le-ai salvat pentru acces rapid',
    el: 'Συνεδρίες που έχεις αποθηκεύσει για γρήγορη πρόσβαση',
    tr: 'Hızlı erişim için kaydettiğin seanslar',
  },
  noFavorites: {
    me: 'Još nema favorita',
    en: 'No favorites yet',
    sr: 'Još nema omiljenih',
    hr: 'Još nema favorita',
    bs: 'Još nema omiljenih',
    sl: 'Še ni priljubljenih',
    mk: 'Сè уште нема омилени',
    sq: 'Ende nuk ka të preferuara',
    bg: 'Все още няма любими',
    ro: 'Încă nu ai favorite',
    el: 'Δεν υπάρχουν αγαπημένα ακόμα',
    tr: 'Henüz favori yok',
  },
  noFavoritesDesc: {
    me: 'Pregledaj sesije i dodaj omiljene!',
    en: 'Browse the sessions and add your favorites!',
    sr: 'Pregledaj sesije i dodaj omiljene!',
    hr: 'Pregledaj sesije i dodaj favorite!',
    bs: 'Pregledaj sesije i dodaj omiljene!',
    sl: 'Prebrskaj seje in dodaj priljubljene!',
    mk: 'Разгледај ги сесиите и додај омилени!',
    sq: 'Shfleto seancat dhe shto të preferuarat!',
    bg: 'Разгледай сесиите и добави любими!',
    ro: 'Răsfoiește sesiunile și adaugă favorite!',
    el: 'Δες τις συνεδρίες και πρόσθεσε αγαπημένα!',
    tr: 'Seanslara göz at ve favorilerine ekle!',
  },
  browseSessions: {
    me: 'Pregledaj Lukaaji Sesije',
    en: 'Browse Lukaaji Sessions',
    sr: 'Pregledaj Lukaaji Sesije',
    hr: 'Pregledaj Lukaaji Sesije',
    bs: 'Pregledaj Lukaaji Sesije',
    sl: 'Prebrskaj Lukaaji Seje',
    mk: 'Прегледај Lukaaji Сесии',
    sq: 'Shfleto Lukaaji Seancat',
    bg: 'Разгледай Lukaaji Сесии',
    ro: 'Răsfoiește sesiunile Lukaaji',
    el: 'Περιήγηση Lukaaji Συνεδριών',
    tr: 'Lukaaji Seanslarını Gözat',
  },
};

/**
 * Get translated favorites string for the given language
 * @param key - The translation key
 * @param language - The target language
 * @returns The translated string
 */
export function getFavoritesTranslation(key: FavoritesTranslationKey, language: Language): string {
  return FAVORITES_TRANSLATIONS[key][language];
}

/**
 * History page translation keys
 */
export type HistoryTranslationKey =
  | 'signInToView'
  | 'signInDesc'
  | 'signIn'
  | 'loadError'
  | 'pageTitle'
  | 'pageSubtitle'
  | 'sessionsStarted'
  | 'completed'
  | 'timeListened'
  | 'favoriteCategory'
  | 'recentSessions'
  | 'noHistory'
  | 'noHistoryDesc'
  | 'browseSessions';

/**
 * History translations for all 12 supported languages
 */
const HISTORY_TRANSLATIONS: Record<HistoryTranslationKey, Record<Language, string>> = {
  signInToView: {
    me: 'Prijavi se za pregled istorije',
    en: 'Sign in to view history',
    sr: 'Prijavi se za pregled istorije',
    hr: 'Prijavi se za pregled povijesti',
    bs: 'Prijavi se za pregled historije',
    sl: 'Prijavi se za ogled zgodovine',
    mk: 'Најави се за преглед на историјата',
    sq: 'Identifikohu për të parë historinë',
    bg: 'Влез, за да видиш историята',
    ro: 'Conectează-te pentru a vedea istoricul',
    el: 'Συνδέσου για να δεις το ιστορικό',
    tr: 'Geçmişi görmek için giriş yap',
  },
  signInDesc: {
    me: 'Prijavi se i prati svoju praksu.',
    en: 'Sign in and track your practice.',
    sr: 'Prijavi se i prati svoju praksu.',
    hr: 'Prijavi se i prati svoju praksu.',
    bs: 'Prijavi se i prati svoju praksu.',
    sl: 'Prijavi se in spremljaj svojo prakso.',
    mk: 'Најави се и следи ја својата пракса.',
    sq: 'Identifikohu dhe ndiq praktikën tënde.',
    bg: 'Влез и следи своята практика.',
    ro: 'Conectează-te și urmărește-ți practica.',
    el: 'Συνδέσου και παρακολούθησε την πρακτική σου.',
    tr: 'Giriş yap ve pratiğini takip et.',
  },
  signIn: {
    me: 'Prijava',
    en: 'Sign In',
    sr: 'Prijava',
    hr: 'Prijava',
    bs: 'Prijava',
    sl: 'Prijava',
    mk: 'Најава',
    sq: 'Identifikohu',
    bg: 'Вход',
    ro: 'Conectare',
    el: 'Σύνδεση',
    tr: 'Giriş Yap',
  },
  loadError: {
    me: 'Učitavanje istorije nije uspjelo. Pokušaj ponovo.',
    en: 'Failed to load history. Please try again.',
    sr: 'Učitavanje istorije nije uspelo. Pokušaj ponovo.',
    hr: 'Učitavanje povijesti nije uspjelo. Pokušaj ponovno.',
    bs: 'Učitavanje historije nije uspjelo. Pokušaj ponovo.',
    sl: 'Nalaganje zgodovine ni uspelo. Poskusi znova.',
    mk: 'Вчитувањето на историјата не успеа. Обиди се повторно.',
    sq: 'Ngarkimi i historisë dështoi. Provo përsëri.',
    bg: 'Неуспешно зареждане на историята. Опитай отново.',
    ro: 'Încărcarea istoricului a eșuat. Încearcă din nou.',
    el: 'Αποτυχία φόρτωσης ιστορικού. Δοκίμασε ξανά.',
    tr: 'Geçmiş yüklenemedi. Tekrar dene.',
  },
  pageTitle: {
    me: 'Tvoje putovanje',
    en: 'Your Journey',
    sr: 'Tvoje putovanje',
    hr: 'Tvoje putovanje',
    bs: 'Tvoje putovanje',
    sl: 'Tvoja pot',
    mk: 'Твоето патување',
    sq: 'Udhëtimi yt',
    bg: 'Твоето пътуване',
    ro: 'Călătoria ta',
    el: 'Το ταξίδι σου',
    tr: 'Yolculuğun',
  },
  pageSubtitle: {
    me: 'Prati svoj napredak u praksi kroz vrijeme',
    en: 'Track your progress in the practice over time',
    sr: 'Prati svoj napredak u praksi kroz vreme',
    hr: 'Prati svoj napredak u praksi kroz vrijeme',
    bs: 'Prati svoj napredak u praksi kroz vrijeme',
    sl: 'Spremljaj svoj napredek pri praksi skozi čas',
    mk: 'Следи го својот напредок во праксата низ времето',
    sq: 'Ndiq progresin tënd në praktikë me kalimin e kohës',
    bg: 'Следи напредъка си в практиката във времето',
    ro: 'Urmărește-ți progresul în practică de-a lungul timpului',
    el: 'Παρακολούθησε την πρόοδό σου στην πρακτική με τον χρόνο',
    tr: 'Zaman içinde pratiğindeki ilerlemeni takip et',
  },
  sessionsStarted: {
    me: 'Pokrenuto Sesija',
    en: 'Sessions Started',
    sr: 'Pokrenuto Sesija',
    hr: 'Pokrenuto Sesija',
    bs: 'Pokrenuto Sesija',
    sl: 'Začetih Sej',
    mk: 'Започнати Сесии',
    sq: 'Seanca të Filluara',
    bg: 'Започнати Сесии',
    ro: 'Sesiuni Începute',
    el: 'Συνεδρίες που Ξεκίνησαν',
    tr: 'Başlatılan Seanslar',
  },
  completed: {
    me: 'Završeno',
    en: 'Completed',
    sr: 'Završeno',
    hr: 'Završeno',
    bs: 'Završeno',
    sl: 'Končano',
    mk: 'Завршено',
    sq: 'Përfunduar',
    bg: 'Завършено',
    ro: 'Finalizat',
    el: 'Ολοκληρώθηκε',
    tr: 'Tamamlandı',
  },
  timeListened: {
    me: 'Vrijeme Slušanja',
    en: 'Time Listened',
    sr: 'Vreme Slušanja',
    hr: 'Vrijeme Slušanja',
    bs: 'Vrijeme Slušanja',
    sl: 'Čas Poslušanja',
    mk: 'Време на Слушање',
    sq: 'Koha e Dëgjuar',
    bg: 'Време на Слушане',
    ro: 'Timp Ascultat',
    el: 'Χρόνος Ακρόασης',
    tr: 'Dinleme Süresi',
  },
  favoriteCategory: {
    me: 'Omiljena Kategorija',
    en: 'Favorite Category',
    sr: 'Omiljena Kategorija',
    hr: 'Omiljena Kategorija',
    bs: 'Omiljena Kategorija',
    sl: 'Priljubljena Kategorija',
    mk: 'Омилена Категорија',
    sq: 'Kategoria e Preferuar',
    bg: 'Любима Категория',
    ro: 'Categoria Preferată',
    el: 'Αγαπημένη Κατηγορία',
    tr: 'Favori Kategori',
  },
  recentSessions: {
    me: 'Nedavne Lukaaji Sesije',
    en: 'Recent Lukaaji Sessions',
    sr: 'Nedavne Lukaaji Sesije',
    hr: 'Nedavne Lukaaji Sesije',
    bs: 'Nedavne Lukaaji Sesije',
    sl: 'Nedavne Lukaaji Seje',
    mk: 'Неодамнешни Lukaaji Сесии',
    sq: 'Lukaaji Seancat e Fundit',
    bg: 'Скорошни Lukaaji Сесии',
    ro: 'Sesiuni Lukaaji Recente',
    el: 'Πρόσφατες Lukaaji Συνεδρίες',
    tr: 'Son Lukaaji Seansları',
  },
  noHistory: {
    me: 'Još nema historije',
    en: 'No history yet',
    sr: 'Još nema istorije',
    hr: 'Još nema povijesti',
    bs: 'Još nema historije',
    sl: 'Še ni zgodovine',
    mk: 'Сè уште нема историја',
    sq: 'Ende nuk ka histori',
    bg: 'Все още няма история',
    ro: 'Încă nu ai istoric',
    el: 'Δεν υπάρχει ιστορικό ακόμα',
    tr: 'Henüz geçmiş yok',
  },
  noHistoryDesc: {
    me: 'Započni svoju prvu sesiju danas!',
    en: 'Start your first session today!',
    sr: 'Započni svoju prvu sesiju danas!',
    hr: 'Započni svoju prvu sesiju danas!',
    bs: 'Započni svoju prvu sesiju danas!',
    sl: 'Začni svojo prvo sejo danes!',
    mk: 'Започни ја својата прва сесија денес!',
    sq: 'Fillo seancën tënde të parë sot!',
    bg: 'Започни първата си сесия днес!',
    ro: 'Începe azi prima ta sesiune!',
    el: 'Ξεκίνα σήμερα την πρώτη σου συνεδρία!',
    tr: 'İlk seansına bugün başla!',
  },
  browseSessions: {
    me: 'Pregledaj Lukaaji Sesije',
    en: 'Browse Lukaaji Sessions',
    sr: 'Pregledaj Lukaaji Sesije',
    hr: 'Pregledaj Lukaaji Sesije',
    bs: 'Pregledaj Lukaaji Sesije',
    sl: 'Prebrskaj Lukaaji Seje',
    mk: 'Прегледај Lukaaji Сесии',
    sq: 'Shfleto Lukaaji Seancat',
    bg: 'Разгледай Lukaaji Сесии',
    ro: 'Răsfoiește sesiunile Lukaaji',
    el: 'Περιήγηση Lukaaji Συνεδριών',
    tr: 'Lukaaji Seanslarını Gözat',
  },
};

/**
 * Get translated history string for the given language
 * @param key - The translation key
 * @param language - The target language
 * @returns The translated string
 */
export function getHistoryTranslation(key: HistoryTranslationKey, language: Language): string {
  return HISTORY_TRANSLATIONS[key][language];
}

/**
 * Home page translation keys (Početna, APP1)
 */
export type HomeTranslationKey =
  | 'title'
  | 'subtitle'
  | 'startHere'
  | 'startHereDesc'
  | 'startFirstSession'
  | 'continueListening'
  | 'acousticDesc'
  | 'guidedDesc'
  | 'needsTitle'
  | 'needPeace'
  | 'needSleep'
  | 'needRelief'
  | 'needConfidence'
  | 'shortBreak';

/**
 * Home page translations for all 12 supported languages: Luka's APP1 wording (`luka-requirements-raw.md:252-263`)
 */
const HOME_TRANSLATIONS: Record<HomeTranslationKey, Record<Language, string>> = {
  title: {
    me: 'Odvoji trenutak za sebe',
    en: 'Take a moment for yourself',
    sr: 'Odvoji trenutak za sebe',
    hr: 'Odvoji trenutak za sebe',
    bs: 'Odvoji trenutak za sebe',
    sl: 'Vzemi si trenutek zase',
    mk: 'Одвои момент за себе',
    sq: 'Ndaj një çast për veten',
    bg: 'Отдели момент за себе си',
    ro: 'Acordă-ți un moment pentru tine',
    el: 'Αφιέρωσε μια στιγμή στον εαυτό σου',
    tr: 'Kendine bir an ayır',
  },
  subtitle: {
    me: 'Tvoj prostor za mir, prisutnost i balans.',
    en: 'Your space for calm, presence and balance.',
    sr: 'Tvoj prostor za mir, prisutnost i balans.',
    hr: 'Tvoj prostor za mir, prisutnost i ravnotežu.',
    bs: 'Tvoj prostor za mir, prisutnost i balans.',
    sl: 'Tvoj prostor za mir, prisotnost in ravnovesje.',
    mk: 'Твој простор за мир, присутност и рамнотежа.',
    sq: 'Hapësira jote për qetësinë, praninë dhe ekuilibrin.',
    bg: 'Твоето пространство за спокойствие, присъствие и баланс.',
    ro: 'Spațiul tău pentru liniște, prezență și echilibru.',
    el: 'Ο χώρος σου για γαλήνη, παρουσία και ισορροπία.',
    tr: 'Huzur, anda kalma ve denge için senin alanın.',
  },
  startHere: {
    me: 'Počni ovdje',
    en: 'Start here',
    sr: 'Počni ovde',
    hr: 'Počni ovdje',
    bs: 'Počni ovdje',
    sl: 'Začni tukaj',
    mk: 'Почни овде',
    sq: 'Fillo këtu',
    bg: 'Започни оттук',
    ro: 'Începe aici',
    el: 'Ξεκίνα εδώ',
    tr: 'Buradan başla',
  },
  startHereDesc: {
    me: 'Upoznaj praksu, korak po korak.',
    en: 'Get to know the practice, step by step.',
    sr: 'Upoznaj praksu, korak po korak.',
    hr: 'Upoznaj praksu, korak po korak.',
    bs: 'Upoznaj praksu, korak po korak.',
    sl: 'Spoznaj prakso, korak za korakom.',
    mk: 'Запознај ја практиката, чекор по чекор.',
    sq: 'Njihu me praktikën, hap pas hapi.',
    bg: 'Опознай практиката стъпка по стъпка.',
    ro: 'Descoperă practica, pas cu pas.',
    el: 'Γνώρισε την πρακτική, βήμα βήμα.',
    tr: 'Pratiği adım adım tanı.',
  },
  startFirstSession: {
    me: 'Započni prvu sesiju',
    en: 'Start your first session',
    sr: 'Započni prvu sesiju',
    hr: 'Započni prvu sesiju',
    bs: 'Započni prvu sesiju',
    sl: 'Začni prvo sejo',
    mk: 'Започни ја првата сесија',
    sq: 'Fillo seancën e parë',
    bg: 'Започни първата сесия',
    ro: 'Începe prima sesiune',
    el: 'Ξεκίνα την πρώτη συνεδρία',
    tr: 'İlk seansına başla',
  },
  continueListening: {
    me: 'Nastavi slušanje',
    en: 'Continue listening',
    sr: 'Nastavi slušanje',
    hr: 'Nastavi slušati',
    bs: 'Nastavi slušanje',
    sl: 'Nadaljuj s poslušanjem',
    mk: 'Продолжи со слушање',
    sq: 'Vazhdo dëgjimin',
    bg: 'Продължи слушането',
    ro: 'Continuă ascultarea',
    el: 'Συνέχισε την ακρόαση',
    tr: 'Dinlemeye devam et',
  },
  acousticDesc: {
    me: 'Praksa bez glasovnog vođenja',
    en: 'Practice without voice guidance',
    sr: 'Praksa bez glasovnog vođenja',
    hr: 'Praksa bez glasovnog vođenja',
    bs: 'Praksa bez glasovnog vođenja',
    sl: 'Praksa brez glasovnega vodenja',
    mk: 'Пракса без гласовно водство',
    sq: 'Praktikë pa udhëzim me zë',
    bg: 'Практика без гласово водене',
    ro: 'Practică fără ghidaj vocal',
    el: 'Πρακτική χωρίς φωνητική καθοδήγηση',
    tr: 'Sesli rehberlik olmadan pratik',
  },
  guidedDesc: {
    me: 'Prati glas kroz izabranu temu',
    en: 'Follow the voice through the topic you choose',
    sr: 'Prati glas kroz izabranu temu',
    hr: 'Prati glas kroz odabranu temu',
    bs: 'Prati glas kroz izabranu temu',
    sl: 'Sledi glasu skozi izbrano temo',
    mk: 'Следи го гласот низ избраната тема',
    sq: 'Ndiq zërin përmes temës që zgjedh',
    bg: 'Следвай гласа през избраната тема',
    ro: 'Urmează vocea prin tema aleasă',
    el: 'Ακολούθησε τη φωνή στο θέμα που επιλέγεις',
    tr: 'Seçtiğin konuda sesi takip et',
  },
  needsTitle: {
    me: 'Šta ti je sada potrebno?',
    en: 'What do you need right now?',
    sr: 'Šta ti je sada potrebno?',
    hr: 'Što ti je sada potrebno?',
    bs: 'Šta ti je sada potrebno?',
    sl: 'Kaj zdaj potrebuješ?',
    mk: 'Што ти е потребно сега?',
    sq: 'Çfarë të nevojitet tani?',
    bg: 'От какво имаш нужда сега?',
    ro: 'De ce ai nevoie acum?',
    el: 'Τι χρειάζεσαι τώρα;',
    tr: 'Şu an neye ihtiyacın var?',
  },
  needPeace: {
    me: 'Mir',
    en: 'Peace',
    sr: 'Mir',
    hr: 'Mir',
    bs: 'Mir',
    sl: 'Mir',
    mk: 'Мир',
    sq: 'Qetësi',
    bg: 'Спокойствие',
    ro: 'Liniște',
    el: 'Γαλήνη',
    tr: 'Huzur',
  },
  needSleep: {
    me: 'San',
    en: 'Sleep',
    sr: 'San',
    hr: 'San',
    bs: 'San',
    sl: 'Spanje',
    mk: 'Сон',
    sq: 'Gjumë',
    bg: 'Сън',
    ro: 'Somn',
    el: 'Ύπνος',
    tr: 'Uyku',
  },
  needRelief: {
    me: 'Rasterećenje',
    en: 'Unwinding',
    sr: 'Rasterećenje',
    hr: 'Rasterećenje',
    bs: 'Rasterećenje',
    sl: 'Razbremenitev',
    mk: 'Растоварување',
    sq: 'Çlirim',
    bg: 'Разтоварване',
    ro: 'Destindere',
    el: 'Αποφόρτιση',
    tr: 'Hafifleme',
  },
  needConfidence: {
    me: 'Samopouzdanje',
    en: 'Self-confidence',
    sr: 'Samopouzdanje',
    hr: 'Samopouzdanje',
    bs: 'Samopouzdanje',
    sl: 'Samozavest',
    mk: 'Самодоверба',
    sq: 'Vetëbesim',
    bg: 'Увереност',
    ro: 'Încredere în sine',
    el: 'Αυτοπεποίθηση',
    tr: 'Özgüven',
  },
  shortBreak: {
    me: 'Kratka pauza za sebe',
    en: 'A short break for yourself',
    sr: 'Kratka pauza za sebe',
    hr: 'Kratka pauza za sebe',
    bs: 'Kratka pauza za sebe',
    sl: 'Kratek premor zase',
    mk: 'Кратка пауза за себе',
    sq: 'Një pushim i shkurtër për veten',
    bg: 'Кратка почивка за себе си',
    ro: 'O scurtă pauză pentru tine',
    el: 'Ένα μικρό διάλειμμα για σένα',
    tr: 'Kendin için kısa bir mola',
  },
};

/**
 * Get translated home page string for the given language
 * @param key - The translation key
 * @param language - The target language
 * @returns The translated string
 */
export function getHomeTranslation(key: HomeTranslationKey, language: Language): string {
  return HOME_TRANSLATIONS[key][language];
}

/**
 * Sessions page translation keys
 */
export type SessionsTranslationKey =
  | 'pageTitle'
  | 'pageSubtitle'
  | 'loadError'
  | 'noSearchResults'
  | 'noCategoryResults'
  | 'showAll'
  | 'otherSessions';

/**
 * Sessions page translations for all 12 supported languages
 */
const SESSIONS_TRANSLATIONS: Record<SessionsTranslationKey, Record<Language, string>> = {
  pageTitle: {
    me: 'Vođene Lukaaji sesije',
    en: 'Guided Lukaaji Sessions',
    sr: 'Vođene Lukaaji sesije',
    hr: 'Vođene Lukaaji sesije',
    bs: 'Vođene Lukaaji sesije',
    sl: 'Vodene Lukaaji seje',
    mk: 'Водени Lukaaji сесии',
    sq: 'Seancat e udhëhequra Lukaaji',
    bg: 'Водени Lukaaji сесии',
    ro: 'Sesiuni Lukaaji ghidate',
    el: 'Καθοδηγούμενες συνεδρίες Lukaaji',
    tr: 'Rehberli Lukaaji Seansları',
  },
  pageSubtitle: {
    me: 'Istraži našu kolekciju vođenih Lukaaji sesija',
    en: 'Explore our collection of guided Lukaaji sessions',
    sr: 'Istraži našu kolekciju vođenih Lukaaji sesija',
    hr: 'Istraži našu kolekciju vođenih Lukaaji sesija',
    bs: 'Istraži našu kolekciju vođenih Lukaaji sesija',
    sl: 'Razišči našo zbirko vodenih Lukaaji sej',
    mk: 'Истражи ја нашата колекција на водени Lukaaji сесии',
    sq: 'Eksploro koleksionin tonë të seancave të udhëhequra Lukaaji',
    bg: 'Разгледай нашата колекция от водени Lukaaji сесии',
    ro: 'Explorează colecția noastră de sesiuni Lukaaji ghidate',
    el: 'Εξερεύνησε τη συλλογή μας από καθοδηγούμενες συνεδρίες Lukaaji',
    tr: 'Rehberli Lukaaji seansları koleksiyonumuzu keşfet',
  },
  loadError: {
    me: 'Greška pri učitavanju sesija. Pokušaj ponovo.',
    en: 'Failed to load sessions. Please try again.',
    sr: 'Greška pri učitavanju sesija. Pokušaj ponovo.',
    hr: 'Pogreška pri učitavanju sesija. Pokušaj ponovno.',
    bs: 'Greška pri učitavanju sesija. Pokušaj ponovo.',
    sl: 'Nalaganje sej ni uspelo. Poskusi znova.',
    mk: 'Неуспешно вчитување на сесиите. Обиди се повторно.',
    sq: 'Dështoi ngarkimi i seancave. Provo përsëri.',
    bg: 'Неуспешно зареждане на сесиите. Опитай отново.',
    ro: 'Încărcarea sesiunilor a eșuat. Încearcă din nou.',
    el: 'Αποτυχία φόρτωσης συνεδριών. Δοκίμασε ξανά.',
    tr: 'Seanslar yüklenemedi. Tekrar dene.',
  },
  noSearchResults: {
    me: 'Nema pronađenih sesija za tvoju pretragu.',
    en: 'No sessions found matching your search.',
    sr: 'Nema pronađenih sesija za tvoju pretragu.',
    hr: 'Nema pronađenih sesija za tvoju pretragu.',
    bs: 'Nema pronađenih sesija za tvoju pretragu.',
    sl: 'Ni sej, ki bi ustrezale tvojemu iskanju.',
    mk: 'Нема пронајдени сесии за твоето пребарување.',
    sq: 'Asnjë seancë nuk përputhet me atë që shkrove.',
    bg: 'Няма намерени сесии, съответстващи на твоето търсене.',
    ro: 'Nu au fost găsite sesiuni care să corespundă căutării tale.',
    el: 'Δεν βρέθηκαν συνεδρίες που να ταιριάζουν με την αναζήτησή σου.',
    tr: 'Aramanla eşleşen seans bulunamadı.',
  },
  noCategoryResults: {
    me: 'Nema dostupnih sesija u ovoj kategoriji.',
    en: 'No sessions available in this category.',
    sr: 'Nema dostupnih sesija u ovoj kategoriji.',
    hr: 'Nema dostupnih sesija u ovoj kategoriji.',
    bs: 'Nema dostupnih sesija u ovoj kategoriji.',
    sl: 'V tej kategoriji ni na voljo nobenih sej.',
    mk: 'Нема достапни сесии во оваа категорија.',
    sq: 'Nuk ka seanca të disponueshme në këtë kategori.',
    bg: 'Няма налични сесии в тази категория.',
    ro: 'Nu există sesiuni disponibile în această categorie.',
    el: 'Δεν υπάρχουν διαθέσιμες συνεδρίες σε αυτήν την κατηγορία.',
    tr: 'Bu kategoride mevcut seans yok.',
  },
  showAll: {
    me: 'Prikaži sve',
    en: 'Show all',
    sr: 'Prikaži sve',
    hr: 'Prikaži sve',
    bs: 'Prikaži sve',
    sl: 'Prikaži vse',
    mk: 'Прикажи ги сите',
    sq: 'Shfaqi të gjitha',
    bg: 'Покажи всички',
    ro: 'Afișează tot',
    el: 'Εμφάνιση όλων',
    tr: 'Tümünü göster',
  },
  // Last Vođene row: published sessions without a known category (A2 fallback).
  otherSessions: {
    me: 'Ostale sesije',
    en: 'Other sessions',
    sr: 'Ostale sesije',
    hr: 'Ostale sesije',
    bs: 'Ostale sesije',
    sl: 'Druge seje',
    mk: 'Други сесии',
    sq: 'Seanca të tjera',
    bg: 'Други сесии',
    ro: 'Alte sesiuni',
    el: 'Άλλες συνεδρίες',
    tr: 'Diğer seanslar',
  },
};

/** CLDR plural category of a session count; every language lists every form, unused ones repeat `other`. */
type SessionCountForm = 'one' | 'two' | 'few' | 'other';

/**
 * "{count} sessions" under a Vođene row, per CLDR plural category (A2). `two` is used by
 * Slovenian only, `few` by me/sr/hr/bs/sl/ro; the other languages repeat their `other` form.
 */
const SESSION_COUNT_TRANSLATIONS: Record<SessionCountForm, Record<Language, string>> = {
  one: {
    me: '{count} sesija',
    en: '{count} session',
    sr: '{count} sesija',
    hr: '{count} sesija',
    bs: '{count} sesija',
    sl: '{count} seja',
    mk: '{count} сесија',
    sq: '{count} seancë',
    bg: '{count} сесия',
    ro: '{count} sesiune',
    el: '{count} συνεδρία',
    tr: '{count} seans',
  },
  two: {
    me: '{count} sesija',
    en: '{count} sessions',
    sr: '{count} sesija',
    hr: '{count} sesija',
    bs: '{count} sesija',
    sl: '{count} seji',
    mk: '{count} сесии',
    sq: '{count} seanca',
    bg: '{count} сесии',
    ro: '{count} de sesiuni',
    el: '{count} συνεδρίες',
    tr: '{count} seans',
  },
  few: {
    me: '{count} sesije',
    en: '{count} sessions',
    sr: '{count} sesije',
    hr: '{count} sesije',
    bs: '{count} sesije',
    sl: '{count} seje',
    mk: '{count} сесии',
    sq: '{count} seanca',
    bg: '{count} сесии',
    ro: '{count} sesiuni',
    el: '{count} συνεδρίες',
    tr: '{count} seans',
  },
  other: {
    me: '{count} sesija',
    en: '{count} sessions',
    sr: '{count} sesija',
    hr: '{count} sesija',
    bs: '{count} sesija',
    sl: '{count} sej',
    mk: '{count} сесии',
    sq: '{count} seanca',
    bg: '{count} сесии',
    ro: '{count} de sesiuni',
    el: '{count} συνεδρίες',
    tr: '{count} seans',
  },
};

/** "12 sesija", "3 sesije", "1 sesija": the count in the language's plural form (Intl.PluralRules on the mapped locale). */
export function getSessionCountLabel(count: number, language: Language): string {
  const forms = {
    one: SESSION_COUNT_TRANSLATIONS.one[language],
    two: SESSION_COUNT_TRANSLATIONS.two[language],
    few: SESSION_COUNT_TRANSLATIONS.few[language],
    other: SESSION_COUNT_TRANSLATIONS.other[language],
  };
  return selectPlural(count, language, forms).replace('{count}', String(count));
}

/**
 * Get translated sessions page string for the given language
 * @param key - The translation key
 * @param language - The target language
 * @returns The translated string
 */
export function getSessionsTranslation(key: SessionsTranslationKey, language: Language): string {
  return SESSIONS_TRANSLATIONS[key][language];
}

/**
 * Session detail page translation keys
 */
export type SessionDetailTranslationKey =
  | 'loadError'
  | 'backToSessions'
  | 'completed'
  | 'premiumContent'
  | 'upgradeToAccess'
  | 'viewPricing'
  | 'benefits'
  | 'removeFromFavorites'
  | 'addToFavorites'
  | 'signIn'
  | 'trackProgressAndSaveFavorites'
  | 'yourProgress'
  | 'complete';

/**
 * Session detail translations for all 12 supported languages
 */
const SESSION_DETAIL_TRANSLATIONS: Record<SessionDetailTranslationKey, Record<Language, string>> = {
  loadError: {
    me: 'Učitavanje sesije nije uspjelo. Pokušaj ponovo.',
    en: 'Failed to load session. Please try again.',
    sr: 'Učitavanje sesije nije uspelo. Pokušaj ponovo.',
    hr: 'Učitavanje sesije nije uspjelo. Pokušaj ponovno.',
    bs: 'Učitavanje sesije nije uspjelo. Pokušaj ponovo.',
    sl: 'Nalaganje seje ni uspelo. Poskusi znova.',
    mk: 'Вчитувањето на сесијата не успеа. Обиди се повторно.',
    sq: 'Ngarkimi i seancës dështoi. Provo përsëri.',
    bg: 'Неуспешно зареждане на сесията. Опитай отново.',
    ro: 'Încărcarea sesiunii a eșuat. Încearcă din nou.',
    el: 'Αποτυχία φόρτωσης συνεδρίας. Δοκίμασε ξανά.',
    tr: 'Seans yüklenemedi. Tekrar dene.',
  },
  backToSessions: {
    me: 'Nazad na Lukaaji sesije',
    en: 'Back to Lukaaji Sessions',
    sr: 'Nazad na Lukaaji sesije',
    hr: 'Natrag na Lukaaji sesije',
    bs: 'Nazad na Lukaaji sesije',
    sl: 'Nazaj na Lukaaji seje',
    mk: 'Назад на Lukaaji сесии',
    sq: 'Kthehu te Lukaaji seancat',
    bg: 'Назад към Lukaaji сесиите',
    ro: 'Înapoi la sesiunile Lukaaji',
    el: 'Επιστροφή στις Lukaaji συνεδρίες',
    tr: 'Lukaaji Seanslarına Dön',
  },
  completed: {
    me: 'Završeno',
    en: 'Completed',
    sr: 'Završeno',
    hr: 'Završeno',
    bs: 'Završeno',
    sl: 'Zaključeno',
    mk: 'Завршено',
    sq: 'Përfunduar',
    bg: 'Завършено',
    ro: 'Finalizat',
    el: 'Ολοκληρώθηκε',
    tr: 'Tamamlandı',
  },
  premiumContent: {
    me: 'Premium sadržaj',
    en: 'Premium Content',
    sr: 'Premium sadržaj',
    hr: 'Premium sadržaj',
    bs: 'Premium sadržaj',
    sl: 'Premium vsebina',
    mk: 'Премиум содржина',
    sq: 'Përmbajtje Premium',
    bg: 'Премиум съдържание',
    ro: 'Conținut Premium',
    el: 'Premium Περιεχόμενο',
    tr: 'Premium İçerik',
  },
  upgradeToAccess: {
    me: 'Nadogradi za pristup ovoj sesiji i svim premium sadržajima.',
    en: 'Upgrade to access this session and all premium content.',
    sr: 'Nadogradi za pristup ovoj sesiji i svim premium sadržajima.',
    hr: 'Nadogradi za pristup ovoj sesiji i svim premium sadržajima.',
    bs: 'Nadogradi za pristup ovoj sesiji i svim premium sadržajima.',
    sl: 'Nadgradi za dostop do te seje in vseh premium vsebin.',
    mk: 'Надгради за пристап до оваа сесија и сите премиум содржини.',
    sq: 'Përmirëso për të pasur akses në këtë seancë dhe në të gjitha përmbajtjet premium.',
    bg: 'Надгради за достъп до тази сесия и цялото премиум съдържание.',
    ro: 'Fă upgrade pentru a accesa această sesiune și tot conținutul premium.',
    el: 'Αναβάθμισε για πρόσβαση σε αυτή τη συνεδρία και σε όλο το premium περιεχόμενο.',
    tr: 'Bu seansa ve tüm premium içeriğe erişmek için yükselt.',
  },
  viewPricing: {
    me: 'Pogledaj cijene',
    en: 'View Pricing',
    sr: 'Pogledaj cene',
    hr: 'Pogledaj cijene',
    bs: 'Pogledaj cijene',
    sl: 'Ogled cen',
    mk: 'Погледни цени',
    sq: 'Shiko çmimet',
    bg: 'Виж цените',
    ro: 'Vezi prețurile',
    el: 'Δες τις τιμές',
    tr: 'Fiyatları Gör',
  },
  benefits: {
    me: 'Koristi',
    en: 'Benefits',
    sr: 'Koristi',
    hr: 'Koristi',
    bs: 'Koristi',
    sl: 'Koristi',
    mk: 'Придобивки',
    sq: 'Përfitimet',
    bg: 'Ползи',
    ro: 'Beneficii',
    el: 'Οφέλη',
    tr: 'Faydalar',
  },
  removeFromFavorites: {
    me: 'Ukloni iz favorita',
    en: 'Remove from Favorites',
    sr: 'Ukloni iz omiljenih',
    hr: 'Ukloni iz favorita',
    bs: 'Ukloni iz omiljenih',
    sl: 'Odstrani iz priljubljenih',
    mk: 'Отстрани од омилени',
    sq: 'Hiq nga të preferuarat',
    bg: 'Премахни от любими',
    ro: 'Elimină din favorite',
    el: 'Αφαίρεση από τα αγαπημένα',
    tr: 'Favorilerden Kaldır',
  },
  addToFavorites: {
    me: 'Dodaj u favorite',
    en: 'Add to Favorites',
    sr: 'Dodaj u omiljene',
    hr: 'Dodaj u favorite',
    bs: 'Dodaj u omiljene',
    sl: 'Dodaj med priljubljene',
    mk: 'Додај во омилени',
    sq: 'Shto në të preferuarat',
    bg: 'Добави в любими',
    ro: 'Adaugă la favorite',
    el: 'Προσθήκη στα αγαπημένα',
    tr: 'Favorilere Ekle',
  },
  signIn: {
    me: 'Prijavi se',
    en: 'Sign in',
    sr: 'Prijavi se',
    hr: 'Prijavi se',
    bs: 'Prijavi se',
    sl: 'Prijavi se',
    mk: 'Најави се',
    sq: 'Identifikohu',
    bg: 'Влез',
    ro: 'Conectează-te',
    el: 'Συνδέσου',
    tr: 'Giriş yap',
  },
  trackProgressAndSaveFavorites: {
    me: 'da pratiš napredak i sačuvaš omiljene.',
    en: 'to track your progress and save favorites.',
    sr: 'da pratiš napredak i sačuvaš omiljene.',
    hr: 'i prati napredak te spremi favorite.',
    bs: 'da pratiš napredak i sačuvaš omiljene.',
    sl: 'in spremljaj napredek ter shranjuj priljubljene.',
    mk: 'за да го следиш напредокот и да зачуваш омилени.',
    sq: 'për të ndjekur progresin dhe për të ruajtur të preferuarat.',
    bg: 'за да следиш напредъка си и да запазваш любими.',
    ro: 'pentru a-ți urmări progresul și a salva favoritele.',
    el: 'για να παρακολουθείς την πρόοδό σου και να αποθηκεύεις αγαπημένα.',
    tr: 'ilerlemeni takip etmek ve favorileri kaydetmek için.',
  },
  yourProgress: {
    me: 'Tvoj napredak',
    en: 'Your progress',
    sr: 'Tvoj napredak',
    hr: 'Tvoj napredak',
    bs: 'Tvoj napredak',
    sl: 'Tvoj napredek',
    mk: 'Твојот напредок',
    sq: 'Progresi yt',
    bg: 'Твоят напредък',
    ro: 'Progresul tău',
    el: 'Η πρόοδός σου',
    tr: 'İlerlemen',
  },
  complete: {
    me: 'završeno',
    en: 'complete',
    sr: 'završeno',
    hr: 'završeno',
    bs: 'završeno',
    sl: 'zaključeno',
    mk: 'завршено',
    sq: 'përfunduar',
    bg: 'завършено',
    ro: 'finalizat',
    el: 'ολοκληρώθηκε',
    tr: 'tamamlandı',
  },
};

/**
 * Get translated session detail string for the given language
 * @param key - The translation key
 * @param language - The target language
 * @returns The translated string
 */
export function getSessionDetailTranslation(key: SessionDetailTranslationKey, language: Language): string {
  return SESSION_DETAIL_TRANSLATIONS[key][language];
}

/**
 * Payment Success page translation keys
 */
export type PaymentSuccessTranslationKey =
  | 'verifyingPayment'
  | 'verificationPending'
  | 'paymentReceivedProcessing'
  | 'waitAndRefresh'
  | 'refreshPage'
  | 'viewProfile'
  | 'paymentSuccessful'
  | 'thankYouSubscribing'
  | 'fullAccessMessage'
  | 'exploreSessions';

/**
 * Payment Success translations for all 12 supported languages
 */
const PAYMENT_SUCCESS_TRANSLATIONS: Record<PaymentSuccessTranslationKey, Record<Language, string>> = {
  verifyingPayment: {
    me: 'Provjeravamo tvoju uplatu...',
    en: 'Verifying your payment...',
    sr: 'Proveravamo tvoju uplatu...',
    hr: 'Provjeravamo tvoju uplatu...',
    bs: 'Provjeravamo tvoju uplatu...',
    sl: 'Preverjamo tvoje plačilo...',
    mk: 'Ја проверуваме твојата уплата...',
    sq: 'Po verifikojmë pagesën tënde...',
    bg: 'Проверяваме твоето плащане...',
    ro: 'Se verifică plata ta...',
    el: 'Επαλήθευση της πληρωμής σου...',
    tr: 'Ödemen doğrulanıyor...',
  },
  verificationPending: {
    me: 'Verifikacija u toku',
    en: 'Verification Pending',
    sr: 'Verifikacija u toku',
    hr: 'Verifikacija u tijeku',
    bs: 'Verifikacija u toku',
    sl: 'Preverjanje v teku',
    mk: 'Верификацијата е во тек',
    sq: 'Verifikimi në pritje',
    bg: 'Проверката е в процес',
    ro: 'Verificare în așteptare',
    el: 'Επαλήθευση σε εκκρεμότητα',
    tr: 'Doğrulama bekliyor',
  },
  paymentReceivedProcessing: {
    me: 'Tvoja uplata je primljena, ali provjera je još u toku',
    en: 'Your payment was received but verification is still processing',
    sr: 'Tvoja uplata je primljena, ali provera je još u toku',
    hr: 'Tvoja uplata je primljena, ali provjera je još u tijeku',
    bs: 'Tvoja uplata je primljena, ali provjera je još u toku',
    sl: 'Tvoje plačilo je bilo prejeto, preverjanje pa še poteka',
    mk: 'Твојата уплата е примена, но проверката сè уште трае',
    sq: 'Pagesa jote u mor, por verifikimi është ende në vazhdim',
    bg: 'Плащането ти е получено, но проверката все още продължава',
    ro: 'Plata ta a fost primită, dar verificarea este încă în curs',
    el: 'Η πληρωμή σου ελήφθη, αλλά η επαλήθευση βρίσκεται ακόμα σε εξέλιξη',
    tr: 'Ödemen alındı ancak doğrulama hâlâ sürüyor',
  },
  waitAndRefresh: {
    me: 'Sačekaj trenutak i osvježi stranicu ili kontaktiraj podršku ako problem potraje.',
    en: 'Please wait a moment and refresh the page, or contact support if the issue persists.',
    sr: 'Sačekaj trenutak i osveži stranicu ili kontaktiraj podršku ako problem potraje.',
    hr: 'Pričekaj trenutak i osvježi stranicu ili kontaktiraj podršku ako problem potraje.',
    bs: 'Sačekaj trenutak i osvježi stranicu ili kontaktiraj podršku ako problem potraje.',
    sl: 'Počakaj trenutek in osveži stran ali se obrni na podporo, če težava ne izgine.',
    mk: 'Почекај момент и освежи ја страницата или контактирај ја поддршката ако проблемот продолжи.',
    sq: 'Prit një moment dhe rifresko faqen, ose kontakto mbështetjen nëse problemi vazhdon.',
    bg: 'Изчакай момент и опресни страницата или се свържи с поддръжката, ако проблемът продължава.',
    ro: 'Așteaptă un moment și reîmprospătează pagina sau contactează asistența dacă problema persistă.',
    el: 'Περίμενε λίγο και ανανέωσε τη σελίδα ή επικοινώνησε με την υποστήριξη αν το πρόβλημα παραμένει.',
    tr: 'Biraz bekle ve sayfayı yenile ya da sorun devam ederse destekle iletişime geç.',
  },
  refreshPage: {
    me: 'Osvježi stranicu',
    en: 'Refresh Page',
    sr: 'Osveži stranicu',
    hr: 'Osvježi stranicu',
    bs: 'Osvježi stranicu',
    sl: 'Osveži stran',
    mk: 'Освежи страница',
    sq: 'Rifresko faqen',
    bg: 'Опресни страницата',
    ro: 'Reîmprospătează pagina',
    el: 'Ανανέωση σελίδας',
    tr: 'Sayfayı yenile',
  },
  viewProfile: {
    me: 'Pogledaj profil',
    en: 'View Profile',
    sr: 'Pogledaj profil',
    hr: 'Pogledaj profil',
    bs: 'Pogledaj profil',
    sl: 'Poglej profil',
    mk: 'Погледни профил',
    sq: 'Shiko profilin',
    bg: 'Виж профила',
    ro: 'Vezi profilul',
    el: 'Προβολή προφίλ',
    tr: 'Profili görüntüle',
  },
  paymentSuccessful: {
    me: 'Plaćanje uspješno!',
    en: 'Payment Successful!',
    sr: 'Plaćanje uspešno!',
    hr: 'Plaćanje uspješno!',
    bs: 'Plaćanje uspješno!',
    sl: 'Plačilo uspešno!',
    mk: 'Плаќањето е успешно!',
    sq: 'Pagesa u krye me sukses!',
    bg: 'Плащането е успешно!',
    ro: 'Plata a fost efectuată cu succes!',
    el: 'Η πληρωμή ολοκληρώθηκε!',
    tr: 'Ödeme başarılı!',
  },
  thankYouSubscribing: {
    me: 'Hvala ti na pretplati na Lukaaji',
    en: 'Thank you for subscribing to Lukaaji',
    sr: 'Hvala ti na pretplati na Lukaaji',
    hr: 'Hvala ti na pretplati na Lukaaji',
    bs: 'Hvala ti na pretplati na Lukaaji',
    sl: 'Hvala za naročnino na Lukaaji',
    mk: 'Ти благодариме за претплатата на Lukaaji',
    sq: 'Faleminderit që u abonove në Lukaaji',
    bg: 'Благодарим ти, че се абонира за Lukaaji',
    ro: 'Îți mulțumim că te-ai abonat la Lukaaji',
    el: 'Σε ευχαριστούμε για τη συνδρομή σου στο Lukaaji',
    tr: 'Lukaaji aboneliğin için teşekkür ederiz',
  },
  fullAccessMessage: {
    me: 'Sada imaš pun pristup svim premium sesijama. Tvoj trenutak za sebe počinje ovdje.',
    en: 'You now have full access to all premium sessions. Your moment for yourself begins here.',
    sr: 'Sada imaš pun pristup svim premium sesijama. Tvoj trenutak za sebe počinje ovde.',
    hr: 'Sada imaš puni pristup svim premium sesijama. Tvoj trenutak za sebe počinje ovdje.',
    bs: 'Sada imaš pun pristup svim premium sesijama. Tvoj trenutak za sebe počinje ovdje.',
    sl: 'Zdaj imaš popoln dostop do vseh premium sej. Tvoj trenutek zase se začne tukaj.',
    mk: 'Сега имаш целосен пристап до сите премиум сесии. Твојот момент за себе почнува тука.',
    sq: 'Tani ke akses të plotë në të gjitha seancat premium. Momenti yt për veten fillon këtu.',
    bg: 'Вече имаш пълен достъп до всички премиум сесии. Твоят момент за себе си започва тук.',
    ro: 'Acum ai acces complet la toate sesiunile premium. Momentul tău pentru tine începe aici.',
    el: 'Τώρα έχεις πλήρη πρόσβαση σε όλες τις premium συνεδρίες. Η στιγμή σου για τον εαυτό σου ξεκινά εδώ.',
    tr: 'Artık tüm premium seanslara tam erişimin var. Kendine ayırdığın an burada başlıyor.',
  },
  exploreSessions: {
    me: 'Istraži sesije',
    en: 'Explore Sessions',
    sr: 'Istraži sesije',
    hr: 'Istraži sesije',
    bs: 'Istraži sesije',
    sl: 'Razišči seje',
    mk: 'Истражи сесии',
    sq: 'Eksploro seancat',
    bg: 'Разгледай сесиите',
    ro: 'Explorează sesiunile',
    el: 'Εξερεύνησε τις συνεδρίες',
    tr: 'Seansları keşfet',
  },
};

/**
 * Get translated Payment Success page string for the given language
 * @param key - The translation key
 * @param language - The target language
 * @returns The translated string
 */
export function getPaymentSuccessTranslation(key: PaymentSuccessTranslationKey, language: Language): string {
  return PAYMENT_SUCCESS_TRANSLATIONS[key][language];
}

/**
 * Plan name translations for all supported languages
 */
/** Plan names keyed by plan id (A5): Luka's WEB5 names, 6 and 12 months from R1. */
const PLAN_TRANSLATIONS: Record<string, Record<Language, string>> = {
  'basic_monthly': {
    me: 'Mjesečno',
    en: 'Monthly',
    sr: 'Mesečno',
    hr: 'Mjesečno',
    bs: 'Mjesečno',
    sl: 'Mesečno',
    mk: 'Месечно',
    sq: 'Mujore',
    bg: 'Месечно',
    ro: 'Lunar',
    el: 'Μηνιαίο',
    tr: 'Aylık',
  },
  'quarterly': {
    me: '3 mjeseca',
    en: '3 months',
    sr: '3 meseca',
    hr: '3 mjeseca',
    bs: '3 mjeseca',
    sl: '3 mesece',
    mk: '3 месеци',
    sq: '3 muaj',
    bg: '3 месеца',
    ro: '3 luni',
    el: '3 μήνες',
    tr: '3 ay',
  },
  'semi_annual': {
    me: '6 mjeseci',
    en: '6 months',
    sr: '6 meseci',
    hr: '6 mjeseci',
    bs: '6 mjeseci',
    sl: '6 mesecev',
    mk: '6 месеци',
    sq: '6 muaj',
    bg: '6 месеца',
    ro: '6 luni',
    el: '6 μήνες',
    tr: '6 ay',
  },
  'annual': {
    me: '12 mjeseci',
    en: '12 months',
    sr: '12 meseci',
    hr: '12 mjeseci',
    bs: '12 mjeseci',
    sl: '12 mesecev',
    mk: '12 месеци',
    sq: '12 muaj',
    bg: '12 месеца',
    ro: '12 luni',
    el: '12 μήνες',
    tr: '12 ay',
  },
  'trial': {
    me: 'Probni period',
    en: 'Free trial',
    sr: 'Probni period',
    hr: 'Probno razdoblje',
    bs: 'Probni period',
    sl: 'Brezplačni preizkus',
    mk: 'Пробен период',
    sq: 'Periudha provuese',
    bg: 'Пробен период',
    ro: 'Perioadă de probă',
    el: 'Δωρεάν δοκιμή',
    tr: 'Ücretsiz deneme',
  },
};

/**
 * Translated plan name for a plan id (`basic_monthly`, `quarterly`, `semi_annual`, `annual`, `trial`).
 * Falls back to the given value if it is not a known plan id.
 */
export function translatePlanName(planName: string | null | undefined, language: Language): string {
  if (!planName) return '';

  const translations = PLAN_TRANSLATIONS[planName];
  if (translations) {
    return translations[language] || planName;
  }

  // Return original if no translation found
  return planName;
}

/**
 * Profile page translation keys
 */
export type ProfileTranslationKey =
  | 'signInToView'
  | 'signInDesc'
  | 'signIn'
  | 'profile'
  | 'manageAccountAndSubscription'
  | 'accountInformation'
  | 'subscription'
  | 'status'
  | 'freeTrial'
  | 'free'
  | 'plan'
  | 'renews'
  | 'trialEnds'
  | 'accessUntil'
  | 'paymentPending'
  | 'trialExpiredAlert'
  | 'manageSubscription'
  | 'upgradeToPremium'
  | 'affiliateProgram'
  | 'yourReferralLink'
  | 'totalReferrals'
  | 'active'
  | 'earnings'
  | 'shareToStartEarning'
  | 'giftSubscriptions'
  | 'shareGift'
  | 'purchaseGift'
  | 'noGiftsSent'
  | 'paymentHistory'
  | 'paymentHistoryAppears'
  | 'settings'
  | 'language'
  | 'signOut'
  | 'tools'
  | 'pendulum'
  | 'pendulumDesc'
  | 'availableWithSubscription'
  | 'deleteOwnsBook';

/**
 * Profile translations for all 12 supported languages
 */
const PROFILE_TRANSLATIONS: Record<ProfileTranslationKey, Record<Language, string>> = {
  signInToView: {
    me: 'Prijavi se za pregled profila',
    en: 'Sign in to view profile',
    sr: 'Prijavi se za pregled profila',
    hr: 'Prijavi se za pregled profila',
    bs: 'Prijavi se za pregled profila',
    sl: 'Prijavi se za ogled profila',
    mk: 'Најави се за преглед на профилот',
    sq: 'Identifikohu për të parë profilin',
    bg: 'Влез, за да видиш профила',
    ro: 'Conectează-te pentru a vedea profilul',
    el: 'Συνδέσου για να δεις το προφίλ',
    tr: 'Profili görmek için giriş yap',
  },
  signInDesc: {
    me: 'Pristupi postavkama naloga i pretplati.',
    en: 'Access your account settings and subscription.',
    sr: 'Pristupi podešavanjima naloga i pretplati.',
    hr: 'Pristupi postavkama računa i pretplati.',
    bs: 'Pristupi postavkama računa i pretplati.',
    sl: 'Dostopaj do nastavitev računa in naročnine.',
    mk: 'Пристапи до поставките на сметката и претплатата.',
    sq: 'Hyr te cilësimet e llogarisë dhe abonimit.',
    bg: 'Влез в настройките на акаунта и абонамента си.',
    ro: 'Accesează setările contului și abonamentul.',
    el: 'Δες τις ρυθμίσεις του λογαριασμού και της συνδρομής σου.',
    tr: 'Hesap ayarlarına ve aboneliğine eriş.',
  },
  signIn: {
    me: 'Prijava',
    en: 'Sign In',
    sr: 'Prijava',
    hr: 'Prijava',
    bs: 'Prijava',
    sl: 'Prijava',
    mk: 'Најава',
    sq: 'Identifikohu',
    bg: 'Вход',
    ro: 'Conectare',
    el: 'Σύνδεση',
    tr: 'Giriş Yap',
  },
  profile: {
    me: 'Profil',
    en: 'Profile',
    sr: 'Profil',
    hr: 'Profil',
    bs: 'Profil',
    sl: 'Profil',
    mk: 'Профил',
    sq: 'Profili',
    bg: 'Профил',
    ro: 'Profil',
    el: 'Προφίλ',
    tr: 'Profil',
  },
  manageAccountAndSubscription: {
    me: 'Upravljaj nalogom i pretplatom',
    en: 'Manage your account and subscription',
    sr: 'Upravljaj nalogom i pretplatom',
    hr: 'Upravljaj računom i pretplatom',
    bs: 'Upravljaj računom i pretplatom',
    sl: 'Upravljaj račun in naročnino',
    mk: 'Управувај со сметката и претплатата',
    sq: 'Menaxho llogarinë dhe abonimin',
    bg: 'Управлявай акаунта и абонамента',
    ro: 'Gestionează contul și abonamentul',
    el: 'Διαχειρίσου τον λογαριασμό και τη συνδρομή σου',
    tr: 'Hesabını ve aboneliğini yönet',
  },
  accountInformation: {
    me: 'Informacije o Računu',
    en: 'Account Information',
    sr: 'Informacije o Nalogu',
    hr: 'Informacije o Računu',
    bs: 'Informacije o Računu',
    sl: 'Podatki o Računu',
    mk: 'Информации за Сметката',
    sq: 'Informacioni i Llogarisë',
    bg: 'Информация за Акаунта',
    ro: 'Informații Cont',
    el: 'Πληροφορίες Λογαριασμού',
    tr: 'Hesap Bilgileri',
  },
  subscription: {
    me: 'Pretplata',
    en: 'Subscription',
    sr: 'Pretplata',
    hr: 'Pretplata',
    bs: 'Pretplata',
    sl: 'Naročnina',
    mk: 'Претплата',
    sq: 'Abonimi',
    bg: 'Абонамент',
    ro: 'Abonament',
    el: 'Συνδρομή',
    tr: 'Abonelik',
  },
  status: {
    me: 'Status',
    en: 'Status',
    sr: 'Status',
    hr: 'Status',
    bs: 'Status',
    sl: 'Status',
    mk: 'Статус',
    sq: 'Statusi',
    bg: 'Статус',
    ro: 'Status',
    el: 'Κατάσταση',
    tr: 'Durum',
  },
  freeTrial: {
    me: 'Probni period',
    en: 'Free trial',
    sr: 'Probni period',
    hr: 'Probno razdoblje',
    bs: 'Probni period',
    sl: 'Brezplačni preizkus',
    mk: 'Пробен период',
    sq: 'Periudhë provuese',
    bg: 'Пробен период',
    ro: 'Perioadă de probă',
    el: 'Δωρεάν δοκιμή',
    tr: 'Ücretsiz deneme',
  },
  free: {
    me: 'Besplatno',
    en: 'Free',
    sr: 'Besplatno',
    hr: 'Besplatno',
    bs: 'Besplatno',
    sl: 'Brezplačno',
    mk: 'Бесплатно',
    sq: 'Falas',
    bg: 'Безплатно',
    ro: 'Gratuit',
    el: 'Δωρεάν',
    tr: 'Ücretsiz',
  },
  plan: {
    me: 'Plan',
    en: 'Plan',
    sr: 'Plan',
    hr: 'Plan',
    bs: 'Plan',
    sl: 'Paket',
    mk: 'План',
    sq: 'Plani',
    bg: 'План',
    ro: 'Plan',
    el: 'Πλάνο',
    tr: 'Plan',
  },
  renews: {
    me: 'Obnavlja se',
    en: 'Renews',
    sr: 'Obnavlja se',
    hr: 'Obnavlja se',
    bs: 'Obnavlja se',
    sl: 'Podaljša se',
    mk: 'Се обновува',
    sq: 'Rinovohet',
    bg: 'Подновява се',
    ro: 'Se reînnoiește',
    el: 'Ανανεώνεται',
    tr: 'Yenileniyor',
  },
  trialEnds: {
    me: 'Probni period ističe',
    en: 'Trial ends',
    sr: 'Probni period ističe',
    hr: 'Probno razdoblje ističe',
    bs: 'Probni period ističe',
    sl: 'Preizkus se konča',
    mk: 'Пробниот период завршува',
    sq: 'Periudha provuese përfundon',
    bg: 'Пробният период завършва',
    ro: 'Perioada de probă se termină',
    el: 'Η δοκιμή λήγει',
    tr: 'Deneme bitiyor',
  },
  accessUntil: {
    me: 'Pristup do',
    en: 'Access until',
    sr: 'Pristup do',
    hr: 'Pristup do',
    bs: 'Pristup do',
    sl: 'Dostop do',
    mk: 'Пристап до',
    sq: 'Qasje deri më',
    bg: 'Достъп до',
    ro: 'Acces până pe',
    el: 'Πρόσβαση έως',
    tr: 'Erişim bitişi',
  },
  paymentPending: {
    me: 'Plaćanje za novi period još nije potvrđeno.',
    en: 'Payment for the new period is not confirmed yet.',
    sr: 'Plaćanje za novi period još nije potvrđeno.',
    hr: 'Plaćanje za novo razdoblje još nije potvrđeno.',
    bs: 'Plaćanje za novi period još nije potvrđeno.',
    sl: 'Plačilo za novo obdobje še ni potrjeno.',
    mk: 'Плаќањето за новиот период сè уште не е потврдено.',
    sq: 'Pagesa për periudhën e re ende nuk është konfirmuar.',
    bg: 'Плащането за новия период още не е потвърдено.',
    ro: 'Plata pentru noua perioadă nu este încă confirmată.',
    el: 'Η πληρωμή για τη νέα περίοδο δεν έχει επιβεβαιωθεί ακόμη.',
    tr: 'Yeni dönemin ödemesi henüz onaylanmadı.',
  },
  trialExpiredAlert: {
    me: 'Tvoj probni period je istekao. Nadogradi za nastavak pristupa premium sadržaju.',
    en: 'Your free trial has expired. Upgrade to continue accessing premium content.',
    sr: 'Tvoj probni period je istekao. Nadogradi za nastavak pristupa premium sadržaju.',
    hr: 'Tvoje probno razdoblje je isteklo. Nadogradi za nastavak pristupa premium sadržaju.',
    bs: 'Tvoj probni period je istekao. Nadogradi za nastavak pristupa premium sadržaju.',
    sl: 'Tvoj brezplačni preizkus je potekel. Nadgradi za nadaljnji dostop do premium vsebin.',
    mk: 'Твојот пробен период истече. Надгради за да продолжиш со пристап до премиум содржините.',
    sq: 'Periudha jote provuese ka skaduar. Përmirëso për të vazhduar aksesin në përmbajtjen premium.',
    bg: 'Пробният ти период изтече. Надгради, за да продължиш достъпа до премиум съдържанието.',
    ro: 'Perioada ta de probă a expirat. Fă upgrade pentru a continua accesul la conținutul premium.',
    el: 'Η δωρεάν δοκιμή σου έληξε. Αναβάθμισε για να συνεχίσεις την πρόσβαση στο premium περιεχόμενο.',
    tr: 'Ücretsiz denemen sona erdi. Premium içeriğe erişmeye devam etmek için yükselt.',
  },
  manageSubscription: {
    me: 'Upravljaj Pretplatom',
    en: 'Manage Subscription',
    sr: 'Upravljaj Pretplatom',
    hr: 'Upravljaj Pretplatom',
    bs: 'Upravljaj Pretplatom',
    sl: 'Upravljaj Naročnino',
    mk: 'Управувај со Претплата',
    sq: 'Menaxho Abonimin',
    bg: 'Управление на Абонамент',
    ro: 'Gestionează Abonamentul',
    el: 'Διαχείριση Συνδρομής',
    tr: 'Aboneliği Yönet',
  },
  upgradeToPremium: {
    me: 'Nadogradi na Premium',
    en: 'Upgrade to Premium',
    sr: 'Nadogradi na Premium',
    hr: 'Nadogradi na Premium',
    bs: 'Nadogradi na Premium',
    sl: 'Nadgradi na Premium',
    mk: 'Надгради на Premium',
    sq: 'Përmirëso në Premium',
    bg: 'Надгради до Premium',
    ro: 'Fă upgrade la Premium',
    el: 'Αναβάθμιση σε Premium',
    tr: 'Premium\'a Yükselt',
  },
  affiliateProgram: {
    me: 'Partnerski Program',
    en: 'Affiliate Program',
    sr: 'Partnerski Program',
    hr: 'Partnerski Program',
    bs: 'Partnerski Program',
    sl: 'Partnerski Program',
    mk: 'Партнерска Програма',
    sq: 'Programi i Partneritetit',
    bg: 'Партньорска Програма',
    ro: 'Program Afiliat',
    el: 'Πρόγραμμα Συνεργατών',
    tr: 'Ortaklık Programı',
  },
  yourReferralLink: {
    me: 'Tvoj link za preporuku',
    en: 'Your Referral Link',
    sr: 'Tvoj link za preporuku',
    hr: 'Tvoj link za preporuku',
    bs: 'Tvoj link za preporuku',
    sl: 'Tvoja priporočilna povezava',
    mk: 'Твојот линк за препорака',
    sq: 'Lidhja jote e referimit',
    bg: 'Твоят линк за препоръка',
    ro: 'Linkul tău de recomandare',
    el: 'Ο σύνδεσμος παραπομπής σου',
    tr: 'Referans linkin',
  },
  totalReferrals: {
    me: 'Ukupno Preporuka',
    en: 'Total Referrals',
    sr: 'Ukupno Preporuka',
    hr: 'Ukupno Preporuka',
    bs: 'Ukupno Preporuka',
    sl: 'Skupaj Priporočil',
    mk: 'Вкупно Препораки',
    sq: 'Gjithsej Referimet',
    bg: 'Общо Препоръки',
    ro: 'Total Recomandări',
    el: 'Σύνολο Παραπομπών',
    tr: 'Toplam Referanslar',
  },
  active: {
    me: 'Aktivnih',
    en: 'Active',
    sr: 'Aktivnih',
    hr: 'Aktivnih',
    bs: 'Aktivnih',
    sl: 'Aktivnih',
    mk: 'Активни',
    sq: 'Aktive',
    bg: 'Активни',
    ro: 'Active',
    el: 'Ενεργές',
    tr: 'Aktif',
  },
  earnings: {
    me: 'Zarada',
    en: 'Earnings',
    sr: 'Zarada',
    hr: 'Zarada',
    bs: 'Zarada',
    sl: 'Zaslužek',
    mk: 'Заработка',
    sq: 'Fitimet',
    bg: 'Печалби',
    ro: 'Câștiguri',
    el: 'Κέρδη',
    tr: 'Kazançlar',
  },
  shareToStartEarning: {
    me: 'Podijeli svoj link i počni da zarađuješ!',
    en: 'Share your link to start earning!',
    sr: 'Podeli svoj link i počni da zarađuješ!',
    hr: 'Podijeli svoj link i počni zarađivati!',
    bs: 'Podijeli svoj link i počni zarađivati!',
    sl: 'Deli svojo povezavo in začni služiti!',
    mk: 'Сподели го својот линк и почни да заработуваш!',
    sq: 'Ndaj lidhjen tënde dhe fillo të fitosh!',
    bg: 'Сподели линка си и започни да печелиш!',
    ro: 'Distribuie linkul tău și începe să câștigi!',
    el: 'Μοιράσου τον σύνδεσμό σου και άρχισε να κερδίζεις!',
    tr: 'Linkini paylaş ve kazanmaya başla!',
  },
  giftSubscriptions: {
    me: 'Poklon Pretplate',
    en: 'Gift Subscriptions',
    sr: 'Poklon Pretplate',
    hr: 'Poklon Pretplate',
    bs: 'Poklon Pretplate',
    sl: 'Darilne Naročnine',
    mk: 'Подарок Претплати',
    sq: 'Abonim Dhuratë',
    bg: 'Подарък Абонаменти',
    ro: 'Abonamente Cadou',
    el: 'Δώρο Συνδρομές',
    tr: 'Hediye Abonelikler',
  },
  shareGift: {
    me: 'Pokloni pretplatu svojim najdražima',
    en: 'Give a subscription to your loved ones',
    sr: 'Pokloni pretplatu svojim najdražima',
    hr: 'Pokloni pretplatu svojim najdražima',
    bs: 'Pokloni pretplatu svojim najdražima',
    sl: 'Podari naročnino svojim najdražjim',
    mk: 'Подари претплата на своите најблиски',
    sq: 'Dhuroju një abonim të dashurve të tu',
    bg: 'Подари абонамент на своите близки',
    ro: 'Oferă un abonament celor dragi',
    el: 'Χάρισε μια συνδρομή στους αγαπημένους σου',
    tr: 'Sevdiklerine abonelik hediye et',
  },
  purchaseGift: {
    me: 'Kupi Poklon',
    en: 'Purchase Gift',
    sr: 'Kupi Poklon',
    hr: 'Kupi Poklon',
    bs: 'Kupi Poklon',
    sl: 'Kupi Darilo',
    mk: 'Купи Подарок',
    sq: 'Bli Dhuratë',
    bg: 'Купи Подарък',
    ro: 'Cumpără Cadou',
    el: 'Αγόρασε δώρο',
    tr: 'Hediye Satın Al',
  },
  noGiftsSent: {
    me: 'Još nema poslanih poklon pretplata',
    en: 'No gift subscriptions sent yet',
    sr: 'Još nema poslatih poklon pretplata',
    hr: 'Još nema poslanih poklon pretplata',
    bs: 'Još nema poslanih poklon pretplata',
    sl: 'Še ni poslanih darilnih naročnin',
    mk: 'Сè уште нема испратени подарок претплати',
    sq: 'Ende nuk ka abonim dhuratë të dërguar',
    bg: 'Все още няма изпратени подарък абонаменти',
    ro: 'Încă nu au fost trimise abonamente cadou',
    el: 'Δεν έχουν σταλεί δώρα συνδρομών ακόμα',
    tr: 'Henüz hediye abonelik gönderilmedi',
  },
  paymentHistory: {
    me: 'Istorija Plaćanja',
    en: 'Payment History',
    sr: 'Istorija Plaćanja',
    hr: 'Povijest Plaćanja',
    bs: 'Historija Plaćanja',
    sl: 'Zgodovina Plačil',
    mk: 'Историја на Плаќања',
    sq: 'Historia e Pagesave',
    bg: 'История на Плащания',
    ro: 'Istoric Plăți',
    el: 'Ιστορικό Πληρωμών',
    tr: 'Ödeme Geçmişi',
  },
  paymentHistoryAppears: {
    me: 'Istorija plaćanja će se pojaviti ovdje',
    en: 'Payment history will appear here',
    sr: 'Istorija plaćanja će se pojaviti ovde',
    hr: 'Povijest plaćanja će se pojaviti ovdje',
    bs: 'Historija plaćanja će se pojaviti ovdje',
    sl: 'Zgodovina plačil se bo prikazala tukaj',
    mk: 'Историјата на плаќања ќе се појави овде',
    sq: 'Historia e pagesave do të shfaqet këtu',
    bg: 'Историята на плащанията ще се появи тук',
    ro: 'Istoricul plăților va apărea aici',
    el: 'Το ιστορικό πληρωμών θα εμφανιστεί εδώ',
    tr: 'Ödeme geçmişi burada görünecek',
  },
  settings: {
    me: 'Postavke',
    en: 'Settings',
    sr: 'Postavke',
    hr: 'Postavke',
    bs: 'Postavke',
    sl: 'Nastavitve',
    mk: 'Поставки',
    sq: 'Cilësimet',
    bg: 'Настройки',
    ro: 'Setări',
    el: 'Ρυθμίσεις',
    tr: 'Ayarlar',
  },
  language: {
    me: 'Jezik',
    en: 'Language',
    sr: 'Jezik',
    hr: 'Jezik',
    bs: 'Jezik',
    sl: 'Jezik',
    mk: 'Јазик',
    sq: 'Gjuha',
    bg: 'Език',
    ro: 'Limbă',
    el: 'Γλώσσα',
    tr: 'Dil',
  },
  signOut: {
    me: 'Odjava',
    en: 'Sign Out',
    sr: 'Odjava',
    hr: 'Odjava',
    bs: 'Odjava',
    sl: 'Odjava',
    mk: 'Одјава',
    sq: 'Dil',
    bg: 'Изход',
    ro: 'Deconectare',
    el: 'Αποσύνδεση',
    tr: 'Çıkış Yap',
  },
  tools: {
    me: 'Alati',
    en: 'Tools',
    sr: 'Alati',
    hr: 'Alati',
    bs: 'Alati',
    sl: 'Orodja',
    mk: 'Алатки',
    sq: 'Mjetet',
    bg: 'Инструменти',
    ro: 'Instrumente',
    el: 'Εργαλεία',
    tr: 'Araçlar',
  },
  pendulum: {
    me: 'Pendulum',
    en: 'Pendulum',
    sr: 'Pendulum',
    hr: 'Pendulum',
    bs: 'Pendulum',
    sl: 'Nihalo',
    mk: 'Нишало',
    sq: 'Lavjerrësi',
    bg: 'Махало',
    ro: 'Pendul',
    el: 'Εκκρεμές',
    tr: 'Sarkaç',
  },
  pendulumDesc: {
    me: 'Interaktivna ploča za pendulum na ekranu telefona.',
    en: 'An interactive pendulum board on your phone screen.',
    sr: 'Interaktivna tabla za pendulum na ekranu telefona.',
    hr: 'Interaktivna ploča za pendulum na zaslonu mobitela.',
    bs: 'Interaktivna ploča za pendulum na ekranu telefona.',
    sl: 'Interaktivna plošča za nihalo na zaslonu telefona.',
    mk: 'Интерактивна табла за нишало на екранот на телефонот.',
    sq: 'Një tabelë interaktive për lavjerrësin në ekranin e telefonit.',
    bg: 'Интерактивна дъска за махало на екрана на телефона.',
    ro: 'O tablă interactivă pentru pendul pe ecranul telefonului.',
    el: 'Ένας διαδραστικός πίνακας για το εκκρεμές στην οθόνη του τηλεφώνου.',
    tr: 'Telefon ekranında etkileşimli bir sarkaç tahtası.',
  },
  availableWithSubscription: {
    me: 'Dostupno uz pretplatu',
    en: 'Available with a subscription',
    sr: 'Dostupno uz pretplatu',
    hr: 'Dostupno uz pretplatu',
    bs: 'Dostupno uz pretplatu',
    sl: 'Na voljo z naročnino',
    mk: 'Достапно со претплата',
    sq: 'E disponueshme me abonim',
    bg: 'Достъпно с абонамент',
    ro: 'Disponibil cu abonament',
    el: 'Διαθέσιμο με συνδρομή',
    tr: 'Abonelikle kullanılabilir',
  },
  // Account deletion with an owned e-book (D17, critic CR-8): the warning before deletion.
  deleteOwnsBook: {
    me: 'Brisanjem naloga gubiš pristup e-knjizi u aplikaciji. Kupovina se ne može prenijeti na novi nalog. Prije brisanja preuzmi PDF i EPUB datoteke.',
    en: 'Deleting your account ends your access to the e-book in the app. The purchase cannot be moved to a new account. Download the PDF and EPUB files before you delete your account.',
    sr: 'Brisanjem naloga gubiš pristup e-knjizi u aplikaciji. Kupovina se ne može preneti na novi nalog. Pre brisanja preuzmi PDF i EPUB datoteke.',
    hr: 'Brisanjem računa gubiš pristup e-knjizi u aplikaciji. Kupnja se ne može prenijeti na novi račun. Prije brisanja preuzmi PDF i EPUB datoteke.',
    bs: 'Brisanjem naloga gubiš pristup e-knjizi u aplikaciji. Kupovina se ne može prenijeti na novi nalog. Prije brisanja preuzmi PDF i EPUB datoteke.',
    sl: 'Z izbrisom računa izgubiš dostop do e-knjige v aplikaciji. Nakupa ni mogoče prenesti na nov račun. Pred izbrisom prenesi datoteki PDF in EPUB.',
    mk: 'Со бришење на сметката го губиш пристапот до е-книгата во апликацијата. Купувањето не може да се префрли на нова сметка. Пред бришењето преземи ги датотеките PDF и EPUB.',
    sq: 'Me fshirjen e llogarisë humb qasjen në librin elektronik në aplikacion. Blerja nuk mund të kalohet në një llogari të re. Para fshirjes shkarko skedarët PDF dhe EPUB.',
    bg: 'С изтриването на акаунта губиш достъпа до електронната книга в приложението. Покупката не може да се прехвърли към нов акаунт. Преди изтриването изтегли книгата в PDF и EPUB.',
    ro: 'Prin ștergerea contului pierzi accesul la cartea electronică din aplicație. Cumpărarea nu poate fi mutată într-un cont nou. Înainte de ștergere, descarcă fișierele PDF și EPUB.',
    el: 'Με τη διαγραφή του λογαριασμού χάνεις την πρόσβαση στο ηλεκτρονικό βιβλίο στην εφαρμογή. Η αγορά δεν μπορεί να μεταφερθεί σε νέο λογαριασμό. Πριν από τη διαγραφή, κατέβασε τα αρχεία PDF και EPUB.',
    tr: 'Hesabını silersen uygulamadaki e-kitaba erişimini kaybedersin. Satın alma yeni bir hesaba aktarılamaz. Silmeden önce PDF ve EPUB dosyalarını indir.',
  },
};

/**
 * Get translated profile string for the given language
 */
export function getProfileTranslation(key: ProfileTranslationKey, language: Language): string {
  return PROFILE_TRANSLATIONS[key][language];
}

/**
 * Pricing page translation keys
 */
export type PricingTranslationKey =
  | 'loadError'
  | 'chooseYourJourney'
  | 'unlockPotential'
  | 'activeSubscription'
  | 'manageSubscription'
  | 'trialActiveUntil'
  | 'trialExpiredOn'
  | 'whyChooseLukaaji'
  | 'guidedSessions'
  | 'guidedSessionsDesc'
  | 'securePrivate'
  | 'securePrivateDesc'
  | 'regularUpdates'
  | 'regularUpdatesDesc'
  | 'haveQuestions'
  | 'checkFaq';

/**
 * Pricing translations for all 12 supported languages
 */
const PRICING_TRANSLATIONS: Record<PricingTranslationKey, Record<Language, string>> = {
  loadError: {
    me: 'Učitavanje cijena nije uspjelo. Pokušaj ponovo kasnije.',
    en: 'Failed to load pricing plans. Please try again later.',
    sr: 'Učitavanje cena nije uspelo. Pokušaj ponovo kasnije.',
    hr: 'Učitavanje cijena nije uspjelo. Pokušaj ponovno kasnije.',
    bs: 'Učitavanje cijena nije uspjelo. Pokušaj ponovo kasnije.',
    sl: 'Nalaganje cen ni uspelo. Poskusi znova kasneje.',
    mk: 'Вчитувањето на цените не успеа. Обиди се повторно подоцна.',
    sq: 'Ngarkimi i çmimeve dështoi. Provo përsëri më vonë.',
    bg: 'Неуспешно зареждане на цените. Опитай отново по-късно.',
    ro: 'Încărcarea prețurilor a eșuat. Încearcă din nou mai târziu.',
    el: 'Αποτυχία φόρτωσης τιμών. Δοκίμασε ξανά αργότερα.',
    tr: 'Fiyatlar yüklenemedi. Daha sonra tekrar dene.',
  },
  chooseYourJourney: {
    me: 'Izaberi svoj paket',
    en: 'Choose your plan',
    sr: 'Izaberi svoj paket',
    hr: 'Odaberi svoj paket',
    bs: 'Izaberi svoj paket',
    sl: 'Izberi svoj paket',
    mk: 'Избери го својот пакет',
    sq: 'Zgjidh paketën tënde',
    bg: 'Избери своя пакет',
    ro: 'Alege-ți pachetul',
    el: 'Επίλεξε το πακέτο σου',
    tr: 'Paketini seç',
  },
  unlockPotential: {
    me: 'Jednostavna pretplata za pristup vođenim sesijama. Započni besplatnim probnim periodom ili izaberi paket koji ti odgovara.',
    en: 'A simple subscription for access to guided sessions. Start with a free trial or choose the plan that suits you.',
    sr: 'Jednostavna pretplata za pristup vođenim sesijama. Započni besplatnim probnim periodom ili izaberi paket koji ti odgovara.',
    hr: 'Jednostavna pretplata za pristup vođenim sesijama. Započni besplatnim probnim razdobljem ili odaberi paket koji ti odgovara.',
    bs: 'Jednostavna pretplata za pristup vođenim sesijama. Započni besplatnim probnim periodom ili izaberi paket koji ti odgovara.',
    sl: 'Preprosta naročnina za dostop do vodenih sej. Začni z brezplačnim preizkusom ali izberi paket, ki ti ustreza.',
    mk: 'Едноставна претплата за пристап до водените сесии. Започни со бесплатен пробен период или избери пакет што ти одговара.',
    sq: 'Një abonim i thjeshtë për akses në seancat e udhëhequra. Fillo me një provë falas ose zgjidh paketën që të përshtatet.',
    bg: 'Лесен абонамент за достъп до водените сесии. Започни с безплатен пробен период или избери пакета, който ти подхожда.',
    ro: 'Un abonament simplu pentru acces la sesiunile ghidate. Începe cu o probă gratuită sau alege pachetul care ți se potrivește.',
    el: 'Μια απλή συνδρομή για πρόσβαση στις καθοδηγούμενες συνεδρίες. Ξεκίνα με δωρεάν δοκιμή ή επίλεξε το πακέτο που σου ταιριάζει.',
    tr: 'Rehberli seanslara erişim için basit bir abonelik. Ücretsiz denemeyle başla ya da sana uygun paketi seç.',
  },
  activeSubscription: {
    me: 'Imaš aktivnu pretplatu.',
    en: 'You have an active subscription.',
    sr: 'Imaš aktivnu pretplatu.',
    hr: 'Imaš aktivnu pretplatu.',
    bs: 'Imaš aktivnu pretplatu.',
    sl: 'Imaš aktivno naročnino.',
    mk: 'Имаш активна претплата.',
    sq: 'Ke një abonim aktiv.',
    bg: 'Имаш активен абонамент.',
    ro: 'Ai un abonament activ.',
    el: 'Έχεις ενεργή συνδρομή.',
    tr: 'Aktif bir aboneliğin var.',
  },
  manageSubscription: {
    me: 'Upravljaj Pretplatom',
    en: 'Manage Subscription',
    sr: 'Upravljaj Pretplatom',
    hr: 'Upravljaj Pretplatom',
    bs: 'Upravljaj Pretplatom',
    sl: 'Upravljaj Naročnino',
    mk: 'Управувај со Претплата',
    sq: 'Menaxho Abonimin',
    bg: 'Управление на Абонамент',
    ro: 'Gestionează Abonamentul',
    el: 'Διαχείριση Συνδρομής',
    tr: 'Aboneliği Yönet',
  },
  trialActiveUntil: {
    me: 'Tvoj probni period traje do {date}.',
    en: 'Your free trial runs until {date}.',
    sr: 'Tvoj probni period traje do {date}.',
    hr: 'Tvoje probno razdoblje traje do {date}.',
    bs: 'Tvoj probni period traje do {date}.',
    sl: 'Tvoj preizkus traja do {date}.',
    mk: 'Твојот пробен период трае до {date}.',
    sq: 'Periudha jote provuese zgjat deri më {date}.',
    bg: 'Пробният ти период е до {date}.',
    ro: 'Perioada ta de probă durează până pe {date}.',
    el: 'Η δοκιμή σου διαρκεί έως {date}.',
    tr: 'Ücretsiz denemen {date} tarihine kadar sürüyor.',
  },
  trialExpiredOn: {
    me: 'Tvoj probni period je istekao {date}.',
    en: 'Your free trial ended on {date}.',
    sr: 'Tvoj probni period je istekao {date}.',
    hr: 'Tvoje probno razdoblje isteklo je {date}.',
    bs: 'Tvoj probni period je istekao {date}.',
    sl: 'Tvoj preizkus se je iztekel {date}.',
    mk: 'Твојот пробен период истече на {date}.',
    sq: 'Periudha jote provuese mbaroi më {date}.',
    bg: 'Пробният ти период изтече на {date}.',
    ro: 'Perioada ta de probă s-a încheiat pe {date}.',
    el: 'Η δοκιμή σου έληξε στις {date}.',
    tr: 'Ücretsiz denemen {date} tarihinde sona erdi.',
  },
  whyChooseLukaaji: {
    me: 'Zašto izabrati Lukaaji?',
    en: 'Why Choose Lukaaji?',
    sr: 'Zašto izabrati Lukaaji?',
    hr: 'Zašto odabrati Lukaaji?',
    bs: 'Zašto izabrati Lukaaji?',
    sl: 'Zakaj izbrati Lukaaji?',
    mk: 'Зошто да го избереш Lukaaji?',
    sq: 'Pse të zgjedhësh Lukaaji?',
    bg: 'Защо да избереш Lukaaji?',
    ro: 'De ce să alegi Lukaaji?',
    el: 'Γιατί να επιλέξεις το Lukaaji;',
    tr: 'Neden Lukaaji\'yi seçmelisin?',
  },
  guidedSessions: {
    me: 'Vođene sesije',
    en: 'Guided Sessions',
    sr: 'Vođene sesije',
    hr: 'Vođene sesije',
    bs: 'Vođene sesije',
    sl: 'Vodene seje',
    mk: 'Водени сесии',
    sq: 'Seanca të udhëhequra',
    bg: 'Водени сесии',
    ro: 'Sesiuni ghidate',
    el: 'Καθοδηγούμενες συνεδρίες',
    tr: 'Rehberli seanslar',
  },
  guidedSessionsDesc: {
    me: 'Prati glas kroz izabranu temu',
    en: 'Follow the voice through the topic you choose',
    sr: 'Prati glas kroz izabranu temu',
    hr: 'Prati glas kroz odabranu temu',
    bs: 'Prati glas kroz izabranu temu',
    sl: 'Sledi glasu skozi izbrano temo',
    mk: 'Следи го гласот низ избраната тема',
    sq: 'Ndiq zërin përmes temës që zgjedh',
    bg: 'Следвай гласа през избраната тема',
    ro: 'Urmează vocea prin tema aleasă',
    el: 'Ακολούθησε τη φωνή στο θέμα που επιλέγεις',
    tr: 'Seçtiğin konuda sesi takip et',
  },
  securePrivate: {
    me: 'Sigurno i Privatno',
    en: 'Secure & Private',
    sr: 'Sigurno i Privatno',
    hr: 'Sigurno i Privatno',
    bs: 'Sigurno i Privatno',
    sl: 'Varno in Zasebno',
    mk: 'Безбедно и Приватно',
    sq: 'I Sigurt dhe Privat',
    bg: 'Сигурно и Лично',
    ro: 'Sigur și Privat',
    el: 'Ασφαλές και Ιδιωτικό',
    tr: 'Güvenli ve Gizli',
  },
  securePrivateDesc: {
    me: 'Tvoj put je ličan i zaštićen',
    en: 'Your journey is personal and protected',
    sr: 'Tvoj put je ličan i zaštićen',
    hr: 'Tvoj put je osoban i zaštićen',
    bs: 'Tvoj put je ličan i zaštićen',
    sl: 'Tvoja pot je osebna in zaščitena',
    mk: 'Твоето патување е лично и заштитено',
    sq: 'Udhëtimi yt është personal dhe i mbrojtur',
    bg: 'Твоето пътуване е лично и защитено',
    ro: 'Călătoria ta este personală și protejată',
    el: 'Το ταξίδι σου είναι προσωπικό και προστατευμένο',
    tr: 'Yolculuğun kişisel ve korunaklı',
  },
  regularUpdates: {
    me: 'Redovna Ažuriranja',
    en: 'Regular Updates',
    sr: 'Redovna Ažuriranja',
    hr: 'Redovna Ažuriranja',
    bs: 'Redovna Ažuriranja',
    sl: 'Redne Posodobitve',
    mk: 'Редовни Ажурирања',
    sq: 'Përditësime të Rregullta',
    bg: 'Редовни Актуализации',
    ro: 'Actualizări Regulate',
    el: 'Τακτικές Ενημερώσεις',
    tr: 'Düzenli Güncellemeler',
  },
  regularUpdatesDesc: {
    me: 'Nove sesije i funkcije redovno se dodaju',
    en: 'New sessions and features added regularly',
    sr: 'Nove sesije i funkcije redovno se dodaju',
    hr: 'Nove sesije i funkcije redovno se dodaju',
    bs: 'Nove sesije i funkcije redovno se dodaju',
    sl: 'Nove seje in funkcije se redno dodajajo',
    mk: 'Нови сесии и функции редовно се додаваат',
    sq: 'Seanca dhe veçori të reja shtohen rregullisht',
    bg: 'Нови сесии и функции се добавят редовно',
    ro: 'Sesiuni și funcții noi adăugate regulat',
    el: 'Νέες συνεδρίες και λειτουργίες προστίθενται τακτικά',
    tr: 'Yeni seanslar ve özellikler düzenli olarak eklenir',
  },
  haveQuestions: {
    me: 'Imaš pitanja?',
    en: 'Have questions?',
    sr: 'Imaš pitanja?',
    hr: 'Imaš pitanja?',
    bs: 'Imaš pitanja?',
    sl: 'Imaš vprašanja?',
    mk: 'Имаш прашања?',
    sq: 'Ke pyetje?',
    bg: 'Имаш въпроси?',
    ro: 'Ai întrebări?',
    el: 'Έχεις ερωτήσεις;',
    tr: 'Soruların mı var?',
  },
  checkFaq: {
    me: 'Pogledaj česta pitanja',
    en: 'Check our FAQ',
    sr: 'Pogledaj česta pitanja',
    hr: 'Pogledaj česta pitanja',
    bs: 'Pogledaj česta pitanja',
    sl: 'Preveri pogosta vprašanja',
    mk: 'Погледни ги честите прашања',
    sq: 'Shiko pyetjet e shpeshta',
    bg: 'Виж честите въпроси',
    ro: 'Vezi întrebările frecvente',
    el: 'Δες τις Συχνές Ερωτήσεις',
    tr: 'SSS\'ye göz at',
  },
};

/**
 * Get translated pricing string for the given language
 */
export function getPricingTranslation(key: PricingTranslationKey, language: Language): string {
  return PRICING_TRANSLATIONS[key][language];
}

/**
 * Chakra page translation keys
 */
export type ChakraTranslationKey =
  | 'loginRequired'
  | 'loginDesc'
  | 'login'
  | 'premiumFeature'
  | 'premiumDesc'
  | 'premiumUse'
  | 'upgradeToPremium'
  | 'chakraDashboard'
  | 'monitorEnergyCenters'
  | 'energyVisualization'
  | 'chakraBalance'
  | 'energyTrends'
  | 'trend'
  | 'improving'
  | 'avgEnergy'
  | 'mentalClarity'
  | 'emotionalBalance'
  | 'chakraCrown'
  | 'chakraThirdEye'
  | 'chakraThroat'
  | 'chakraHeart'
  | 'chakraSolarPlexus'
  | 'chakraSacral'
  | 'chakraRoot';

/**
 * Chakra translations for all 12 supported languages
 */
const CHAKRA_TRANSLATIONS: Record<ChakraTranslationKey, Record<Language, string>> = {
  loginRequired: {
    me: 'Potrebna prijava',
    en: 'Login Required',
    sr: 'Potrebna prijava',
    hr: 'Potrebna prijava',
    bs: 'Potrebna prijava',
    sl: 'Potrebna prijava',
    mk: 'Потребна е најава',
    sq: 'Kërkohet identifikimi',
    bg: 'Изисква се вход',
    ro: 'Conectare necesară',
    el: 'Απαιτείται σύνδεση',
    tr: 'Giriş gerekli',
  },
  loginDesc: {
    me: 'Prijavi se da pristupiš Čakra Dashboardu',
    en: 'Please login to access the Chakra Dashboard',
    sr: 'Prijavi se da pristupiš Čakra Dashboardu',
    hr: 'Prijavi se za pristup Čakra Dashboardu',
    bs: 'Prijavi se da pristupiš Čakra Dashboardu',
    sl: 'Prijavi se za dostop do Čakra nadzorne plošče',
    mk: 'Најави се за да пристапиш до Чакра Dashboard',
    sq: 'Identifikohu për të hyrë në Panelin e Chakrave',
    bg: 'Влез за достъп до Чакра таблото',
    ro: 'Conectează-te pentru a accesa Tabloul Chakra',
    el: 'Συνδέσου για πρόσβαση στον Πίνακα Τσάκρα',
    tr: 'Çakra Panosuna erişmek için giriş yap',
  },
  login: {
    me: 'Prijava',
    en: 'Login',
    sr: 'Prijava',
    hr: 'Prijava',
    bs: 'Prijava',
    sl: 'Prijava',
    mk: 'Најава',
    sq: 'Identifikohu',
    bg: 'Вход',
    ro: 'Conectare',
    el: 'Σύνδεση',
    tr: 'Giriş',
  },
  premiumFeature: {
    me: 'Premium funkcija',
    en: 'Premium Feature',
    sr: 'Premium funkcija',
    hr: 'Premium funkcija',
    bs: 'Premium funkcija',
    sl: 'Premium funkcija',
    mk: 'Premium функција',
    sq: 'Veçori Premium',
    bg: 'Premium функция',
    ro: 'Funcție Premium',
    el: 'Premium Λειτουργία',
    tr: 'Premium Özellik',
  },
  premiumDesc: {
    me: 'Čakra Dashboard je dostupan uz pretplatu',
    en: 'Chakra Dashboard is available with a subscription',
    sr: 'Čakra Dashboard je dostupan uz pretplatu',
    hr: 'Čakra Dashboard je dostupan uz pretplatu',
    bs: 'Čakra Dashboard je dostupan uz pretplatu',
    sl: 'Čakra Nadzorna plošča je na voljo z naročnino',
    mk: 'Чакра Dashboard е достапен со претплата',
    sq: 'Paneli i Chakrave është i disponueshëm me abonim',
    bg: 'Чакра Табло е достъпно с абонамент',
    ro: 'Tabloul Chakra este disponibil cu un abonament',
    el: 'Ο Πίνακας Τσάκρα είναι διαθέσιμος με συνδρομή',
    tr: 'Çakra Panosu abonelikle kullanılabilir',
  },
  premiumUse: {
    me: 'Prati svoje energetske centre, radi dnevne provjere i prati svoj duhovni napredak.',
    en: 'Track your energy centers, perform daily check-ins, and monitor your spiritual progress.',
    sr: 'Prati svoje energetske centre, radi dnevne provere i prati svoj duhovni napredak.',
    hr: 'Prati svoje energetske centre, radi dnevne provjere i prati svoj duhovni napredak.',
    bs: 'Prati svoje energetske centre, radi dnevne provjere i prati svoj duhovni napredak.',
    sl: 'Spremljaj svoje energijske centre, opravljaj dnevne preglede in spremljaj svoj duhovni napredek.',
    mk: 'Следи ги своите енергетски центри, прави дневни проверки и следи го својот духовен напредок.',
    sq: 'Ndiq qendrat e tua energjetike, bëj kontrolle ditore dhe ndiq progresin tënd shpirtëror.',
    bg: 'Следи енергийните си центрове, прави ежедневни проверки и следи духовния си напредък.',
    ro: 'Urmărește-ți centrele energetice, fă verificări zilnice și urmărește-ți progresul spiritual.',
    el: 'Παρακολούθησε τα ενεργειακά σου κέντρα, κάνε καθημερινούς ελέγχους και παρακολούθησε την πνευματική σου πρόοδο.',
    tr: 'Enerji merkezlerini takip et, günlük kontroller yap ve ruhsal ilerlemeni izle.',
  },
  upgradeToPremium: {
    me: 'Nadogradi na Premium',
    en: 'Upgrade to Premium',
    sr: 'Nadogradi na Premium',
    hr: 'Nadogradi na Premium',
    bs: 'Nadogradi na Premium',
    sl: 'Nadgradi na Premium',
    mk: 'Надгради на Premium',
    sq: 'Përmirëso në Premium',
    bg: 'Надгради до Premium',
    ro: 'Fă upgrade la Premium',
    el: 'Αναβάθμιση σε Premium',
    tr: 'Premium\'a Yükselt',
  },
  chakraDashboard: {
    me: 'Čakra Dashboard',
    en: 'Chakra Dashboard',
    sr: 'Čakra Dashboard',
    hr: 'Čakra Dashboard',
    bs: 'Čakra Dashboard',
    sl: 'Čakra Nadzorna Plošča',
    mk: 'Чакра Dashboard',
    sq: 'Paneli i Chakrave',
    bg: 'Чакра Табло',
    ro: 'Tablou Chakra',
    el: 'Πίνακας Τσάκρα',
    tr: 'Çakra Panosu',
  },
  monitorEnergyCenters: {
    me: 'Prati svoje energetske centre i balans',
    en: 'Monitor your energy centers and track your balance',
    sr: 'Prati svoje energetske centre i balans',
    hr: 'Prati svoje energetske centre i balans',
    bs: 'Prati svoje energetske centre i balans',
    sl: 'Spremljaj svoje energijske centre in ravnovesje',
    mk: 'Следи ги своите енергетски центри и балансот',
    sq: 'Ndiq qendrat e tua energjetike dhe ekuilibrin',
    bg: 'Следи енергийните си центрове и баланса',
    ro: 'Urmărește-ți centrele energetice și echilibrul',
    el: 'Παρακολούθησε τα ενεργειακά σου κέντρα και την ισορροπία σου',
    tr: 'Enerji merkezlerini ve dengeni izle',
  },
  energyVisualization: {
    me: 'Energetska vizualizacija',
    en: 'Energy Visualization',
    sr: 'Energetska vizualizacija',
    hr: 'Energetska vizualizacija',
    bs: 'Energetska vizualizacija',
    sl: 'Energijska Vizualizacija',
    mk: 'Енергетска Визуализација',
    sq: 'Vizualizimi i Energjisë',
    bg: 'Енергийна Визуализация',
    ro: 'Vizualizare Energetică',
    el: 'Ενεργειακή Οπτικοποίηση',
    tr: 'Enerji Görselleştirme',
  },
  chakraBalance: {
    me: 'Balans čakri',
    en: 'Chakra Balance',
    sr: 'Balans čakri',
    hr: 'Balans čakri',
    bs: 'Balans čakri',
    sl: 'Ravnotežje Čaker',
    mk: 'Баланс на Чакри',
    sq: 'Balanca e Chakrave',
    bg: 'Баланс на Чакри',
    ro: 'Echilibru Chakra',
    el: 'Ισορροπία Τσάκρα',
    tr: 'Çakra Dengesi',
  },
  energyTrends: {
    me: 'Energetski trendovi',
    en: 'Energy Trends',
    sr: 'Energetski trendovi',
    hr: 'Energetski trendovi',
    bs: 'Energetski trendovi',
    sl: 'Energijski Trendi',
    mk: 'Енергетски Трендови',
    sq: 'Trendet e Energjisë',
    bg: 'Енергийни Тенденции',
    ro: 'Tendințe Energetice',
    el: 'Ενεργειακές Τάσεις',
    tr: 'Enerji Trendleri',
  },
  trend: {
    me: 'Trend',
    en: 'Trend',
    sr: 'Trend',
    hr: 'Trend',
    bs: 'Trend',
    sl: 'Trend',
    mk: 'Тренд',
    sq: 'Trend',
    bg: 'Тенденция',
    ro: 'Tendință',
    el: 'Τάση',
    tr: 'Trend',
  },
  improving: {
    me: 'Poboljšanje',
    en: 'Improving',
    sr: 'Poboljšanje',
    hr: 'Poboljšanje',
    bs: 'Poboljšanje',
    sl: 'Izboljšanje',
    mk: 'Подобрување',
    sq: 'Përmirësim',
    bg: 'Подобряване',
    ro: 'În îmbunătățire',
    el: 'Βελτίωση',
    tr: 'İyileşme',
  },
  avgEnergy: {
    me: 'Prosj. energija',
    en: 'Avg Energy',
    sr: 'Prosj. energija',
    hr: 'Prosj. energija',
    bs: 'Prosj. energija',
    sl: 'Povp. energija',
    mk: 'Просечна енергија',
    sq: 'Energji mesatare',
    bg: 'Средна енергия',
    ro: 'Energie medie',
    el: 'Μέση Ενέργεια',
    tr: 'Ort. Enerji',
  },
  mentalClarity: {
    me: 'Mentalna jasnoća',
    en: 'Mental Clarity',
    sr: 'Mentalna jasnoća',
    hr: 'Mentalna jasnoća',
    bs: 'Mentalna jasnoća',
    sl: 'Mentalna Jasnost',
    mk: 'Ментална Јасност',
    sq: 'Qartësi Mendore',
    bg: 'Ментална Яснота',
    ro: 'Claritate Mentală',
    el: 'Πνευματική Διαύγεια',
    tr: 'Zihinsel Netlik',
  },
  emotionalBalance: {
    me: 'Emoc. balans',
    en: 'Emotional Balance',
    sr: 'Emoc. balans',
    hr: 'Emoc. balans',
    bs: 'Emoc. balans',
    sl: 'Čustveno Ravnovesje',
    mk: 'Емоционален Баланс',
    sq: 'Balanca Emocionale',
    bg: 'Емоционален Баланс',
    ro: 'Echilibru Emoțional',
    el: 'Συναισθηματική Ισορροπία',
    tr: 'Duygusal Denge',
  },
  chakraCrown: {
    me: 'Kruna',
    en: 'Crown',
    sr: 'Kruna',
    hr: 'Kruna',
    bs: 'Kruna',
    sl: 'Kronska',
    mk: 'Круна',
    sq: 'Kurora',
    bg: 'Коронна',
    ro: 'Coroană',
    el: 'Κορώνα',
    tr: 'Taç',
  },
  chakraThirdEye: {
    me: 'Treće oko',
    en: 'Third Eye',
    sr: 'Treće oko',
    hr: 'Treće oko',
    bs: 'Treće oko',
    sl: 'Tretje oko',
    mk: 'Трето око',
    sq: 'Syri i tretë',
    bg: 'Трето око',
    ro: 'Al treilea ochi',
    el: 'Τρίτο μάτι',
    tr: 'Üçüncü göz',
  },
  chakraThroat: {
    me: 'Grlo',
    en: 'Throat',
    sr: 'Grlo',
    hr: 'Grlo',
    bs: 'Grlo',
    sl: 'Grlna',
    mk: 'Грло',
    sq: 'Fyti',
    bg: 'Гърлена',
    ro: 'Gât',
    el: 'Λαιμός',
    tr: 'Boğaz',
  },
  chakraHeart: {
    me: 'Srce',
    en: 'Heart',
    sr: 'Srce',
    hr: 'Srce',
    bs: 'Srce',
    sl: 'Srčna',
    mk: 'Срце',
    sq: 'Zemra',
    bg: 'Сърдечна',
    ro: 'Inimă',
    el: 'Καρδιά',
    tr: 'Kalp',
  },
  chakraSolarPlexus: {
    me: 'Solarni pleksus',
    en: 'Solar Plexus',
    sr: 'Solarni pleksus',
    hr: 'Solarni pleksus',
    bs: 'Solarni pleksus',
    sl: 'Sončni pleksus',
    mk: 'Соларен плексус',
    sq: 'Pleksusi diellor',
    bg: 'Слънчев сплит',
    ro: 'Plexul solar',
    el: 'Ηλιακό πλέγμα',
    tr: 'Güneş sinir ağı',
  },
  chakraSacral: {
    me: 'Sakralna',
    en: 'Sacral',
    sr: 'Sakralna',
    hr: 'Sakralna',
    bs: 'Sakralna',
    sl: 'Sakralna',
    mk: 'Сакрална',
    sq: 'Sakrale',
    bg: 'Сакрална',
    ro: 'Sacrală',
    el: 'Ιερή',
    tr: 'Sakral',
  },
  chakraRoot: {
    me: 'Korijenska',
    en: 'Root',
    sr: 'Korenska',
    hr: 'Korijenska',
    bs: 'Korijenska',
    sl: 'Korenska',
    mk: 'Коренска',
    sq: 'Rrënjë',
    bg: 'Коренна',
    ro: 'Rădăcină',
    el: 'Ρίζα',
    tr: 'Kök',
  },
};

/**
 * Get translated chakra string for the given language
 */
export function getChakraTranslation(key: ChakraTranslationKey, language: Language): string {
  return CHAKRA_TRANSLATIONS[key][language];
}

/**
 * Journal page translation keys
 */
export type JournalTranslationKey =
  | 'loginRequired'
  | 'loginDesc'
  | 'login'
  | 'premiumFeature'
  | 'premiumDesc'
  | 'premiumUse'
  | 'upgrade'
  | 'title'
  | 'subtitle'
  | 'newEntry'
  | 'todaysPrompt'
  | 'promptText'
  | 'recentEntries'
  | 'noEntries'
  | 'exportJournal'
  | 'insights'
  | 'insightsText'
  | 'before'
  | 'after'
  | 'deleteEntry'
  | 'deleteConfirm'
  | 'loadError'
  | 'saveError';

/**
 * Journal translations for all 12 supported languages
 */
const JOURNAL_TRANSLATIONS: Record<JournalTranslationKey, Record<Language, string>> = {
  loginRequired: {
    me: 'Potrebna prijava',
    en: 'Login Required',
    sr: 'Potrebna prijava',
    hr: 'Potrebna prijava',
    bs: 'Potrebna prijava',
    sl: 'Potrebna prijava',
    mk: 'Потребна е најава',
    sq: 'Kërkohet identifikimi',
    bg: 'Изисква се вход',
    ro: 'Conectare necesară',
    el: 'Απαιτείται σύνδεση',
    tr: 'Giriş gerekli',
  },
  loginDesc: {
    me: 'Prijavi se da pristupiš Dnevniku',
    en: 'Please login to access your Journal',
    sr: 'Prijavi se da pristupiš Dnevniku',
    hr: 'Prijavi se za pristup Dnevniku',
    bs: 'Prijavi se da pristupiš Dnevniku',
    sl: 'Prijavi se za dostop do Dnevnika',
    mk: 'Најави се за да пристапиш до Дневникот',
    sq: 'Identifikohu për të hyrë në Ditarin tënd',
    bg: 'Влез за достъп до своя Дневник',
    ro: 'Conectează-te pentru a accesa Jurnalul',
    el: 'Συνδέσου για πρόσβαση στο Ημερολόγιο',
    tr: 'Günlüğüne erişmek için giriş yap',
  },
  login: {
    me: 'Prijava',
    en: 'Login',
    sr: 'Prijava',
    hr: 'Prijava',
    bs: 'Prijava',
    sl: 'Prijava',
    mk: 'Најава',
    sq: 'Identifikohu',
    bg: 'Вход',
    ro: 'Conectare',
    el: 'Σύνδεση',
    tr: 'Giriş',
  },
  premiumFeature: {
    me: 'Premium funkcija',
    en: 'Premium Feature',
    sr: 'Premium funkcija',
    hr: 'Premium funkcija',
    bs: 'Premium funkcija',
    sl: 'Premium funkcija',
    mk: 'Premium функција',
    sq: 'Veçori Premium',
    bg: 'Premium функция',
    ro: 'Funcție Premium',
    el: 'Premium Λειτουργία',
    tr: 'Premium Özellik',
  },
  premiumDesc: {
    me: 'Dnevnik je dostupan premium pretplatnicima',
    en: 'The Journal is available to premium subscribers',
    sr: 'Dnevnik je dostupan premium pretplatnicima',
    hr: 'Dnevnik je dostupan premium pretplatnicima',
    bs: 'Dnevnik je dostupan premium pretplatnicima',
    sl: 'Dnevnik je na voljo premium naročnikom',
    mk: 'Дневникот е достапен за premium претплатници',
    sq: 'Ditari është i disponueshëm për abonentët premium',
    bg: 'Дневникът е достъпен за премиум абонати',
    ro: 'Jurnalul este disponibil pentru abonații premium',
    el: 'Το Ημερολόγιο είναι διαθέσιμο για premium συνδρομητές',
    tr: 'Günlük premium aboneler için mevcuttur',
  },
  premiumUse: {
    me: 'Prati svoje duhovno putovanje uz dnevne poticaje, refleksije i uvide.',
    en: 'Track your spiritual journey with daily prompts, reflections, and insights.',
    sr: 'Prati svoje duhovno putovanje uz dnevne podsticaje, refleksije i uvide.',
    hr: 'Prati svoje duhovno putovanje uz dnevne poticaje, refleksije i uvide.',
    bs: 'Prati svoje duhovno putovanje uz dnevne poticaje, refleksije i uvide.',
    sl: 'Spremljaj svojo duhovno pot z dnevnimi spodbudami, refleksijami in vpogledi.',
    mk: 'Следи го своето духовно патување со дневни поттици, рефлексии и увиди.',
    sq: 'Ndiq udhëtimin tënd shpirtëror me nxitje ditore, reflektime dhe njohuri.',
    bg: 'Следи духовното си пътуване с ежедневни подсказки, размисли и прозрения.',
    ro: 'Urmărește-ți călătoria spirituală cu îndemnuri zilnice, reflecții și perspective.',
    el: 'Παρακολούθησε το πνευματικό σου ταξίδι με καθημερινές προτροπές, αναστοχασμούς και ιδέες.',
    tr: 'Günlük istemler, yansımalar ve içgörülerle ruhsal yolculuğunu takip et.',
  },
  upgrade: {
    me: 'Nadogradi na Premium',
    en: 'Upgrade to Premium',
    sr: 'Nadogradi na Premium',
    hr: 'Nadogradi na Premium',
    bs: 'Nadogradi na Premium',
    sl: 'Nadgradi na Premium',
    mk: 'Надгради на Premium',
    sq: 'Përmirëso në Premium',
    bg: 'Надгради до Premium',
    ro: 'Fă upgrade la Premium',
    el: 'Αναβάθμιση σε Premium',
    tr: 'Premium\'a Yükselt',
  },
  title: {
    me: 'Dnevnik',
    en: 'Journal',
    sr: 'Dnevnik',
    hr: 'Dnevnik',
    bs: 'Dnevnik',
    sl: 'Dnevnik',
    mk: 'Дневник',
    sq: 'Ditari',
    bg: 'Дневник',
    ro: 'Jurnal',
    el: 'Ημερολόγιο',
    tr: 'Günlük',
  },
  subtitle: {
    me: 'Bilježi svoje duhovno putovanje i prati napredak',
    en: 'Document your spiritual journey and track your progress',
    sr: 'Beleži svoje duhovno putovanje i prati napredak',
    hr: 'Bilježi svoje duhovno putovanje i prati napredak',
    bs: 'Bilježi svoje duhovno putovanje i prati napredak',
    sl: 'Beleži svojo duhovno pot in spremljaj napredek',
    mk: 'Запишувај го своето духовно патување и следи го напредокот',
    sq: 'Shëno udhëtimin tënd shpirtëror dhe ndiq progresin',
    bg: 'Записвай духовното си пътуване и следи напредъка',
    ro: 'Notează-ți călătoria spirituală și urmărește-ți progresul',
    el: 'Κατάγραψε το πνευματικό σου ταξίδι και παρακολούθησε την πρόοδό σου',
    tr: 'Ruhsal yolculuğunu kaydet ve ilerlemeni takip et',
  },
  newEntry: {
    me: 'Novi Unos',
    en: 'New Entry',
    sr: 'Novi Unos',
    hr: 'Novi Unos',
    bs: 'Novi Unos',
    sl: 'Nov Vnos',
    mk: 'Нов Внес',
    sq: 'Hyrje e Re',
    bg: 'Нов Запис',
    ro: 'Intrare Nouă',
    el: 'Νέα Καταχώρηση',
    tr: 'Yeni Giriş',
  },
  todaysPrompt: {
    me: 'Današnji Poticaj',
    en: "Today's Prompt",
    sr: 'Današnji Poticaj',
    hr: 'Današnji Poticaj',
    bs: 'Današnji Poticaj',
    sl: 'Današnja Spodbuda',
    mk: 'Денешен Поттик',
    sq: 'Nxitja e Sotme',
    bg: 'Днешният Подтик',
    ro: 'Îndemnul de Astăzi',
    el: 'Η Σημερινή Προτροπή',
    tr: 'Bugünün İstemi',
  },
  promptText: {
    me: '"Za šta osjećaš zahvalnost danas? Kako se tvoja energija promijenila ove sedmice?"',
    en: '"What are you grateful for today? How has your energy shifted this week?"',
    sr: '"Za šta osećaš zahvalnost danas? Kako se tvoja energija promenila ove nedelje?"',
    hr: '"Za što osjećaš zahvalnost danas? Kako se tvoja energija promijenila ovaj tjedan?"',
    bs: '"Za šta osjećaš zahvalnost danas? Kako se tvoja energija promijenila ove sedmice?"',
    sl: '"Za kaj danes čutiš hvaležnost? Kako se je tvoja energija spremenila ta teden?"',
    mk: '"За што чувствуваш благодарност денес? Како се промени твојата енергија оваа недела?"',
    sq: '"Për çfarë ndjen mirënjohje sot? Si ka ndryshuar energjia jote këtë javë?"',
    bg: '"За какво изпитваш благодарност днес? Как се промени енергията ти тази седмица?"',
    ro: '"Pentru ce simți recunoștință astăzi? Cum s-a schimbat energia ta săptămâna aceasta?"',
    el: '"Για τι νιώθεις ευγνωμοσύνη σήμερα; Πώς άλλαξε η ενέργειά σου αυτή την εβδομάδα;"',
    tr: '"Bugün ne için minnettarsın? Bu hafta enerjin nasıl değişti?"',
  },
  recentEntries: {
    me: 'Nedavni Unosi',
    en: 'Recent Entries',
    sr: 'Nedavni Unosi',
    hr: 'Nedavni Unosi',
    bs: 'Nedavni Unosi',
    sl: 'Nedavni Vnosi',
    mk: 'Неодамнешни Внесови',
    sq: 'Hyrjet e Fundit',
    bg: 'Скорошни Записи',
    ro: 'Intrări Recente',
    el: 'Πρόσφατες Καταχωρήσεις',
    tr: 'Son Girişler',
  },
  noEntries: {
    me: 'Još nema unosa u dnevnik. Počni da bilježiš svoje putovanje!',
    en: 'No journal entries yet. Start documenting your journey!',
    sr: 'Još nema unosa u dnevnik. Počni da beležiš svoje putovanje!',
    hr: 'Još nema unosa u dnevnik. Počni bilježiti svoje putovanje!',
    bs: 'Još nema unosa u dnevnik. Počni bilježiti svoje putovanje!',
    sl: 'Še ni vnosov v dnevnik. Začni beležiti svojo pot!',
    mk: 'Сè уште нема внесови во дневникот. Почни да го запишуваш своето патување!',
    sq: 'Ende nuk ka shënime në ditar. Fillo të shënosh udhëtimin tënd!',
    bg: 'Все още няма записи в дневника. Започни да записваш пътуването си!',
    ro: 'Încă nu există intrări în jurnal. Începe să-ți notezi călătoria!',
    el: 'Δεν υπάρχουν ακόμα καταχωρήσεις στο ημερολόγιο. Άρχισε να καταγράφεις το ταξίδι σου!',
    tr: 'Henüz günlük girişi yok. Yolculuğunu kaydetmeye başla!',
  },
  exportJournal: {
    me: 'Izvezi Dnevnik',
    en: 'Export Journal',
    sr: 'Izvezi Dnevnik',
    hr: 'Izvezi Dnevnik',
    bs: 'Izvezi Dnevnik',
    sl: 'Izvozi Dnevnik',
    mk: 'Извези Дневник',
    sq: 'Eksporto Ditarin',
    bg: 'Експортирай Дневник',
    ro: 'Exportă Jurnalul',
    el: 'Εξαγωγή Ημερολογίου',
    tr: 'Günlüğü Dışa Aktar',
  },
  insights: {
    me: 'Uvidi',
    en: 'Insights',
    sr: 'Uvidi',
    hr: 'Uvidi',
    bs: 'Uvidi',
    sl: 'Vpogledi',
    mk: 'Увиди',
    sq: 'Njohuri',
    bg: 'Прозрения',
    ro: 'Perspective',
    el: 'Ιδέες',
    tr: 'İçgörüler',
  },
  insightsText: {
    me: 'Tvoji uvidi i obrasci pojaviće se ovdje kada dodaš više unosa.',
    en: 'Your insights and patterns will appear here as you add more entries.',
    sr: 'Tvoji uvidi i obrasci pojaviće se ovde kada dodaš više unosa.',
    hr: 'Tvoji uvidi i obrasci pojavit će se ovdje kada dodaš više unosa.',
    bs: 'Tvoji uvidi i obrasci pojavit će se ovdje kada dodaš više unosa.',
    sl: 'Tvoji vpogledi in vzorci se bodo pojavili tukaj, ko dodaš več vnosov.',
    mk: 'Твоите увиди и обрасци ќе се појават овде кога ќе додадеш повеќе внесови.',
    sq: 'Njohuritë dhe modelet e tua do të shfaqen këtu kur të shtosh më shumë shënime.',
    bg: 'Твоите прозрения и модели ще се появят тук, когато добавиш повече записи.',
    ro: 'Perspectivele și tiparele tale vor apărea aici pe măsură ce adaugi mai multe intrări.',
    el: 'Οι ιδέες και τα μοτίβα σου θα εμφανιστούν εδώ καθώς προσθέτεις περισσότερες καταχωρήσεις.',
    tr: 'Daha fazla giriş ekledikçe içgörülerin ve kalıpların burada görünecek.',
  },
  before: {
    me: 'Prije',
    en: 'Before',
    sr: 'Pre',
    hr: 'Prije',
    bs: 'Prije',
    sl: 'Pred',
    mk: 'Пред',
    sq: 'Para',
    bg: 'Преди',
    ro: 'Înainte',
    el: 'Πριν',
    tr: 'Önce',
  },
  after: {
    me: 'Poslije',
    en: 'After',
    sr: 'Posle',
    hr: 'Poslije',
    bs: 'Poslije',
    sl: 'Po',
    mk: 'По',
    sq: 'Pas',
    bg: 'След',
    ro: 'După',
    el: 'Μετά',
    tr: 'Sonra',
  },
  deleteEntry: {
    me: 'Obriši unos',
    en: 'Delete entry',
    sr: 'Obriši unos',
    hr: 'Izbriši unos',
    bs: 'Obriši unos',
    sl: 'Izbriši vnos',
    mk: 'Избриши внес',
    sq: 'Fshi shënimin',
    bg: 'Изтрий записа',
    ro: 'Șterge intrarea',
    el: 'Διαγραφή καταχώρησης',
    tr: 'Girişi sil',
  },
  deleteConfirm: {
    me: 'Obrisati ovaj unos? To se ne može poništiti.',
    en: 'Delete this entry? This cannot be undone.',
    sr: 'Obrisati ovaj unos? To se ne može poništiti.',
    hr: 'Izbrisati ovaj unos? To se ne može poništiti.',
    bs: 'Obrisati ovaj unos? To se ne može poništiti.',
    sl: 'Želiš izbrisati ta vnos? Tega ni mogoče razveljaviti.',
    mk: 'Да се избрише овој внес? Ова не може да се врати.',
    sq: 'Të fshihet ky shënim? Ky veprim nuk mund të zhbëhet.',
    bg: 'Да се изтрие ли този запис? Това не може да бъде отменено.',
    ro: 'Ștergi această intrare? Acțiunea nu poate fi anulată.',
    el: 'Διαγραφή αυτής της καταχώρησης; Δεν μπορεί να αναιρεθεί.',
    tr: 'Bu giriş silinsin mi? Bu işlem geri alınamaz.',
  },
  loadError: {
    me: 'Unosi se nisu učitali. Pokušaj ponovo.',
    en: 'Could not load your entries. Please try again.',
    sr: 'Unosi se nisu učitali. Pokušaj ponovo.',
    hr: 'Unosi se nisu učitali. Pokušaj ponovno.',
    bs: 'Unosi se nisu učitali. Pokušaj ponovo.',
    sl: 'Vnosov ni bilo mogoče naložiti. Poskusi znova.',
    mk: 'Внесовите не се вчитаа. Обиди се повторно.',
    sq: 'Shënimet nuk u ngarkuan. Provo përsëri.',
    bg: 'Записите не се заредиха. Опитай отново.',
    ro: 'Intrările nu s-au încărcat. Încearcă din nou.',
    el: 'Δεν ήταν δυνατή η φόρτωση των καταχωρήσεων. Δοκίμασε ξανά.',
    tr: 'Girişler yüklenemedi. Tekrar dene.',
  },
  saveError: {
    me: 'Promjena nije sačuvana. Pokušaj ponovo.',
    en: 'Your change was not saved. Please try again.',
    sr: 'Promena nije sačuvana. Pokušaj ponovo.',
    hr: 'Promjena nije spremljena. Pokušaj ponovno.',
    bs: 'Promjena nije sačuvana. Pokušaj ponovo.',
    sl: 'Spremembe ni bilo mogoče shraniti. Poskusi znova.',
    mk: 'Промената не е зачувана. Обиди се повторно.',
    sq: 'Ndryshimi nuk u ruajt. Provo përsëri.',
    bg: 'Промяната не е запазена. Опитай отново.',
    ro: 'Modificarea nu a fost salvată. Încearcă din nou.',
    el: 'Η αλλαγή δεν αποθηκεύτηκε. Δοκίμασε ξανά.',
    tr: 'Değişiklik kaydedilemedi. Tekrar dene.',
  },
};

/**
 * Get translated journal string for the given language
 */
export function getJournalTranslation(key: JournalTranslationKey, language: Language): string {
  return JOURNAL_TRANSLATIONS[key][language];
}

/**
 * Pendulum page translation keys
 */
export type PendulumTranslationKey =
  | 'loginRequired'
  | 'loginDesc'
  | 'login'
  | 'premiumFeature'
  | 'premiumDesc'
  | 'premiumUse'
  | 'upgrade'
  | 'title'
  | 'openFullscreen'
  | 'howToUse'
  | 'instruction1'
  | 'instruction2'
  | 'instruction3'
  | 'instruction4'
  | 'instruction5'
  | 'instruction6'
  | 'preview'
  | 'previewNote'
  | 'energyZones'
  | 'harmful'
  | 'stressful'
  | 'neutral'
  | 'beneficial'
  | 'optimal'
  | 'canvasHarmful'
  | 'canvasNeutral'
  | 'canvasNeutralSub'
  | 'canvasBeneficial'
  | 'canvasYes'
  | 'canvasNo';

/**
 * Pendulum translations for all 12 supported languages
 */
const PENDULUM_TRANSLATIONS: Record<PendulumTranslationKey, Record<Language, string>> = {
  loginRequired: {
    me: 'Potrebna prijava',
    en: 'Login Required',
    sr: 'Potrebna prijava',
    hr: 'Potrebna prijava',
    bs: 'Potrebna prijava',
    sl: 'Potrebna prijava',
    mk: 'Потребна е најава',
    sq: 'Kërkohet identifikimi',
    bg: 'Изисква се вход',
    ro: 'Conectare necesară',
    el: 'Απαιτείται σύνδεση',
    tr: 'Giriş gerekli',
  },
  loginDesc: {
    me: 'Prijavi se da pristupiš Pendulumu',
    en: 'Please login to access the Pendulum feature',
    sr: 'Prijavi se da pristupiš Pendulumu',
    hr: 'Prijavi se za pristup Pendulumu',
    bs: 'Prijavi se da pristupiš Pendulumu',
    sl: 'Prijavi se za dostop do Nihala',
    mk: 'Најави се за да пристапиш до Нихалото',
    sq: 'Identifikohu për të hyrë te Penduli',
    bg: 'Влез за достъп до Махалото',
    ro: 'Conectează-te pentru a accesa Pendulul',
    el: 'Συνδέσου για πρόσβαση στο Εκκρεμές',
    tr: 'Sarkaca erişmek için giriş yap',
  },
  login: {
    me: 'Prijava',
    en: 'Login',
    sr: 'Prijava',
    hr: 'Prijava',
    bs: 'Prijava',
    sl: 'Prijava',
    mk: 'Најава',
    sq: 'Identifikohu',
    bg: 'Вход',
    ro: 'Conectare',
    el: 'Σύνδεση',
    tr: 'Giriş',
  },
  premiumFeature: {
    me: 'Premium funkcija',
    en: 'Premium Feature',
    sr: 'Premium funkcija',
    hr: 'Premium funkcija',
    bs: 'Premium funkcija',
    sl: 'Premium funkcija',
    mk: 'Premium функција',
    sq: 'Veçori Premium',
    bg: 'Premium функция',
    ro: 'Funcție Premium',
    el: 'Premium Λειτουργία',
    tr: 'Premium Özellik',
  },
  premiumDesc: {
    me: 'Pendulum/Radiestezija alat je dostupan za premium pretplatnike',
    en: 'The Pendulum/Radiesthesia tool is available for premium subscribers',
    sr: 'Pendulum/Radiestezija alat je dostupan za premium pretplatnike',
    hr: 'Pendulum/Radiestezija alat je dostupan za premium pretplatnike',
    bs: 'Pendulum/Radiestezija alat je dostupan za premium pretplatnike',
    sl: 'Orodje Nihalo/Radiestezija je na voljo za premium naročnike',
    mk: 'Алатката Нихало/Радиестезија е достапна за premium претплатници',
    sq: 'Mjeti Pendul/Radiestezi është i disponueshëm për abonentët premium',
    bg: 'Инструментът Махало/Радиестезия е достъпен за премиум абонати',
    ro: 'Instrumentul Pendul/Radiestezie este disponibil pentru abonații premium',
    el: 'Το εργαλείο Εκκρεμές/Ραδιαισθησία είναι διαθέσιμο για premium συνδρομητές',
    tr: 'Sarkaç/Radyestezi aracı premium aboneler için mevcuttur',
  },
  premiumUse: {
    me: 'Koristi interaktivnu ploču sa svojim pendulumom i usmjeri pažnju na pitanje ili namjeru.',
    en: 'Use the interactive board with your own pendulum and focus on a question or intention.',
    sr: 'Koristi interaktivnu ploču sa svojim pendulumom i usmeri pažnju na pitanje ili nameru.',
    hr: 'Koristi interaktivnu ploču sa svojim pendulumom i usmjeri pažnju na pitanje ili namjeru.',
    bs: 'Koristi interaktivnu ploču sa svojim pendulumom i usmjeri pažnju na pitanje ili namjeru.',
    sl: 'Uporabi interaktivno ploščo s svojim nihalom in se osredotoči na vprašanje ali namen.',
    mk: 'Користи ја интерактивната табла со своето нихало и насочи го вниманието кон прашање или намера.',
    sq: 'Përdor tabelën interaktive me pendulin tënd dhe përqendrohu te një pyetje ose qëllim.',
    bg: 'Използвай интерактивната дъска със своето махало и насочи вниманието си към въпрос или намерение.',
    ro: 'Folosește tabla interactivă cu pendulul tău și concentrează-te pe o întrebare sau o intenție.',
    el: 'Χρησιμοποίησε τον διαδραστικό πίνακα με το δικό σου εκκρεμές και εστίασε σε μια ερώτηση ή πρόθεση.',
    tr: 'Etkileşimli tahtayı kendi sarkacınla kullan ve bir soruya ya da niyete odaklan.',
  },
  upgrade: {
    me: 'Nadogradi na Premium',
    en: 'Upgrade to Premium',
    sr: 'Nadogradi na Premium',
    hr: 'Nadogradi na Premium',
    bs: 'Nadogradi na Premium',
    sl: 'Nadgradi na Premium',
    mk: 'Надгради на Premium',
    sq: 'Përmirëso në Premium',
    bg: 'Надгради до Premium',
    ro: 'Fă upgrade la Premium',
    el: 'Αναβάθμιση σε Premium',
    tr: 'Premium\'a Yükselt',
  },
  title: {
    me: 'Kalibracija Penduluma',
    en: 'Pendulum Calibration',
    sr: 'Kalibracija Penduluma',
    hr: 'Kalibracija Penduluma',
    bs: 'Kalibracija Penduluma',
    sl: 'Kalibracija Nihala',
    mk: 'Калибрација на Нихало',
    sq: 'Kalibrimi i Pendulit',
    bg: 'Калибриране на Махало',
    ro: 'Calibrarea Pendulului',
    el: 'Βαθμονόμηση Εκκρεμούς',
    tr: 'Sarkaç Kalibrasyonu',
  },
  openFullscreen: {
    me: 'Otvori puni ekran',
    en: 'Open Fullscreen Board',
    sr: 'Otvori puni ekran',
    hr: 'Otvori puni ekran',
    bs: 'Otvori puni ekran',
    sl: 'Odpri celozaslonsko ploščo',
    mk: 'Отвори цел екран',
    sq: 'Hap Tabelën me Ekran të Plotë',
    bg: 'Отвори на цял екран',
    ro: 'Deschide Tabla pe Tot Ecranul',
    el: 'Άνοιγμα Πλήρους Οθόνης',
    tr: 'Tam Ekran Tahtayı Aç',
  },
  howToUse: {
    me: 'Kako koristiti',
    en: 'How to Use',
    sr: 'Kako koristiti',
    hr: 'Kako koristiti',
    bs: 'Kako koristiti',
    sl: 'Kako uporabiti',
    mk: 'Како да користите',
    sq: 'Si të Përdoret',
    bg: 'Как да използвате',
    ro: 'Cum să folosești',
    el: 'Πώς να το χρησιμοποιήσεις',
    tr: 'Nasıl Kullanılır',
  },
  instruction1: {
    me: 'Postavi pametni telefon ravno na sto',
    en: 'Place your smartphone flat on a table',
    sr: 'Postavi pametni telefon ravno na sto',
    hr: 'Postavi pametni telefon ravno na stol',
    bs: 'Postavi pametni telefon ravno na sto',
    sl: 'Položi pametni telefon ravno na mizo',
    mk: 'Постави го паметниот телефон рамно на маса',
    sq: 'Vendose smartphone-in rrafsh mbi tavolinë',
    bg: 'Постави смартфона си плоско на маса',
    ro: 'Pune smartphone-ul întins pe masă',
    el: 'Τοποθέτησε το smartphone σου επίπεδα σε ένα τραπέζι',
    tr: 'Akıllı telefonunu masaya düz koy',
  },
  instruction2: {
    me: 'Otvori radiestezijsku ploču na punom ekranu',
    en: 'Open the fullscreen radiesthesia board',
    sr: 'Otvori radiestezijsku ploču na punom ekranu',
    hr: 'Otvori radiestezijsku ploču na punom ekranu',
    bs: 'Otvori radiestezijsku ploču na punom ekranu',
    sl: 'Odpri radiestezijsko ploščo v celozaslonskem načinu',
    mk: 'Отвори ја радиестезиската табла на цел екран',
    sq: 'Hap tabelën e radiestezisë në ekran të plotë',
    bg: 'Отвори радиестезийната дъска на цял екран',
    ro: 'Deschide tabla de radiestezie pe tot ecranul',
    el: 'Άνοιξε τον πίνακα ραδιαισθησίας σε πλήρη οθόνη',
    tr: 'Tam ekran radyestezi tahtasını aç',
  },
  instruction3: {
    me: 'Drži fizički pendulum iznad centralne tačke na ekranu',
    en: 'Hold your physical pendulum over the center point on screen',
    sr: 'Drži fizički pendulum iznad centralne tačke na ekranu',
    hr: 'Drži fizički pendulum iznad središnje točke na ekranu',
    bs: 'Drži fizički pendulum iznad centralne tačke na ekranu',
    sl: 'Drži fizično nihalo nad osrednjo točko na zaslonu',
    mk: 'Држи го физичкото нихало над централната точка на екранот',
    sq: 'Mbaje pendulin fizik mbi pikën qendrore në ekran',
    bg: 'Дръж физическото си махало над централната точка на екрана',
    ro: 'Ține pendulul fizic deasupra punctului central de pe ecran',
    el: 'Κράτα το φυσικό σου εκκρεμές πάνω από το κεντρικό σημείο της οθόνης',
    tr: 'Fiziksel sarkacını ekrandaki merkez noktanın üzerinde tut',
  },
  instruction4: {
    me: 'Fokusiraj se na svoje pitanje ili namjeru',
    en: 'Focus on your question or intention',
    sr: 'Fokusiraj se na svoje pitanje ili nameru',
    hr: 'Usredotoči se na svoje pitanje ili namjeru',
    bs: 'Fokusiraj se na svoje pitanje ili namjeru',
    sl: 'Osredotoči se na svoje vprašanje ali namen',
    mk: 'Фокусирај се на своето прашање или намера',
    sq: 'Përqendrohu te pyetja ose qëllimi yt',
    bg: 'Фокусирай се върху въпроса или намерението си',
    ro: 'Concentrează-te pe întrebarea sau intenția ta',
    el: 'Εστίασε στην ερώτηση ή την πρόθεσή σου',
    tr: 'Sorana veya niyetine odaklan',
  },
  instruction5: {
    me: 'Posmatraj gdje pendulum pokazuje na skali (0-100)',
    en: 'Observe where the pendulum points on the scale (0-100)',
    sr: 'Posmatraj gde pendulum pokazuje na skali (0-100)',
    hr: 'Promatraj gdje pendulum pokazuje na skali (0-100)',
    bs: 'Posmatraj gdje pendulum pokazuje na skali (0-100)',
    sl: 'Opazuj, kam kaže nihalo na lestvici (0-100)',
    mk: 'Набљудувај каде покажува нихалото на скалата (0-100)',
    sq: 'Vëzhgo ku tregon penduli në shkallë (0-100)',
    bg: 'Наблюдавай къде сочи махалото по скалата (0-100)',
    ro: 'Observă unde indică pendulul pe scară (0-100)',
    el: 'Παρατήρησε πού δείχνει το εκκρεμές στην κλίμακα (0-100)',
    tr: 'Sarkacın ölçekte (0-100) nereyi gösterdiğini gözlemle',
  },
  instruction6: {
    me: 'Centar pokazuje DA i NE odgovore',
    en: 'The center shows YES (DA) and NO (NE) responses',
    sr: 'Centar pokazuje DA i NE odgovore',
    hr: 'Centar pokazuje DA i NE odgovore',
    bs: 'Centar pokazuje DA i NE odgovore',
    sl: 'V sredini so prikazani odgovori DA in NE',
    mk: 'Центарот покажува ДА и НЕ одговори',
    sq: 'Qendra tregon përgjigjet PO dhe JO',
    bg: 'Центърът показва отговорите ДА и НЕ',
    ro: 'Centrul arată răspunsurile DA și NU',
    el: 'Το κέντρο δείχνει τις απαντήσεις ΝΑΙ και ΟΧΙ',
    tr: 'Merkez EVET ve HAYIR yanıtlarını gösterir',
  },
  preview: {
    me: 'Pregled',
    en: 'Preview',
    sr: 'Pregled',
    hr: 'Pregled',
    bs: 'Pregled',
    sl: 'Predogled',
    mk: 'Преглед',
    sq: 'Pamje Paraprake',
    bg: 'Преглед',
    ro: 'Previzualizare',
    el: 'Προεπισκόπηση',
    tr: 'Önizleme',
  },
  previewNote: {
    me: 'Dodirni „Otvori puni ekran“ za korišćenje sa fizičkim pendulumom',
    en: 'Tap "Open Fullscreen Board" to use with your physical pendulum',
    sr: 'Dodirni "Otvori puni ekran" za korišćenje sa fizičkim pendulumom',
    hr: 'Dodirni "Otvori puni ekran" za korištenje s fizičkim pendulumom',
    bs: 'Dodirni "Otvori puni ekran" za korištenje sa fizičkim pendulumom',
    sl: 'Tapni "Odpri celozaslonsko ploščo" za uporabo s fizičnim nihalom',
    mk: 'Допри „Отвори цел екран" за употреба со физичко нихало',
    sq: 'Prek "Hap Tabelën me Ekran të Plotë" për ta përdorur me pendulin tënd fizik',
    bg: 'Докосни „Отвори на цял екран" за използване с физическо махало',
    ro: 'Atinge „Deschide Tabla pe Tot Ecranul" pentru a o folosi cu pendulul fizic',
    el: 'Πάτησε "Άνοιγμα Πλήρους Οθόνης" για χρήση με το φυσικό σου εκκρεμές',
    tr: 'Fiziksel sarkacınla kullanmak için "Tam Ekran Tahtayı Aç"a dokun',
  },
  energyZones: {
    me: 'Energetske zone',
    en: 'Energy Zones',
    sr: 'Energetske zone',
    hr: 'Energetske zone',
    bs: 'Energetske zone',
    sl: 'Energijske cone',
    mk: 'Енергетски зони',
    sq: 'Zonat e Energjisë',
    bg: 'Енергийни зони',
    ro: 'Zone Energetice',
    el: 'Ενεργειακές Ζώνες',
    tr: 'Enerji Bölgeleri',
  },
  harmful: {
    me: 'Negativno',
    en: 'Negative',
    sr: 'Negativno',
    hr: 'Negativno',
    bs: 'Negativno',
    sl: 'Negativno',
    mk: 'Негативно',
    sq: 'Negative',
    bg: 'Негативно',
    ro: 'Negativ',
    el: 'Αρνητικό',
    tr: 'Olumsuz',
  },
  stressful: {
    me: 'Napeto',
    en: 'Tense',
    sr: 'Napeto',
    hr: 'Napeto',
    bs: 'Napeto',
    sl: 'Napeto',
    mk: 'Напнато',
    sq: 'E tensionuar',
    bg: 'Напрегнато',
    ro: 'Încordat',
    el: 'Τεταμένο',
    tr: 'Gergin',
  },
  neutral: {
    me: 'Neutralno',
    en: 'Neutral',
    sr: 'Neutralno',
    hr: 'Neutralno',
    bs: 'Neutralno',
    sl: 'Nevtralno',
    mk: 'Неутрално',
    sq: 'Neutrale',
    bg: 'Неутрално',
    ro: 'Neutru',
    el: 'Ουδέτερο',
    tr: 'Nötr',
  },
  beneficial: {
    me: 'Korisno',
    en: 'Beneficial',
    sr: 'Korisno',
    hr: 'Korisno',
    bs: 'Korisno',
    sl: 'Koristno',
    mk: 'Корисно',
    sq: 'E Dobishme',
    bg: 'Полезно',
    ro: 'Benefic',
    el: 'Ωφέλιμο',
    tr: 'Faydalı',
  },
  optimal: {
    me: 'Optimalno/Pozitivno',
    en: 'Optimal/Useful',
    sr: 'Optimalno/Pozitivno',
    hr: 'Optimalno/Pozitivno',
    bs: 'Optimalno/Pozitivno',
    sl: 'Optimalno/Pozitivno',
    mk: 'Оптимално/Позитивно',
    sq: 'Optimale/E Dobishme',
    bg: 'Оптимално/Положително',
    ro: 'Optim/Pozitiv',
    el: 'Βέλτιστο/Χρήσιμο',
    tr: 'Optimal/Pozitif',
  },
  canvasHarmful: {
    me: 'NEGATIVNO',
    en: 'NEGATIVE',
    sr: 'NEGATIVNO',
    hr: 'NEGATIVNO',
    bs: 'NEGATIVNO',
    sl: 'NEGATIVNO',
    mk: 'НЕГАТИВНО',
    sq: 'NEGATIVE',
    bg: 'НЕГАТИВНО',
    ro: 'NEGATIV',
    el: 'ΑΡΝΗΤΙΚΟ',
    tr: 'OLUMSUZ',
  },
  canvasNeutral: {
    me: 'NEUTRALNO',
    en: 'NEUTRAL',
    sr: 'NEUTRALNO',
    hr: 'NEUTRALNO',
    bs: 'NEUTRALNO',
    sl: 'NEVTRALNO',
    mk: 'НЕУТРАЛНО',
    sq: 'NEUTRALE',
    bg: 'НЕУТРАЛНО',
    ro: 'NEUTRU',
    el: 'ΟΥΔΕΤΕΡΟ',
    tr: 'NÖTR',
  },
  canvasNeutralSub: {
    me: 'RAVNOTEŽA',
    en: 'BALANCE',
    sr: 'RAVNOTEŽA',
    hr: 'RAVNOTEŽA',
    bs: 'RAVNOTEŽA',
    sl: 'RAVNOVESJE',
    mk: 'РАМНОТЕЖА',
    sq: 'EKUILIBËR',
    bg: 'РАВНОВЕСИЕ',
    ro: 'ECHILIBRU',
    el: 'ΙΣΟΡΡΟΠΙΑ',
    tr: 'DENGE',
  },
  canvasBeneficial: {
    me: 'KORISNO',
    en: 'BENEFICIAL',
    sr: 'KORISNO',
    hr: 'KORISNO',
    bs: 'KORISNO',
    sl: 'KORISTNO',
    mk: 'КОРИСНО',
    sq: 'E DOBISHME',
    bg: 'ПОЛЕЗНО',
    ro: 'BENEFIC',
    el: 'ΩΦΕΛΙΜΟ',
    tr: 'FAYDALI',
  },
  canvasYes: {
    me: 'DA',
    en: 'YES',
    sr: 'DA',
    hr: 'DA',
    bs: 'DA',
    sl: 'DA',
    mk: 'ДА',
    sq: 'PO',
    bg: 'ДА',
    ro: 'DA',
    el: 'ΝΑΙ',
    tr: 'EVET',
  },
  canvasNo: {
    me: 'NE',
    en: 'NO',
    sr: 'NE',
    hr: 'NE',
    bs: 'NE',
    sl: 'NE',
    mk: 'НЕ',
    sq: 'JO',
    bg: 'НЕ',
    ro: 'NU',
    el: 'ΟΧΙ',
    tr: 'HAYIR',
  },
};

/**
 * Get translated pendulum string for the given language
 */
export function getPendulumTranslation(key: PendulumTranslationKey, language: Language): string {
  return PENDULUM_TRANSLATIONS[key][language];
}

/**
 * SessionCard translation keys
 */
export type SessionCardTranslationKey =
  | 'upgrade'
  | 'play'
  | 'guidedSession'
  | 'guided'
  | 'premium'
  | 'favorite';

/**
 * SessionCard translations for all 12 supported languages
 */
const SESSIONCARD_TRANSLATIONS: Record<SessionCardTranslationKey, Record<Language, string>> = {
  upgrade: {
    me: 'Nadogradi',
    en: 'Upgrade',
    sr: 'Nadogradi',
    hr: 'Nadogradi',
    bs: 'Nadogradi',
    sl: 'Nadgradi',
    mk: 'Надгради',
    sq: 'Përmirëso',
    bg: 'Надгради',
    ro: 'Upgrade',
    el: 'Αναβάθμιση',
    tr: 'Yükselt',
  },
  play: {
    me: 'Pokreni',
    en: 'Play',
    sr: 'Pokreni',
    hr: 'Pokreni',
    bs: 'Pokreni',
    sl: 'Predvajaj',
    mk: 'Пушти',
    sq: 'Luaj',
    bg: 'Пусни',
    ro: 'Redă',
    el: 'Αναπαραγωγή',
    tr: 'Oynat',
  },
  guidedSession: {
    me: 'Vođena sesija',
    en: 'Guided Session',
    sr: 'Vođena sesija',
    hr: 'Vođena sesija',
    bs: 'Vođena sesija',
    sl: 'Vodena seja',
    mk: 'Водена сесија',
    sq: 'Seancë e udhëhequr',
    bg: 'Водена сесия',
    ro: 'Sesiune ghidată',
    el: 'Καθοδηγούμενη συνεδρία',
    tr: 'Rehberli Seans',
  },
  guided: {
    me: 'Vođena',
    en: 'Guided',
    sr: 'Vođena',
    hr: 'Vođena',
    bs: 'Vođena',
    sl: 'Vodena',
    mk: 'Водена',
    sq: 'E udhëhequr',
    bg: 'Водена',
    ro: 'Ghidată',
    el: 'Καθοδηγούμενη',
    tr: 'Rehberli',
  },
  premium: {
    me: 'Premium',
    en: 'Premium',
    sr: 'Premium',
    hr: 'Premium',
    bs: 'Premium',
    sl: 'Premium',
    mk: 'Премиум',
    sq: 'Premium',
    bg: 'Премиум',
    ro: 'Premium',
    el: 'Premium',
    tr: 'Premium',
  },
  favorite: {
    me: 'Omiljena',
    en: 'Favorite',
    sr: 'Omiljena',
    hr: 'Omiljena',
    bs: 'Omiljena',
    sl: 'Priljubljena',
    mk: 'Омилена',
    sq: 'E preferuar',
    bg: 'Любима',
    ro: 'Favorită',
    el: 'Αγαπημένη',
    tr: 'Favori',
  },
};

/**
 * Get translated SessionCard string for the given language
 * @param key - The translation key
 * @param language - The target language
 * @returns The translated string
 */
export function getSessionCardTranslation(key: SessionCardTranslationKey, language: Language): string {
  return SESSIONCARD_TRANSLATIONS[key][language];
}

/**
 * Pricing Card translation keys
 */
export type PlanPickerTranslationKey =
  | 'choosePlanCta'
  | 'forOneMonth'
  | 'perMonthApprox'
  | 'allPlansInclude'
  | 'renewsMonthly'
  | 'renewsQuarterly'
  | 'renewsSemiAnnual'
  | 'renewsAnnual'
  | 'renewalTerms'
  | 'freeTrialPlan'
  | 'fullAccess5Days'
  | 'trialStartsAtRegistration'
  | 'startFreeTrial'
  | 'trialPayNow'
  | 'processing'
  | 'somethingWentWrong';

/**
 * Pretplata plan picker (A5): plan lines, CTA, renewal and cancellation disclosure, trial block.
 * Montenegrin from Luka's WEB5 and Q6 where it exists (docs/redesign-2026-10/copy-for-luka.md lists ours).
 */
const PLAN_PICKER_TRANSLATIONS: Record<PlanPickerTranslationKey, Record<Language, string>> = {
  choosePlanCta: {
    me: 'Izaberi paket — {price}',
    en: 'Choose plan — {price}',
    sr: 'Izaberi paket — {price}',
    hr: 'Odaberi paket — {price}',
    bs: 'Izaberi paket — {price}',
    sl: 'Izberi paket — {price}',
    mk: 'Избери пакет — {price}',
    sq: 'Zgjidh paketën — {price}',
    bg: 'Избери план — {price}',
    ro: 'Alege abonamentul — {price}',
    el: 'Επίλεξε πακέτο — {price}',
    tr: 'Paketi seç — {price}',
  },
  forOneMonth: {
    me: 'Za 1 mjesec',
    en: 'For 1 month',
    sr: 'Za 1 mesec',
    hr: 'Za 1 mjesec',
    bs: 'Za 1 mjesec',
    sl: 'Za 1 mesec',
    mk: 'За 1 месец',
    sq: 'Për 1 muaj',
    bg: 'За 1 месец',
    ro: 'Pentru 1 lună',
    el: 'Για 1 μήνα',
    tr: '1 ay için',
  },
  perMonthApprox: {
    me: 'Oko {price} mjesečno',
    en: 'About {price} a month',
    sr: 'Oko {price} mesečno',
    hr: 'Oko {price} mjesečno',
    bs: 'Oko {price} mjesečno',
    sl: 'Približno {price} na mesec',
    mk: 'Околу {price} месечно',
    sq: 'Rreth {price} në muaj',
    bg: 'Около {price} на месец',
    ro: 'Aproximativ {price} pe lună',
    el: 'Περίπου {price} τον μήνα',
    tr: 'Aylık yaklaşık {price}',
  },
  allPlansInclude: {
    me: 'Svi paketi uključuju:',
    en: 'Every plan includes:',
    sr: 'Svi paketi uključuju:',
    hr: 'Svi paketi uključuju:',
    bs: 'Svi paketi uključuju:',
    sl: 'Vsi paketi vključujejo:',
    mk: 'Сите пакети вклучуваат:',
    sq: 'Të gjitha paketat përfshijnë:',
    bg: 'Всички планове включват:',
    ro: 'Toate abonamentele includ:',
    el: 'Όλα τα πακέτα περιλαμβάνουν:',
    tr: 'Tüm paketlere dahil:',
  },
  renewsMonthly: {
    me: 'Naplaćuje se {price} svakog mjeseca.',
    en: '{price} is charged every month.',
    sr: 'Naplaćuje se {price} svakog meseca.',
    hr: 'Naplaćuje se {price} svaki mjesec.',
    bs: 'Naplaćuje se {price} svakog mjeseca.',
    sl: 'Vsak mesec se zaračuna {price}.',
    mk: 'Се наплатува {price} секој месец.',
    sq: 'Tarifohet {price} çdo muaj.',
    bg: 'Таксуват се {price} всеки месец.',
    ro: 'Se percep {price} în fiecare lună.',
    el: 'Χρεώνεται {price} κάθε μήνα.',
    tr: 'Her ay {price} tahsil edilir.',
  },
  renewsQuarterly: {
    me: 'Naplaćuje se {price} svaka 3 mjeseca.',
    en: '{price} is charged every 3 months.',
    sr: 'Naplaćuje se {price} svaka 3 meseca.',
    hr: 'Naplaćuje se {price} svaka 3 mjeseca.',
    bs: 'Naplaćuje se {price} svaka 3 mjeseca.',
    sl: 'Vsake 3 mesece se zaračuna {price}.',
    mk: 'Се наплатува {price} на секои 3 месеци.',
    sq: 'Tarifohet {price} çdo 3 muaj.',
    bg: 'Таксуват се {price} на всеки 3 месеца.',
    ro: 'Se percep {price} la fiecare 3 luni.',
    el: 'Χρεώνεται {price} κάθε 3 μήνες.',
    tr: 'Her 3 ayda bir {price} tahsil edilir.',
  },
  renewsSemiAnnual: {
    me: 'Naplaćuje se {price} svakih 6 mjeseci.',
    en: '{price} is charged every 6 months.',
    sr: 'Naplaćuje se {price} svakih 6 meseci.',
    hr: 'Naplaćuje se {price} svakih 6 mjeseci.',
    bs: 'Naplaćuje se {price} svakih 6 mjeseci.',
    sl: 'Vsakih 6 mesecev se zaračuna {price}.',
    mk: 'Се наплатува {price} на секои 6 месеци.',
    sq: 'Tarifohet {price} çdo 6 muaj.',
    bg: 'Таксуват се {price} на всеки 6 месеца.',
    ro: 'Se percep {price} la fiecare 6 luni.',
    el: 'Χρεώνεται {price} κάθε 6 μήνες.',
    tr: 'Her 6 ayda bir {price} tahsil edilir.',
  },
  renewsAnnual: {
    me: 'Naplaćuje se {price} svakih 12 mjeseci.',
    en: '{price} is charged every 12 months.',
    sr: 'Naplaćuje se {price} svakih 12 meseci.',
    hr: 'Naplaćuje se {price} svakih 12 mjeseci.',
    bs: 'Naplaćuje se {price} svakih 12 mjeseci.',
    sl: 'Vsakih 12 mesecev se zaračuna {price}.',
    mk: 'Се наплатува {price} на секои 12 месеци.',
    sq: 'Tarifohet {price} çdo 12 muaj.',
    bg: 'Таксуват се {price} на всеки 12 месеца.',
    ro: 'Se percep {price} la fiecare 12 luni.',
    el: 'Χρεώνεται {price} κάθε 12 μήνες.',
    tr: 'Her 12 ayda bir {price} tahsil edilir.',
  },
  renewalTerms: {
    me: 'Pretplata se automatski obnavlja i može se otkazati bilo kada. Pristup ostaje do kraja plaćenog perioda.',
    en: 'The subscription renews automatically and can be cancelled at any time. Access remains until the end of the paid period.',
    sr: 'Pretplata se automatski obnavlja i može se otkazati u bilo kom trenutku. Pristup ostaje do kraja plaćenog perioda.',
    hr: 'Pretplata se automatski obnavlja i može se otkazati u bilo kojem trenutku. Pristup ostaje do kraja plaćenog razdoblja.',
    bs: 'Pretplata se automatski obnavlja i može se otkazati bilo kada. Pristup ostaje do kraja plaćenog perioda.',
    sl: 'Naročnina se samodejno podaljša in jo je mogoče kadar koli preklicati. Dostop ostane do konca plačanega obdobja.',
    mk: 'Претплатата автоматски се обновува и може да се откаже во секое време. Пристапот останува до крајот на платениот период.',
    sq: 'Abonimi rinovohet automatikisht dhe mund të anulohet në çdo kohë. Qasja mbetet deri në fund të periudhës së paguar.',
    bg: 'Абонаментът се подновява автоматично и може да бъде прекратен по всяко време. Достъпът остава до края на платения период.',
    ro: 'Abonamentul se reînnoiește automat și poate fi anulat oricând. Accesul rămâne până la sfârșitul perioadei plătite.',
    el: 'Η συνδρομή ανανεώνεται αυτόματα και μπορεί να ακυρωθεί ανά πάσα στιγμή. Η πρόσβαση παραμένει μέχρι το τέλος της πληρωμένης περιόδου.',
    tr: 'Abonelik otomatik olarak yenilenir ve istediğin zaman iptal edilebilir. Erişim, ödenen dönemin sonuna kadar devam eder.',
  },
  freeTrialPlan: {
    me: 'Besplatan probni period od 5 dana',
    en: '5-day free trial',
    sr: 'Besplatan probni period od 5 dana',
    hr: 'Besplatno probno razdoblje od 5 dana',
    bs: 'Besplatan probni period od 5 dana',
    sl: '5-dnevni brezplačni preizkus',
    mk: 'Бесплатен пробен период од 5 дена',
    sq: 'Periudhë provuese falas 5-ditore',
    bg: 'Безплатен пробен период от 5 дни',
    ro: 'Perioadă de probă gratuită de 5 zile',
    el: 'Δωρεάν δοκιμή 5 ημερών',
    tr: '5 günlük ücretsiz deneme',
  },
  fullAccess5Days: {
    me: 'Puni pristup 5 dana, bez kartice.',
    en: 'Full access for 5 days, no card required.',
    sr: 'Pun pristup 5 dana, bez kartice.',
    hr: 'Puni pristup 5 dana, bez kartice.',
    bs: 'Puni pristup 5 dana, bez kartice.',
    sl: 'Poln dostop 5 dni, brez kartice.',
    mk: 'Целосен пристап 5 дена, без картичка.',
    sq: 'Qasje e plotë për 5 ditë, pa kartë.',
    bg: 'Пълен достъп за 5 дни, без карта.',
    ro: 'Acces complet timp de 5 zile, fără card.',
    el: 'Πλήρης πρόσβαση για 5 ημέρες, χωρίς κάρτα.',
    tr: '5 gün tam erişim, kart gerekmez.',
  },
  trialStartsAtRegistration: {
    me: 'Počinje kad napraviš nalog. Jedan probni period po nalogu.',
    en: 'It starts when you create your account. One trial per account.',
    sr: 'Počinje kad napraviš nalog. Jedan probni period po nalogu.',
    hr: 'Počinje kad izradiš korisnički račun. Jedno probno razdoblje po računu.',
    bs: 'Počinje kad napraviš nalog. Jedan probni period po nalogu.',
    sl: 'Začne se, ko ustvariš račun. En preizkus na račun.',
    mk: 'Започнува кога ќе креираш сметка. Еден пробен период по сметка.',
    sq: 'Fillon kur krijon llogarinë. Një periudhë provuese për llogari.',
    bg: 'Започва, когато създадеш акаунт. Един пробен период на акаунт.',
    ro: 'Începe când îți creezi contul. O singură perioadă de probă pe cont.',
    el: 'Ξεκινά όταν δημιουργείς λογαριασμό. Μία δοκιμή ανά λογαριασμό.',
    tr: 'Hesabını oluşturduğunda başlar. Hesap başına bir deneme.',
  },
  startFreeTrial: {
    me: 'Započni probni period',
    en: 'Start free trial',
    sr: 'Započni probni period',
    hr: 'Započni probno razdoblje',
    bs: 'Započni probni period',
    sl: 'Začni preizkus',
    mk: 'Започни пробен период',
    sq: 'Fillo periudhën provuese',
    bg: 'Започни пробния период',
    ro: 'Începe perioada de probă',
    el: 'Ξεκίνα τη δοκιμή',
    tr: 'Denemeyi başlat',
  },
  trialPayNow: {
    me: 'Ako platiš tokom probnog perioda, plaćeni period počinje odmah.',
    en: 'If you pay during the trial, the paid period starts right away.',
    sr: 'Ako platiš tokom probnog perioda, plaćeni period počinje odmah.',
    hr: 'Ako platiš tijekom probnog razdoblja, plaćeno razdoblje počinje odmah.',
    bs: 'Ako platiš tokom probnog perioda, plaćeni period počinje odmah.',
    sl: 'Če plačaš med preizkusom, se plačano obdobje začne takoj.',
    mk: 'Ако платиш за време на пробниот период, платениот период почнува веднаш.',
    sq: 'Nëse paguan gjatë periudhës provuese, periudha e paguar fillon menjëherë.',
    bg: 'Ако платиш по време на пробния период, платеният период започва веднага.',
    ro: 'Dacă plătești în timpul perioadei de probă, perioada plătită începe imediat.',
    el: 'Αν πληρώσεις κατά τη δοκιμή, η πληρωμένη περίοδος ξεκινά αμέσως.',
    tr: 'Deneme sırasında ödeme yaparsan ücretli dönem hemen başlar.',
  },
  processing: {
    me: 'Obrada...',
    en: 'Processing...',
    sr: 'Obrada...',
    hr: 'Obrada...',
    bs: 'Obrada...',
    sl: 'Obdelava...',
    mk: 'Обработка...',
    sq: 'Duke Përpunuar...',
    bg: 'Обработка...',
    ro: 'Procesare...',
    el: 'Επεξεργασία...',
    tr: 'İşleniyor...',
  },
  somethingWentWrong: {
    me: 'Nešto je pošlo po zlu. Pokušaj ponovo.',
    en: 'Something went wrong. Please try again.',
    sr: 'Nešto je pošlo po zlu. Pokušaj ponovo.',
    hr: 'Nešto je pošlo po zlu. Pokušaj ponovno.',
    bs: 'Nešto je pošlo po zlu. Pokušaj ponovo.',
    sl: 'Nekaj je šlo narobe. Poskusi znova.',
    mk: 'Нешто тргна наопаку. Обиди се повторно.',
    sq: 'Diçka shkoi keq. Provo përsëri.',
    bg: 'Нещо се обърка. Опитай отново.',
    ro: 'Ceva nu a funcționat. Încearcă din nou.',
    el: 'Κάτι πήγε στραβά. Δοκίμασε ξανά.',
    tr: 'Bir şeyler ters gitti. Tekrar dene.',
  },
};

export function getPlanPickerTranslation(key: PlanPickerTranslationKey, language: Language): string {
  return PLAN_PICKER_TRANSLATIONS[key][language];
}

/**
 * Check-in Modal translation keys
 */
export type CheckinModalTranslationKey =
  | 'title'
  | 'overallEnergy'
  | 'mood'
  | 'physicalState'
  | 'physicalStatePlaceholder'
  | 'mentalClarity'
  | 'emotionalBalance'
  | 'saveCheckin'
  | 'moodPeaceful'
  | 'moodJoyful'
  | 'moodCalm'
  | 'moodAnxious'
  | 'moodStressed'
  | 'moodTired';

/**
 * Check-in Modal translations for all 12 supported languages
 */
const CHECKIN_MODAL_TRANSLATIONS: Record<CheckinModalTranslationKey, Record<Language, string>> = {
  title: {
    me: 'Dnevna Energetska Provjera',
    en: 'Daily Energetic Check-in',
    sr: 'Dnevna Energetska Provera',
    hr: 'Dnevna Energetska Provjera',
    bs: 'Dnevna Energetska Provjera',
    sl: 'Dnevni Energetski Pregled',
    mk: 'Дневна Енергетска Проверка',
    sq: 'Kontrolli Ditor Energjetik',
    bg: 'Дневна Енергетична Проверка',
    ro: 'Control Energetic Zilnic',
    el: 'Ημερήσιος Ενεργειακός Έλεγχος',
    tr: 'Günlük Enerji Kontrolü',
  },
  overallEnergy: {
    me: 'Ukupna Energija',
    en: 'Overall Energy',
    sr: 'Ukupna Energija',
    hr: 'Ukupna Energija',
    bs: 'Ukupna Energija',
    sl: 'Skupna Energija',
    mk: 'Вкупна Енергија',
    sq: 'Energjia e Përgjithshme',
    bg: 'Обща Енергия',
    ro: 'Energie Generală',
    el: 'Συνολική Ενέργεια',
    tr: 'Genel Enerji',
  },
  mood: {
    me: 'Raspoloženje',
    en: 'Mood',
    sr: 'Raspoloženje',
    hr: 'Raspoloženje',
    bs: 'Raspoloženje',
    sl: 'Razpoloženje',
    mk: 'Расположение',
    sq: 'Disponimi',
    bg: 'Настроение',
    ro: 'Dispoziție',
    el: 'Διάθεση',
    tr: 'Ruh Hali',
  },
  physicalState: {
    me: 'Fizičko Stanje',
    en: 'Physical State',
    sr: 'Fizičko Stanje',
    hr: 'Fizičko Stanje',
    bs: 'Fizičko Stanje',
    sl: 'Fizično Stanje',
    mk: 'Физичка Состојба',
    sq: 'Gjendja Fizike',
    bg: 'Физическо Състояние',
    ro: 'Stare Fizică',
    el: 'Σωματική Κατάσταση',
    tr: 'Fiziksel Durum',
  },
  physicalStatePlaceholder: {
    me: 'Kako se tvoje tijelo osjeća?',
    en: 'How does your body feel?',
    sr: 'Kako se tvoje telo oseća?',
    hr: 'Kako se tvoje tijelo osjeća?',
    bs: 'Kako se tvoje tijelo osjeća?',
    sl: 'Kako se počuti tvoje telo?',
    mk: 'Како се чувствува твоето тело?',
    sq: 'Si ndihet trupi yt?',
    bg: 'Как се чувства тялото ти?',
    ro: 'Cum se simte corpul tău?',
    el: 'Πώς νιώθει το σώμα σου;',
    tr: 'Vücudun nasıl hissediyor?',
  },
  mentalClarity: {
    me: 'Mentalna Jasnoća',
    en: 'Mental Clarity',
    sr: 'Mentalna Jasnoća',
    hr: 'Mentalna Jasnoća',
    bs: 'Mentalna Jasnoća',
    sl: 'Mentalna Jasnost',
    mk: 'Ментална Јасност',
    sq: 'Qartësia Mendore',
    bg: 'Ментална Яснота',
    ro: 'Claritate Mentală',
    el: 'Νοητική Διαύγεια',
    tr: 'Zihinsel Berraklık',
  },
  emotionalBalance: {
    me: 'Emocionalna Ravnoteža',
    en: 'Emotional Balance',
    sr: 'Emocionalna Ravnoteža',
    hr: 'Emocionalna Ravnoteža',
    bs: 'Emocionalna Ravnoteža',
    sl: 'Čustveno Ravnovesje',
    mk: 'Емоционална Рамнотежа',
    sq: 'Ekuilibri Emocional',
    bg: 'Емоционален Баланс',
    ro: 'Echilibru Emoțional',
    el: 'Συναισθηματική Ισορροπία',
    tr: 'Duygusal Denge',
  },
  saveCheckin: {
    me: 'Sačuvaj Provjeru',
    en: 'Save Check-in',
    sr: 'Sačuvaj Proveru',
    hr: 'Spremi Provjeru',
    bs: 'Sačuvaj Provjeru',
    sl: 'Shrani Pregled',
    mk: 'Зачувај Проверка',
    sq: 'Ruaj Kontrollin',
    bg: 'Запази Проверката',
    ro: 'Salvează Controlul',
    el: 'Αποθήκευση Ελέγχου',
    tr: 'Kontrolü Kaydet',
  },
  moodPeaceful: {
    me: 'Mir',
    en: 'Peaceful',
    sr: 'Mir',
    hr: 'Mir',
    bs: 'Mir',
    sl: 'Mir',
    mk: 'Мир',
    sq: 'Paqe',
    bg: 'Покой',
    ro: 'Liniște',
    el: 'Γαλήνη',
    tr: 'Huzurlu',
  },
  moodJoyful: {
    me: 'Radost',
    en: 'Joyful',
    sr: 'Radost',
    hr: 'Radost',
    bs: 'Radost',
    sl: 'Veselje',
    mk: 'Радост',
    sq: 'Gëzim',
    bg: 'Радост',
    ro: 'Bucurie',
    el: 'Χαρά',
    tr: 'Neşeli',
  },
  moodCalm: {
    me: 'Smirenost',
    en: 'Calm',
    sr: 'Smirenost',
    hr: 'Smirenost',
    bs: 'Smirenost',
    sl: 'Umirjenost',
    mk: 'Смиреност',
    sq: 'Qetësi',
    bg: 'Спокойствие',
    ro: 'Calm',
    el: 'Ηρεμία',
    tr: 'Sakin',
  },
  moodAnxious: {
    me: 'Nemir',
    en: 'Uneasy',
    sr: 'Nemir',
    hr: 'Nemir',
    bs: 'Nemir',
    sl: 'Nemir',
    mk: 'Немир',
    sq: 'Shqetësim',
    bg: 'Безпокойство',
    ro: 'Neliniște',
    el: 'Ανησυχία',
    tr: 'Huzursuz',
  },
  moodStressed: {
    me: 'Napetost',
    en: 'Tense',
    sr: 'Napetost',
    hr: 'Napetost',
    bs: 'Napetost',
    sl: 'Napetost',
    mk: 'Напнатост',
    sq: 'Tension',
    bg: 'Напрежение',
    ro: 'Încordare',
    el: 'Ένταση',
    tr: 'Gergin',
  },
  moodTired: {
    me: 'Umor',
    en: 'Tired',
    sr: 'Umor',
    hr: 'Umor',
    bs: 'Umor',
    sl: 'Utrujenost',
    mk: 'Замор',
    sq: 'Lodhje',
    bg: 'Умора',
    ro: 'Oboseală',
    el: 'Κούραση',
    tr: 'Yorgun',
  },
};

/**
 * Get translated check-in modal string for the given language
 */
export function getCheckinModalTranslation(key: CheckinModalTranslationKey, language: Language): string {
  return CHECKIN_MODAL_TRANSLATIONS[key][language];
}

/**
 * Journal Entry Modal translation keys
 */
export type JournalEntryModalTranslationKey =
  | 'title'
  | 'entry'
  | 'entryPlaceholder'
  | 'moodBefore'
  | 'moodAfter'
  | 'selectMood'
  | 'cancel'
  | 'saveEntry'
  | 'moodPeaceful'
  | 'moodJoyful'
  | 'moodAnxious'
  | 'moodStressed'
  | 'moodCalm'
  | 'moodEnergized';

/**
 * Journal Entry Modal translations for all 12 supported languages
 */
const JOURNAL_ENTRY_MODAL_TRANSLATIONS: Record<JournalEntryModalTranslationKey, Record<Language, string>> = {
  title: {
    me: 'Novi Unos u Dnevnik',
    en: 'New Journal Entry',
    sr: 'Novi Unos u Dnevnik',
    hr: 'Novi Unos u Dnevnik',
    bs: 'Novi Unos u Dnevnik',
    sl: 'Nov Vnos v Dnevnik',
    mk: 'Нов Внес во Дневник',
    sq: 'Hyrje e Re në Ditar',
    bg: 'Нов Запис в Дневник',
    ro: 'Intrare Nouă în Jurnal',
    el: 'Νέα Καταχώρηση Ημερολογίου',
    tr: 'Yeni Günlük Girişi',
  },
  entry: {
    me: 'Unos',
    en: 'Entry',
    sr: 'Unos',
    hr: 'Unos',
    bs: 'Unos',
    sl: 'Vnos',
    mk: 'Внес',
    sq: 'Hyrje',
    bg: 'Запис',
    ro: 'Intrare',
    el: 'Καταχώρηση',
    tr: 'Giriş',
  },
  entryPlaceholder: {
    me: 'Napiši svoje misli, refleksije ili namjere...',
    en: 'Write your thoughts, reflections, or intentions...',
    sr: 'Napiši svoje misli, refleksije ili namere...',
    hr: 'Napiši svoje misli, refleksije ili namjere...',
    bs: 'Napiši svoje misli, refleksije ili namjere...',
    sl: 'Napiši svoje misli, razmišljanja ali namene...',
    mk: 'Напиши ги своите мисли, рефлексии или намери...',
    sq: 'Shkruaj mendimet, reflektimet ose qëllimet e tua...',
    bg: 'Напиши своите мисли, размисли или намерения...',
    ro: 'Scrie-ți gândurile, reflecțiile sau intențiile...',
    el: 'Γράψε τις σκέψεις, τους αναστοχασμούς ή τις προθέσεις σου...',
    tr: 'Düşüncelerini, yansımalarını veya niyetlerini yaz...',
  },
  moodBefore: {
    me: 'Raspoloženje prije',
    en: 'Mood before',
    sr: 'Raspoloženje pre',
    hr: 'Raspoloženje prije',
    bs: 'Raspoloženje prije',
    sl: 'Razpoloženje pred',
    mk: 'Расположение пред',
    sq: 'Disponimi para',
    bg: 'Настроение преди',
    ro: 'Dispoziție înainte',
    el: 'Διάθεση πριν',
    tr: 'Önceki ruh hâli',
  },
  moodAfter: {
    me: 'Raspoloženje poslije (neobavezno)',
    en: 'Mood after (optional)',
    sr: 'Raspoloženje posle (opciono)',
    hr: 'Raspoloženje poslije (neobavezno)',
    bs: 'Raspoloženje poslije (neobavezno)',
    sl: 'Razpoloženje po (neobvezno)',
    mk: 'Расположение после (опционално)',
    sq: 'Disponimi pas (opsionale)',
    bg: 'Настроение след (по избор)',
    ro: 'Dispoziție după (opțional)',
    el: 'Διάθεση μετά (προαιρετικό)',
    tr: 'Sonraki ruh hâli (isteğe bağlı)',
  },
  selectMood: {
    me: 'Izaberi raspoloženje...',
    en: 'Select mood...',
    sr: 'Izaberi raspoloženje...',
    hr: 'Odaberi raspoloženje...',
    bs: 'Izaberi raspoloženje...',
    sl: 'Izberi razpoloženje...',
    mk: 'Избери расположение...',
    sq: 'Zgjidh disponimin...',
    bg: 'Избери настроение...',
    ro: 'Alege starea...',
    el: 'Επίλεξε διάθεση...',
    tr: 'Ruh hâlini seç...',
  },
  cancel: {
    me: 'Otkaži',
    en: 'Cancel',
    sr: 'Otkaži',
    hr: 'Otkaži',
    bs: 'Otkaži',
    sl: 'Prekliči',
    mk: 'Откажи',
    sq: 'Anulo',
    bg: 'Отказ',
    ro: 'Anulare',
    el: 'Ακύρωση',
    tr: 'İptal',
  },
  saveEntry: {
    me: 'Sačuvaj Unos',
    en: 'Save Entry',
    sr: 'Sačuvaj Unos',
    hr: 'Spremi Unos',
    bs: 'Sačuvaj Unos',
    sl: 'Shrani Vnos',
    mk: 'Зачувај Внес',
    sq: 'Ruaj Hyrjen',
    bg: 'Запази Записа',
    ro: 'Salvează Intrarea',
    el: 'Αποθήκευση Καταχώρησης',
    tr: 'Girişi Kaydet',
  },
  moodPeaceful: {
    me: 'Mir',
    en: 'Peaceful',
    sr: 'Mir',
    hr: 'Mir',
    bs: 'Mir',
    sl: 'Mir',
    mk: 'Мир',
    sq: 'Paqe',
    bg: 'Покой',
    ro: 'Liniște',
    el: 'Γαλήνη',
    tr: 'Huzurlu',
  },
  moodJoyful: {
    me: 'Radost',
    en: 'Joyful',
    sr: 'Radost',
    hr: 'Radost',
    bs: 'Radost',
    sl: 'Veselje',
    mk: 'Радост',
    sq: 'Gëzim',
    bg: 'Радост',
    ro: 'Bucurie',
    el: 'Χαρά',
    tr: 'Neşeli',
  },
  moodAnxious: {
    me: 'Nemir',
    en: 'Uneasy',
    sr: 'Nemir',
    hr: 'Nemir',
    bs: 'Nemir',
    sl: 'Nemir',
    mk: 'Немир',
    sq: 'Shqetësim',
    bg: 'Безпокойство',
    ro: 'Neliniște',
    el: 'Ανησυχία',
    tr: 'Huzursuz',
  },
  moodStressed: {
    me: 'Napetost',
    en: 'Tense',
    sr: 'Napetost',
    hr: 'Napetost',
    bs: 'Napetost',
    sl: 'Napetost',
    mk: 'Напнатост',
    sq: 'Tension',
    bg: 'Напрежение',
    ro: 'Încordare',
    el: 'Ένταση',
    tr: 'Gergin',
  },
  moodCalm: {
    me: 'Smirenost',
    en: 'Calm',
    sr: 'Smirenost',
    hr: 'Smirenost',
    bs: 'Smirenost',
    sl: 'Umirjenost',
    mk: 'Смиреност',
    sq: 'Qetësi',
    bg: 'Спокойствие',
    ro: 'Calm',
    el: 'Ηρεμία',
    tr: 'Sakin',
  },
  moodEnergized: {
    me: 'Energičnost',
    en: 'Energized',
    sr: 'Energičnost',
    hr: 'Energičnost',
    bs: 'Energičnost',
    sl: 'Energičnost',
    mk: 'Енергичност',
    sq: 'Energji',
    bg: 'Енергичност',
    ro: 'Energie',
    el: 'Ενέργεια',
    tr: 'Enerjik',
  },
};

/**
 * Get translated journal entry modal string for the given language
 */
export function getJournalEntryModalTranslation(key: JournalEntryModalTranslationKey, language: Language): string {
  return JOURNAL_ENTRY_MODAL_TRANSLATIONS[key][language];
}

/**
 * Upgrade Modal translation keys
 */
export type UpgradeModalTranslationKey =
  | 'title'
  | 'description'
  | 'featureUnlimitedAccess'
  | 'featureOfflineDownloads'
  | 'featureExclusiveContent'
  | 'featureAdFree'
  | 'featurePendulumTools'
  | 'featureJournal'
  | 'upgradeNow'
  | 'maybeLater';

/**
 * Upgrade Modal translations for all 12 supported languages
 */
const UPGRADE_MODAL_TRANSLATIONS: Record<UpgradeModalTranslationKey, Record<Language, string>> = {
  title: {
    me: 'Nadogradi na Premium',
    en: 'Upgrade to Premium',
    sr: 'Nadogradi na Premium',
    hr: 'Nadogradi na Premium',
    bs: 'Nadogradi na Premium',
    sl: 'Nadgradi na Premium',
    mk: 'Надгради на Премиум',
    sq: 'Përmirëso në Premium',
    bg: 'Надгради до Премиум',
    ro: 'Fă upgrade la Premium',
    el: 'Αναβάθμιση σε Premium',
    tr: 'Premium\'a Yükselt',
  },
  description: {
    me: 'Dobij pristup svim premium sesijama i funkcijama',
    en: 'Get access to all premium sessions and features',
    sr: 'Dobij pristup svim premium sesijama i funkcijama',
    hr: 'Dobij pristup svim premium sesijama i značajkama',
    bs: 'Dobij pristup svim premium sesijama i funkcijama',
    sl: 'Pridobi dostop do vseh premium sej in funkcij',
    mk: 'Добиј пристап до сите премиум сесии и функции',
    sq: 'Merr akses në të gjitha seancat dhe veçoritë premium',
    bg: 'Получи достъп до всички премиум сесии и функции',
    ro: 'Obține acces la toate sesiunile și funcțiile premium',
    el: 'Απόκτησε πρόσβαση σε όλες τις premium συνεδρίες και λειτουργίες',
    tr: 'Tüm premium seanslara ve özelliklere eriş',
  },
  featureUnlimitedAccess: {
    me: 'Neograničen pristup svim sesijama',
    en: 'Unlimited access to all sessions',
    sr: 'Neograničen pristup svim sesijama',
    hr: 'Neograničen pristup svim sesijama',
    bs: 'Neograničen pristup svim sesijama',
    sl: 'Neomejen dostop do vseh sej',
    mk: 'Неограничен пристап до сите сесии',
    sq: 'Akses i pakufizuar në të gjitha seancat',
    bg: 'Неограничен достъп до всички сесии',
    ro: 'Acces nelimitat la toate sesiunile',
    el: 'Απεριόριστη πρόσβαση σε όλες τις συνεδρίες',
    tr: 'Tüm seanslara sınırsız erişim',
  },
  featureOfflineDownloads: {
    me: 'Preuzimanja za offline',
    en: 'Offline downloads',
    sr: 'Preuzimanja za offline',
    hr: 'Preuzimanja za offline',
    bs: 'Preuzimanja za offline',
    sl: 'Prenosi brez povezave',
    mk: 'Преземања за офлајн',
    sq: 'Shkarkime për jashtë linjës',
    bg: 'Офлайн изтегляния',
    ro: 'Descărcări offline',
    el: 'Λήψεις εκτός σύνδεσης',
    tr: 'Çevrimdışı indirmeler',
  },
  featureExclusiveContent: {
    me: 'Ekskluzivni premium sadržaj',
    en: 'Exclusive premium content',
    sr: 'Ekskluzivni premium sadržaj',
    hr: 'Ekskluzivni premium sadržaj',
    bs: 'Ekskluzivni premium sadržaj',
    sl: 'Ekskluzivna premium vsebina',
    mk: 'Ексклузивна премиум содржина',
    sq: 'Përmbajtje ekskluzive premium',
    bg: 'Ексклузивно премиум съдържание',
    ro: 'Conținut premium exclusiv',
    el: 'Αποκλειστικό premium περιεχόμενο',
    tr: 'Özel premium içerik',
  },
  featureAdFree: {
    me: 'Iskustvo bez reklama',
    en: 'Ad-free experience',
    sr: 'Iskustvo bez reklama',
    hr: 'Iskustvo bez reklama',
    bs: 'Iskustvo bez reklama',
    sl: 'Izkušnja brez oglasov',
    mk: 'Искуство без реклами',
    sq: 'Përvojë pa reklama',
    bg: 'Изживяване без реклами',
    ro: 'Experiență fără reclame',
    el: 'Εμπειρία χωρίς διαφημίσεις',
    tr: 'Reklamsız deneyim',
  },
  featurePendulumTools: {
    me: 'Pendulum i Radiestezija alati',
    en: 'Pendulum & Radiesthesia tools',
    sr: 'Pendulum i Radiestezija alati',
    hr: 'Pendulum i Radiestezija alati',
    bs: 'Pendulum i Radiestezija alati',
    sl: 'Nihalo in radiestezijska orodja',
    mk: 'Нишало и радиестезиски алатки',
    sq: 'Mjetet e lavjerrësit dhe radiestezisë',
    bg: 'Инструменти за махало и радиестезия',
    ro: 'Instrumente de pendul și radiestezice',
    el: 'Εργαλεία εκκρεμούς και ραδιαισθησίας',
    tr: 'Sarkaç ve Radyestezi araçları',
  },
  featureJournal: {
    me: 'Dnevnik',
    en: 'Journal',
    sr: 'Dnevnik',
    hr: 'Dnevnik',
    bs: 'Dnevnik',
    sl: 'Dnevnik',
    mk: 'Дневник',
    sq: 'Ditari',
    bg: 'Дневник',
    ro: 'Jurnal',
    el: 'Ημερολόγιο',
    tr: 'Günlük',
  },
  upgradeNow: {
    me: 'Nadogradi Sada',
    en: 'Upgrade Now',
    sr: 'Nadogradi Sada',
    hr: 'Nadogradi Sada',
    bs: 'Nadogradi Sada',
    sl: 'Nadgradi Zdaj',
    mk: 'Надгради Сега',
    sq: 'Përmirëso Tani',
    bg: 'Надгради сега',
    ro: 'Fă upgrade acum',
    el: 'Αναβάθμιση Τώρα',
    tr: 'Şimdi Yükselt',
  },
  maybeLater: {
    me: 'Možda kasnije',
    en: 'Maybe later',
    sr: 'Možda kasnije',
    hr: 'Možda kasnije',
    bs: 'Možda kasnije',
    sl: 'Morda pozneje',
    mk: 'Можеби подоцна',
    sq: 'Ndoshta më vonë',
    bg: 'Може би по-късно',
    ro: 'Poate mai târziu',
    el: 'Ίσως αργότερα',
    tr: 'Belki sonra',
  },
};

/**
 * Get translated upgrade modal string for the given language
 */
export function getUpgradeModalTranslation(key: UpgradeModalTranslationKey, language: Language): string {
  return UPGRADE_MODAL_TRANSLATIONS[key][language];
}

/**
 * Session Flow Modal translation keys
 */
export type SessionFlowTranslationKey =
  | 'setIntention'
  | 'intentionDesc'
  | 'intentionPlaceholder'
  | 'enableMusic'
  | 'musicVolume'
  | 'begin'
  | 'skip'
  | 'reflection'
  | 'reflectionDesc'
  | 'howFeeling'
  | 'feelingPlaceholder'
  | 'insights'
  | 'insightsPlaceholder'
  | 'gratitude'
  | 'gratitudePlaceholder'
  | 'complete'
  | 'skipReflection'
  | 'sessionComplete'
  | 'completeDesc'
  | 'newSession'
  | 'close';

/**
 * Session Flow Modal translations for all 12 supported languages
 */
const SESSION_FLOW_TRANSLATIONS: Record<SessionFlowTranslationKey, Record<Language, string>> = {
  setIntention: {
    me: 'Postavi Namjeru',
    en: 'Set Your Intention',
    sr: 'Postavi Nameru',
    hr: 'Postavi Namjeru',
    bs: 'Postavi Namjeru',
    sl: 'Določi Namero',
    mk: 'Постави Намера',
    sq: 'Vendos Qëllimin',
    bg: 'Задай намерение',
    ro: 'Setează Intenția',
    el: 'Όρισε την πρόθεσή σου',
    tr: 'Niyetini belirle',
  },
  intentionDesc: {
    me: 'Odvoji trenutak da postaviš namjeru za ovu sesiju.',
    en: 'Take a moment to set your intention for this session.',
    sr: 'Odvoji trenutak da postaviš nameru za ovu sesiju.',
    hr: 'Odvoji trenutak i postavi namjeru za ovu sesiju.',
    bs: 'Odvoji trenutak da postaviš namjeru za ovu sesiju.',
    sl: 'Vzemi si trenutek in določi namero za to sejo.',
    mk: 'Одвој момент за да ја поставиш својата намера за оваа сесија.',
    sq: 'Merr një moment për të vendosur qëllimin tënd për këtë seancë.',
    bg: 'Отдели момент, за да зададеш намерението си за тази сесия.',
    ro: 'Ia-ți un moment pentru a-ți stabili intenția pentru această sesiune.',
    el: 'Αφιέρωσε λίγο χρόνο για να ορίσεις την πρόθεσή σου για αυτή τη συνεδρία.',
    tr: 'Bu seans için niyetini belirlemek üzere kendine bir an ayır.',
  },
  intentionPlaceholder: {
    me: 'Šta te danas dovodi ovoj praksi?',
    en: 'What brings you to this practice today?',
    sr: 'Šta te danas dovodi ovoj praksi?',
    hr: 'Što te danas dovodi ovoj praksi?',
    bs: 'Šta te danas dovodi ovoj praksi?',
    sl: 'Kaj te danes pripelje k tej praksi?',
    mk: 'Што те донесе до оваа пракса денес?',
    sq: 'Çfarë të sjell sot në këtë praktikë?',
    bg: 'Какво те води до тази практика днес?',
    ro: 'Ce te aduce astăzi la această practică?',
    el: 'Τι σε φέρνει σε αυτή την πρακτική σήμερα;',
    tr: 'Bugün seni bu pratiğe ne getirdi?',
  },
  enableMusic: {
    me: 'Pozadinska muzika',
    en: 'Background Music',
    sr: 'Pozadinska muzika',
    hr: 'Pozadinska glazba',
    bs: 'Pozadinska muzika',
    sl: 'Glasba v ozadju',
    mk: 'Позадинска музика',
    sq: 'Muzikë në sfond',
    bg: 'Фонова музика',
    ro: 'Muzică de fundal',
    el: 'Μουσική υπόκρουση',
    tr: 'Arka Plan Müziği',
  },
  musicVolume: {
    me: 'Jačina muzike',
    en: 'Music Volume',
    sr: 'Jačina muzike',
    hr: 'Glasnoća glazbe',
    bs: 'Jačina muzike',
    sl: 'Glasnost glasbe',
    mk: 'Јачина на музика',
    sq: 'Volumi i muzikës',
    bg: 'Сила на звука на музиката',
    ro: 'Volumul muzicii',
    el: 'Ένταση μουσικής',
    tr: 'Müzik Ses Seviyesi',
  },
  begin: {
    me: 'Započni sesiju',
    en: 'Begin Session',
    sr: 'Započni sesiju',
    hr: 'Započni sesiju',
    bs: 'Započni sesiju',
    sl: 'Začni sejo',
    mk: 'Започни сесија',
    sq: 'Fillo Seancën',
    bg: 'Започни сесия',
    ro: 'Începe Sesiunea',
    el: 'Έναρξη Συνεδρίας',
    tr: 'Seansı Başlat',
  },
  skip: {
    me: 'Preskoči',
    en: 'Skip',
    sr: 'Preskoči',
    hr: 'Preskoči',
    bs: 'Preskoči',
    sl: 'Preskoči',
    mk: 'Прескокни',
    sq: 'Kapërce',
    bg: 'Пропусни',
    ro: 'Sari',
    el: 'Παράλειψη',
    tr: 'Atla',
  },
  reflection: {
    me: 'Refleksija sesije',
    en: 'Session Reflection',
    sr: 'Refleksija sesije',
    hr: 'Refleksija sesije',
    bs: 'Refleksija sesije',
    sl: 'Refleksija seje',
    mk: 'Рефлексија на сесија',
    sq: 'Reflektimi i Seancës',
    bg: 'Размисъл за сесията',
    ro: 'Reflecție asupra Sesiunii',
    el: 'Αναστοχασμός Συνεδρίας',
    tr: 'Seans Yansıması',
  },
  reflectionDesc: {
    me: 'Odvoji trenutak da razmisliš o svom iskustvu.',
    en: 'Take a moment to reflect on your experience.',
    sr: 'Odvoji trenutak da razmisliš o svom iskustvu.',
    hr: 'Odvoji trenutak i razmisli o svom iskustvu.',
    bs: 'Odvoji trenutak da razmisliš o svom iskustvu.',
    sl: 'Vzemi si trenutek za razmislek o svoji izkušnji.',
    mk: 'Одвој момент за да размислиш за своето искуство.',
    sq: 'Merr një moment për të reflektuar mbi përvojën tënde.',
    bg: 'Отдели момент за размисъл върху своето преживяване.',
    ro: 'Ia-ți un moment pentru a reflecta asupra experienței tale.',
    el: 'Αφιέρωσε λίγο χρόνο για να σκεφτείς την εμπειρία σου.',
    tr: 'Deneyimin üzerine düşünmek için kendine bir an ayır.',
  },
  howFeeling: {
    me: 'Kako se osjećaš?',
    en: 'How are you feeling?',
    sr: 'Kako se osećaš?',
    hr: 'Kako se osjećaš?',
    bs: 'Kako se osjećaš?',
    sl: 'Kako se počutiš?',
    mk: 'Како се чувствуваш?',
    sq: 'Si ndihesh?',
    bg: 'Как се чувстваш?',
    ro: 'Cum te simți?',
    el: 'Πώς νιώθεις;',
    tr: 'Nasıl hissediyorsun?',
  },
  feelingPlaceholder: {
    me: 'Opiši svoje trenutno stanje...',
    en: 'Describe your current state...',
    sr: 'Opiši svoje trenutno stanje...',
    hr: 'Opiši svoje trenutno stanje...',
    bs: 'Opiši svoje trenutno stanje...',
    sl: 'Opiši svoje trenutno stanje...',
    mk: 'Опиши ја својата моментална состојба...',
    sq: 'Përshkruaj gjendjen tënde aktuale...',
    bg: 'Опиши текущото си състояние...',
    ro: 'Descrie-ți starea actuală...',
    el: 'Περίγραψε την τρέχουσα κατάστασή σου...',
    tr: 'Şu anki durumunu anlat...',
  },
  insights: {
    me: 'Bilo kakvi uvidi ili otkrivenja?',
    en: 'Any insights or revelations?',
    sr: 'Bilo kakvi uvidi ili otkrivenja?',
    hr: 'Bilo kakvi uvidi ili otkrivenja?',
    bs: 'Bilo kakvi uvidi ili otkrivenja?',
    sl: 'Kakšni uvidi ali razodetja?',
    mk: 'Некакви увиди или откритија?',
    sq: 'Ndonjë njohuri ose zbulime?',
    bg: 'Някакви прозрения или открития?',
    ro: 'Vreo perspectivă sau revelație?',
    el: 'Κάποιες διαπιστώσεις ή αποκαλύψεις;',
    tr: 'Herhangi bir içgörü veya keşif?',
  },
  insightsPlaceholder: {
    me: 'Šta ti je ova sesija otkrila?',
    en: 'What did this session show you?',
    sr: 'Šta ti je ova sesija otkrila?',
    hr: 'Što ti je ova sesija otkrila?',
    bs: 'Šta ti je ova sesija otkrila?',
    sl: 'Kaj ti je razkrila ta seja?',
    mk: 'Што ти откри оваа сесија?',
    sq: 'Çfarë të zbuloi kjo seancë?',
    bg: 'Какво ти разкри тази сесия?',
    ro: 'Ce ți-a dezvăluit această sesiune?',
    el: 'Τι σου αποκάλυψε αυτή η συνεδρία;',
    tr: 'Bu seans sana neyi gösterdi?',
  },
  gratitude: {
    me: 'Za šta osjećaš zahvalnost?',
    en: 'What are you grateful for?',
    sr: 'Za šta osećaš zahvalnost?',
    hr: 'Za što osjećaš zahvalnost?',
    bs: 'Za šta osjećaš zahvalnost?',
    sl: 'Za kaj čutiš hvaležnost?',
    mk: 'За што чувствуваш благодарност?',
    sq: 'Për çfarë ndjen mirënjohje?',
    bg: 'За какво изпитваш благодарност?',
    ro: 'Pentru ce simți recunoștință?',
    el: 'Για τι νιώθεις ευγνωμοσύνη;',
    tr: 'Ne için minnet duyuyorsun?',
  },
  gratitudePlaceholder: {
    me: 'Izrazi svoju zahvalnost...',
    en: 'Express your gratitude...',
    sr: 'Izrazi svoju zahvalnost...',
    hr: 'Izrazi svoju zahvalnost...',
    bs: 'Izrazi svoju zahvalnost...',
    sl: 'Izrazi svojo hvaležnost...',
    mk: 'Изрази ја својата благодарност...',
    sq: 'Shpreh mirënjohjen tënde...',
    bg: 'Изрази благодарността си...',
    ro: 'Exprimă-ți recunoștința...',
    el: 'Έκφρασε την ευγνωμοσύνη σου...',
    tr: 'Minnettarlığını ifade et...',
  },
  complete: {
    me: 'Završi sesiju',
    en: 'Complete Session',
    sr: 'Završi sesiju',
    hr: 'Završi sesiju',
    bs: 'Završi sesiju',
    sl: 'Zaključi sejo',
    mk: 'Заврши сесија',
    sq: 'Përfundo Seancën',
    bg: 'Завърши сесия',
    ro: 'Finalizează Sesiunea',
    el: 'Ολοκλήρωση Συνεδρίας',
    tr: 'Seansı Tamamla',
  },
  skipReflection: {
    me: 'Preskoči refleksiju',
    en: 'Skip Reflection',
    sr: 'Preskoči refleksiju',
    hr: 'Preskoči refleksiju',
    bs: 'Preskoči refleksiju',
    sl: 'Preskoči refleksijo',
    mk: 'Прескокни рефлексија',
    sq: 'Kapërce Reflektimin',
    bg: 'Пропусни размисъла',
    ro: 'Sari Reflecția',
    el: 'Παράλειψη Αναστοχασμού',
    tr: 'Yansımayı Atla',
  },
  sessionComplete: {
    me: 'Sesija završena',
    en: 'Session Complete',
    sr: 'Sesija završena',
    hr: 'Sesija završena',
    bs: 'Sesija završena',
    sl: 'Seja zaključena',
    mk: 'Сесијата е завршена',
    sq: 'Seanca e Përfunduar',
    bg: 'Сесията е завършена',
    ro: 'Sesiune Finalizată',
    el: 'Η Συνεδρία Ολοκληρώθηκε',
    tr: 'Seans Tamamlandı',
  },
  completeDesc: {
    me: 'Odlično! Tvoja sesija je zabilježena.',
    en: 'Well done! Your session has been recorded.',
    sr: 'Odlično! Tvoja sesija je zabeležena.',
    hr: 'Odlično! Tvoja sesija je zabilježena.',
    bs: 'Odlično! Tvoja sesija je zabilježena.',
    sl: 'Odlično! Tvoja seja je zabeležena.',
    mk: 'Одлично! Твојата сесија е зачувана.',
    sq: 'Shkëlqyeshëm! Seanca jote është regjistruar.',
    bg: 'Браво! Сесията ти е записана.',
    ro: 'Foarte bine! Sesiunea ta a fost înregistrată.',
    el: 'Μπράβο! Η συνεδρία σου καταγράφηκε.',
    tr: 'Harika! Seansın kaydedildi.',
  },
  newSession: {
    me: 'Nova sesija',
    en: 'New Session',
    sr: 'Nova sesija',
    hr: 'Nova sesija',
    bs: 'Nova sesija',
    sl: 'Nova seja',
    mk: 'Нова сесија',
    sq: 'Seancë e Re',
    bg: 'Нова сесия',
    ro: 'Sesiune Nouă',
    el: 'Νέα Συνεδρία',
    tr: 'Yeni Seans',
  },
  close: {
    me: 'Zatvori',
    en: 'Close',
    sr: 'Zatvori',
    hr: 'Zatvori',
    bs: 'Zatvori',
    sl: 'Zapri',
    mk: 'Затвори',
    sq: 'Mbyll',
    bg: 'Затвори',
    ro: 'Închide',
    el: 'Κλείσιμο',
    tr: 'Kapat',
  },
};

/**
 * Get translated session flow modal string for the given language
 */
export function getSessionFlowTranslation(key: SessionFlowTranslationKey, language: Language): string {
  return SESSION_FLOW_TRANSLATIONS[key][language];
}

/**
 * Session Filters translation keys
 */
export type SessionFiltersTranslationKey =
  | 'searchPlaceholder'
  | 'categories'
  | 'duration'
  | 'durationAll'
  | 'durationSos'
  | 'durationStandard'
  | 'durationDeep';

/**
 * Session Filters translations for all 12 supported languages
 */
const SESSION_FILTERS_TRANSLATIONS: Record<SessionFiltersTranslationKey, Record<Language, string>> = {
  searchPlaceholder: {
    me: 'Pretraži sesije...',
    en: 'Search sessions...',
    sr: 'Pretraži sesije...',
    hr: 'Pretraži sesije...',
    bs: 'Pretraži sesije...',
    sl: 'Išči seje...',
    mk: 'Пребарај сесии...',
    sq: 'Kërko seancat...',
    bg: 'Търсене на сесии...',
    ro: 'Caută sesiuni...',
    el: 'Αναζήτηση συνεδριών...',
    tr: 'Seansları ara...',
  },
  categories: {
    me: 'Kategorije',
    en: 'Categories',
    sr: 'Kategorije',
    hr: 'Kategorije',
    bs: 'Kategorije',
    sl: 'Kategorije',
    mk: 'Категории',
    sq: 'Kategoritë',
    bg: 'Категории',
    ro: 'Categorii',
    el: 'Κατηγορίες',
    tr: 'Kategoriler',
  },
  duration: {
    me: 'Trajanje',
    en: 'Duration',
    sr: 'Trajanje',
    hr: 'Trajanje',
    bs: 'Trajanje',
    sl: 'Trajanje',
    mk: 'Траење',
    sq: 'Kohëzgjatja',
    bg: 'Продължителност',
    ro: 'Durată',
    el: 'Διάρκεια',
    tr: 'Süre',
  },
  durationAll: {
    me: 'Sve',
    en: 'All',
    sr: 'Sve',
    hr: 'Sve',
    bs: 'Sve',
    sl: 'Vse',
    mk: 'Сите',
    sq: 'Të gjitha',
    bg: 'Всички',
    ro: 'Toate',
    el: 'Όλες',
    tr: 'Tümü',
  },
  durationSos: {
    me: 'SOS',
    en: 'SOS',
    sr: 'SOS',
    hr: 'SOS',
    bs: 'SOS',
    sl: 'SOS',
    mk: 'SOS',
    sq: 'SOS',
    bg: 'SOS',
    ro: 'SOS',
    el: 'SOS',
    tr: 'SOS',
  },
  durationStandard: {
    me: 'Standardne',
    en: 'Standard',
    sr: 'Standardne',
    hr: 'Standardne',
    bs: 'Standardne',
    sl: 'Standardne',
    mk: 'Стандардни',
    sq: 'Standarde',
    bg: 'Стандартни',
    ro: 'Standard',
    el: 'Τυπικές',
    tr: 'Standart',
  },
  durationDeep: {
    me: 'Produbljene',
    en: 'Extended',
    sr: 'Produbljene',
    hr: 'Produbljene',
    bs: 'Produbljene',
    sl: 'Poglobljene',
    mk: 'Продлабочени',
    sq: 'Të thelluara',
    bg: 'Задълбочени',
    ro: 'Aprofundate',
    el: 'Σε βάθος',
    tr: 'Derin',
  },
};

/**
 * Get translated session filters string for the given language
 */
export function getSessionFiltersTranslation(key: SessionFiltersTranslationKey, language: Language): string {
  return SESSION_FILTERS_TRANSLATIONS[key][language];
}

// ============================================================================
// AUDIO PLAYER TRANSLATIONS
// ============================================================================

export type AudioPlayerTranslationKey =
  | 'restart'
  | 'skipBack'
  | 'play'
  | 'pause'
  | 'skipForward'
  | 'mute'
  | 'unmute'
  | 'volume'
  | 'closePlayer';

/**
 * Audio Player translations for all 12 supported languages
 */
const AUDIO_PLAYER_TRANSLATIONS: Record<AudioPlayerTranslationKey, Record<Language, string>> = {
  restart: {
    me: 'Ponovo pokreni',
    en: 'Restart',
    sr: 'Ponovo pokreni',
    hr: 'Ponovo pokreni',
    bs: 'Ponovo pokreni',
    sl: 'Ponovno zaženi',
    mk: 'Рестартирај',
    sq: 'Rifillo',
    bg: 'Рестартирай',
    ro: 'Repornește',
    el: 'Επανεκκίνηση',
    tr: 'Yeniden başlat',
  },
  skipBack: {
    me: 'Vrati 10 sekundi',
    en: 'Skip back 10 seconds',
    sr: 'Vrati 10 sekundi',
    hr: 'Vrati 10 sekundi',
    bs: 'Vrati 10 sekundi',
    sl: 'Nazaj 10 sekund',
    mk: 'Врати 10 секунди',
    sq: 'Kthehu 10 sekonda',
    bg: 'Назад 10 секунди',
    ro: 'Înapoi 10 secunde',
    el: 'Πίσω 10 δευτερόλεπτα',
    tr: '10 saniye geri',
  },
  play: {
    me: 'Pokreni',
    en: 'Play',
    sr: 'Pokreni',
    hr: 'Pokreni',
    bs: 'Pokreni',
    sl: 'Predvajaj',
    mk: 'Пушти',
    sq: 'Luaj',
    bg: 'Пусни',
    ro: 'Redare',
    el: 'Αναπαραγωγή',
    tr: 'Oynat',
  },
  pause: {
    me: 'Pauziraj',
    en: 'Pause',
    sr: 'Pauziraj',
    hr: 'Pauziraj',
    bs: 'Pauziraj',
    sl: 'Premor',
    mk: 'Пауза',
    sq: 'Pauzë',
    bg: 'Пауза',
    ro: 'Pauză',
    el: 'Παύση',
    tr: 'Duraklat',
  },
  skipForward: {
    me: 'Naprijed 10 sekundi',
    en: 'Skip forward 10 seconds',
    sr: 'Napred 10 sekundi',
    hr: 'Naprijed 10 sekundi',
    bs: 'Naprijed 10 sekundi',
    sl: 'Naprej 10 sekund',
    mk: 'Напред 10 секунди',
    sq: 'Përpara 10 sekonda',
    bg: 'Напред 10 секунди',
    ro: 'Înainte 10 secunde',
    el: 'Μπροστά 10 δευτερόλεπτα',
    tr: '10 saniye ileri',
  },
  mute: {
    me: 'Isključi zvuk',
    en: 'Mute',
    sr: 'Isključi zvuk',
    hr: 'Isključi zvuk',
    bs: 'Isključi zvuk',
    sl: 'Utišaj',
    mk: 'Исклучи звук',
    sq: 'Hiq zërin',
    bg: 'Без звук',
    ro: 'Dezactivează sunetul',
    el: 'Σίγαση',
    tr: 'Sesi kapat',
  },
  unmute: {
    me: 'Uključi zvuk',
    en: 'Unmute',
    sr: 'Uključi zvuk',
    hr: 'Uključi zvuk',
    bs: 'Uključi zvuk',
    sl: 'Povrni zvok',
    mk: 'Вклучи звук',
    sq: 'Aktivizo zërin',
    bg: 'Със звук',
    ro: 'Activează sunetul',
    el: 'Κατάργηση σίγασης',
    tr: 'Sesi aç',
  },
  volume: {
    me: 'Glasnoća',
    en: 'Volume',
    sr: 'Glasnoća',
    hr: 'Glasnoća',
    bs: 'Glasnoća',
    sl: 'Glasnost',
    mk: 'Гласност',
    sq: 'Volumi',
    bg: 'Сила на звука',
    ro: 'Volum',
    el: 'Ένταση',
    tr: 'Ses',
  },
  closePlayer: {
    me: 'Zatvori player',
    en: 'Close player',
    sr: 'Zatvori plejer',
    hr: 'Zatvori player',
    bs: 'Zatvori player',
    sl: 'Zapri predvajalnik',
    mk: 'Затвори плеер',
    sq: 'Mbyll luajtësin',
    bg: 'Затвори плейъра',
    ro: 'Închide playerul',
    el: 'Κλείσιμο player',
    tr: 'Oynatıcıyı kapat',
  },
};

/**
 * Get translated audio player string for the given language
 */
export function getAudioPlayerTranslation(key: AudioPlayerTranslationKey, language: Language): string {
  return AUDIO_PLAYER_TRANSLATIONS[key][language];
}

// ============================================================================
// APP PAGE TRANSLATIONS
// ============================================================================

export type AppPageTranslationKey =
  | 'about'
  | 'contact'
  | 'privacyPolicy'
  | 'termsOfService'
  | 'faq'
  | 'helpCenter';

/**
 * App page translations for all 12 supported languages
 */
const APP_PAGE_TRANSLATIONS: Record<AppPageTranslationKey, Record<Language, string>> = {
  about: {
    me: 'O nama',
    en: 'About',
    sr: 'O nama',
    hr: 'O nama',
    bs: 'O nama',
    sl: 'O nas',
    mk: 'За нас',
    sq: 'Rreth nesh',
    bg: 'За нас',
    ro: 'Despre noi',
    el: 'Σχετικά',
    tr: 'Hakkımızda',
  },
  contact: {
    me: 'Kontakt',
    en: 'Contact',
    sr: 'Kontakt',
    hr: 'Kontakt',
    bs: 'Kontakt',
    sl: 'Kontakt',
    mk: 'Контакт',
    sq: 'Kontakt',
    bg: 'Контакт',
    ro: 'Contact',
    el: 'Επικοινωνία',
    tr: 'İletişim',
  },
  privacyPolicy: {
    me: 'Politika privatnosti',
    en: 'Privacy Policy',
    sr: 'Politika privatnosti',
    hr: 'Politika privatnosti',
    bs: 'Politika privatnosti',
    sl: 'Politika zasebnosti',
    mk: 'Политика за приватност',
    sq: 'Politika e privatësisë',
    bg: 'Политика за поверителност',
    ro: 'Politica de confidențialitate',
    el: 'Πολιτική απορρήτου',
    tr: 'Gizlilik Politikası',
  },
  termsOfService: {
    me: 'Uslovi korištenja',
    en: 'Terms of Service',
    sr: 'Uslovi korišćenja',
    hr: 'Uvjeti korištenja',
    bs: 'Uslovi korištenja',
    sl: 'Pogoji uporabe',
    mk: 'Услови за користење',
    sq: 'Kushtet e shërbimit',
    bg: 'Условия за ползване',
    ro: 'Termeni și condiții',
    el: 'Όροι χρήσης',
    tr: 'Hizmet Şartları',
  },
  faq: {
    me: 'Česta pitanja',
    en: 'FAQ',
    sr: 'Česta pitanja',
    hr: 'Česta pitanja',
    bs: 'Česta pitanja',
    sl: 'Pogosta vprašanja',
    mk: 'Најчесто поставувани прашања',
    sq: 'Pyetjet e shpeshta',
    bg: 'Често задавани въпроси',
    ro: 'Întrebări frecvente',
    el: 'Συχνές ερωτήσεις',
    tr: 'SSS',
  },
  helpCenter: {
    me: 'Centar za pomoć',
    en: 'Help Center',
    sr: 'Centar za pomoć',
    hr: 'Centar za pomoć',
    bs: 'Centar za pomoć',
    sl: 'Center za pomoč',
    mk: 'Центар за помош',
    sq: 'Qendra e ndihmës',
    bg: 'Помощен център',
    ro: 'Centru de ajutor',
    el: 'Κέντρο βοήθειας',
    tr: 'Yardım Merkezi',
  },
};

/**
 * Get translated app page string for the given language
 */
export function getAppPageTranslation(key: AppPageTranslationKey, language: Language): string {
  return APP_PAGE_TRANSLATIONS[key][language];
}

// ============================================================================
// JOURNAL EXPORT TRANSLATIONS
// ============================================================================

export type JournalExportTranslationKey =
  | 'exportDate'
  | 'exportMoodBefore'
  | 'exportMoodAfter'
  | 'exportNA';

/**
 * Journal export translations for all 12 supported languages
 */
const JOURNAL_EXPORT_TRANSLATIONS: Record<JournalExportTranslationKey, Record<Language, string>> = {
  exportDate: {
    me: 'Datum',
    en: 'Date',
    sr: 'Datum',
    hr: 'Datum',
    bs: 'Datum',
    sl: 'Datum',
    mk: 'Датум',
    sq: 'Data',
    bg: 'Дата',
    ro: 'Data',
    el: 'Ημερομηνία',
    tr: 'Tarih',
  },
  exportMoodBefore: {
    me: 'Raspoloženje prije',
    en: 'Mood Before',
    sr: 'Raspoloženje pre',
    hr: 'Raspoloženje prije',
    bs: 'Raspoloženje prije',
    sl: 'Razpoloženje pred',
    mk: 'Расположение пред',
    sq: 'Gjendja para',
    bg: 'Настроение преди',
    ro: 'Dispoziție înainte',
    el: 'Διάθεση πριν',
    tr: 'Önceki ruh hali',
  },
  exportMoodAfter: {
    me: 'Raspoloženje poslije',
    en: 'Mood After',
    sr: 'Raspoloženje posle',
    hr: 'Raspoloženje poslije',
    bs: 'Raspoloženje poslije',
    sl: 'Razpoloženje po',
    mk: 'Расположение после',
    sq: 'Gjendja pas',
    bg: 'Настроение след',
    ro: 'Dispoziție după',
    el: 'Διάθεση μετά',
    tr: 'Sonraki ruh hali',
  },
  exportNA: {
    me: 'N/A',
    en: 'N/A',
    sr: 'N/A',
    hr: 'N/A',
    bs: 'N/A',
    sl: 'N/A',
    mk: 'N/A',
    sq: 'N/A',
    bg: 'N/A',
    ro: 'N/A',
    el: 'Δ/Υ',
    tr: 'Yok',
  },
};

/**
 * Get translated journal export string for the given language
 */
export function getJournalExportTranslation(key: JournalExportTranslationKey, language: Language): string {
  return JOURNAL_EXPORT_TRANSLATIONS[key][language];
}

/**
 * Tools card in Profil → Alati (A1 renders it; Pendulum moves there from the bottom nav)
 */
export type ToolsTranslationKey = 'heading' | 'pendulumTitle' | 'pendulumDesc';

/**
 * Tools translations for all 12 supported languages
 */
const TOOLS_TRANSLATIONS: Record<ToolsTranslationKey, Record<Language, string>> = {
  heading: {
    me: 'Alati',
    en: 'Tools',
    sr: 'Alati',
    hr: 'Alati',
    bs: 'Alati',
    sl: 'Orodja',
    mk: 'Алатки',
    sq: 'Mjete',
    bg: 'Инструменти',
    ro: 'Instrumente',
    el: 'Εργαλεία',
    tr: 'Araçlar',
  },
  pendulumTitle: {
    me: 'Pendulum',
    en: 'Pendulum',
    sr: 'Pendulum',
    hr: 'Pendulum',
    bs: 'Pendulum',
    sl: 'Nihalo',
    mk: 'Нишало',
    sq: 'Lavjerrësi',
    bg: 'Махало',
    ro: 'Pendul',
    el: 'Εκκρεμές',
    tr: 'Sarkaç',
  },
  pendulumDesc: {
    me: 'Ploča za rad sa tvojim pendulumom, uz pitanje ili namjeru.',
    en: 'A board to use with your own pendulum, with a question or intention in mind.',
    sr: 'Ploča za rad sa tvojim pendulumom, uz pitanje ili nameru.',
    hr: 'Ploča za rad s tvojim pendulumom, uz pitanje ili namjeru.',
    bs: 'Ploča za rad s tvojim pendulumom, uz pitanje ili namjeru.',
    sl: 'Plošča za delo s tvojim nihalom, ob vprašanju ali nameri.',
    mk: 'Табла за работа со твоето нишало, со прашање или намера.',
    sq: 'Tabelë për të punuar me lavjerrësin tënd, me një pyetje ose qëllim.',
    bg: 'Дъска за работа с твоето махало, с въпрос или намерение.',
    ro: 'O tablă pentru pendulul tău, cu o întrebare sau o intenție.',
    el: 'Ένας πίνακας για το εκκρεμές σου, με μια ερώτηση ή πρόθεση.',
    tr: 'Kendi sarkacınla, bir soru ya da niyetle kullanabileceğin bir tahta.',
  },
};

/**
 * Get translated tools-card string for the given language
 */
export function getToolsTranslation(key: ToolsTranslationKey, language: Language): string {
  return TOOLS_TRANSLATIONS[key][language];
}

/**
 * In-app book (ebook-plan § 4.3): the Početna card, the Profil → Alati tile and the /book overview.
 */
export type BookTranslationKey =
  | 'cardTitle'
  | 'cardText'
  | 'toolTitle'
  | 'languageNote'
  | 'startReading'
  | 'continueReading'
  | 'progress'
  | 'coverAlt'
  | 'signInTitle'
  | 'signInText'
  | 'signIn'
  | 'loading'
  | 'loadError'
  | 'retry'
  | 'unavailable'
  | 'locked';

/**
 * Book card and overview strings for all 12 supported languages. The book itself is in Montenegrin (D9).
 */
const BOOK_TRANSLATIONS: Record<BookTranslationKey, Record<Language, string>> = {
  cardTitle: {
    me: 'Knjiga „Lukaaji metoda“',
    en: 'The book “Lukaaji metoda”',
    sr: 'Knjiga „Lukaaji metoda“',
    hr: 'Knjiga „Lukaaji metoda“',
    bs: 'Knjiga „Lukaaji metoda“',
    sl: 'Knjiga »Lukaaji metoda«',
    mk: 'Книгата „Lukaaji metoda“',
    sq: 'Libri „Lukaaji metoda“',
    bg: 'Книгата „Lukaaji metoda“',
    ro: 'Cartea „Lukaaji metoda”',
    el: 'Το βιβλίο «Lukaaji metoda»',
    tr: '“Lukaaji metoda” kitabı',
  },
  cardText: {
    me: 'Čitaj knjigu Luke Đurovića u aplikaciji. Odlomak sa početka knjige je besplatan.',
    en: "Read Luka Đurović's book in the app. An excerpt from the beginning of the book is free.",
    sr: 'Čitaj knjigu Luke Đurovića u aplikaciji. Odlomak sa početka knjige je besplatan.',
    hr: 'Čitaj knjigu Luke Đurovića u aplikaciji. Ulomak s početka knjige je besplatan.',
    bs: 'Čitaj knjigu Luke Đurovića u aplikaciji. Odlomak s početka knjige je besplatan.',
    sl: 'Beri knjigo Luke Đurovića v aplikaciji. Odlomek z začetka knjige je brezplačen.',
    mk: 'Читај ја книгата на Luka Đurović во апликацијата. Извадокот од почетокот на книгата е бесплатен.',
    sq: 'Lexo librin e autorit Luka Đurović në aplikacion. Një fragment nga fillimi i librit është falas.',
    bg: 'Чети книгата на Luka Đurović в приложението. Откъсът от началото на книгата е безплатен.',
    ro: 'Citește cartea lui Luka Đurović în aplicație. Un fragment de la începutul cărții este gratuit.',
    el: 'Διάβασε το βιβλίο του Luka Đurović στην εφαρμογή. Ένα απόσπασμα από την αρχή του βιβλίου είναι δωρεάν.',
    tr: "Luka Đurović'in kitabını uygulamada oku. Kitabın başından bir bölüm ücretsiz.",
  },
  toolTitle: {
    me: 'Knjiga',
    en: 'Book',
    sr: 'Knjiga',
    hr: 'Knjiga',
    bs: 'Knjiga',
    sl: 'Knjiga',
    mk: 'Книга',
    sq: 'Libri',
    bg: 'Книга',
    ro: 'Carte',
    el: 'Βιβλίο',
    tr: 'Kitap',
  },
  languageNote: {
    me: 'Knjiga je na crnogorskom jeziku.',
    en: 'The book is in Montenegrin.',
    sr: 'Knjiga je na crnogorskom jeziku.',
    hr: 'Knjiga je na crnogorskom jeziku.',
    bs: 'Knjiga je na crnogorskom jeziku.',
    sl: 'Knjiga je v črnogorščini.',
    mk: 'Книгата е на црногорски јазик.',
    sq: 'Libri është në gjuhën malazeze.',
    bg: 'Книгата е на черногорски език.',
    ro: 'Cartea este în limba muntenegreană.',
    el: 'Το βιβλίο είναι στα μαυροβουνιακά.',
    tr: 'Kitap Karadağca.',
  },
  startReading: {
    me: 'Počni čitanje',
    en: 'Start reading',
    sr: 'Počni čitanje',
    hr: 'Počni čitati',
    bs: 'Počni čitanje',
    sl: 'Začni brati',
    mk: 'Почни да читаш',
    sq: 'Fillo leximin',
    bg: 'Започни четенето',
    ro: 'Începe lectura',
    el: 'Ξεκίνα την ανάγνωση',
    tr: 'Okumaya başla',
  },
  continueReading: {
    me: 'Nastavi čitanje',
    en: 'Continue reading',
    sr: 'Nastavi čitanje',
    hr: 'Nastavi čitati',
    bs: 'Nastavi čitanje',
    sl: 'Nadaljuj z branjem',
    mk: 'Продолжи со читање',
    sq: 'Vazhdo leximin',
    bg: 'Продължи четенето',
    ro: 'Continuă lectura',
    el: 'Συνέχισε την ανάγνωση',
    tr: 'Okumaya devam et',
  },
  progress: {
    me: 'Pročitano: {percent} %',
    en: 'Read: {percent}%',
    sr: 'Pročitano: {percent} %',
    hr: 'Pročitano: {percent} %',
    bs: 'Pročitano: {percent} %',
    sl: 'Prebrano: {percent} %',
    mk: 'Прочитано: {percent} %',
    sq: 'Lexuar: {percent}%',
    bg: 'Прочетено: {percent} %',
    ro: 'Citit: {percent} %',
    el: 'Διαβάστηκε: {percent}%',
    tr: 'Okunan: %{percent}',
  },
  coverAlt: {
    me: 'Naslovnica knjige „Lukaaji metoda“ Luke Đurovića: čovjek ide ka svjetlu kroz zlatne linije nalik otisku prsta',
    en: 'Cover of the book “Lukaaji metoda” by Luka Đurović: a person walking towards the light through golden lines like a fingerprint',
    sr: 'Naslovna strana knjige „Lukaaji metoda“ Luke Đurovića: čovek ide ka svetlu kroz zlatne linije nalik otisku prsta',
    hr: 'Naslovnica knjige „Lukaaji metoda“ Luke Đurovića: čovjek ide prema svjetlu kroz zlatne linije nalik otisku prsta',
    bs: 'Naslovnica knjige „Lukaaji metoda“ Luke Đurovića: čovjek ide prema svjetlu kroz zlatne linije nalik otisku prsta',
    sl: 'Naslovnica knjige »Lukaaji metoda« Luke Đurovića: človek hodi proti svetlobi med zlatimi črtami, podobnimi prstnemu odtisu',
    mk: 'Корица на книгата „Lukaaji metoda“ од Luka Đurović: човек оди кон светлината низ златни линии слични на отпечаток од прст',
    sq: 'Kopertina e librit „Lukaaji metoda“ nga Luka Đurović: një njeri ecën drejt dritës nëpër vija të arta si gjurmë gishti',
    bg: 'Корица на книгата „Lukaaji metoda“ от Luka Đurović: човек върви към светлината през златни линии като пръстов отпечатък',
    ro: 'Coperta cărții „Lukaaji metoda” de Luka Đurović: un om merge spre lumină printre linii aurii ca o amprentă',
    el: 'Εξώφυλλο του βιβλίου «Lukaaji metoda» του Luka Đurović: ένας άνθρωπος περπατά προς το φως μέσα από χρυσές γραμμές σαν δακτυλικό αποτύπωμα',
    tr: "Luka Đurović'in “Lukaaji metoda” kitabının kapağı: parmak izine benzeyen altın çizgilerin arasından ışığa doğru yürüyen bir insan",
  },
  signInTitle: {
    me: 'Prijavi se da čitaš knjigu',
    en: 'Sign in to read the book',
    sr: 'Prijavi se da čitaš knjigu',
    hr: 'Prijavi se za čitanje knjige',
    bs: 'Prijavi se da čitaš knjigu',
    sl: 'Prijavi se za branje knjige',
    mk: 'Најави се за да ја читаш книгата',
    sq: 'Identifikohu për të lexuar librin',
    bg: 'Влез, за да четеш книгата',
    ro: 'Conectează-te ca să citești cartea',
    el: 'Συνδέσου για να διαβάσεις το βιβλίο',
    tr: 'Kitabı okumak için giriş yap',
  },
  signInText: {
    me: 'Knjigu čitaš u aplikaciji, sa svojim nalogom.',
    en: 'You read the book in the app, with your account.',
    sr: 'Knjigu čitaš u aplikaciji, sa svojim nalogom.',
    hr: 'Knjigu čitaš u aplikaciji, sa svojim računom.',
    bs: 'Knjigu čitaš u aplikaciji, sa svojim računom.',
    sl: 'Knjigo bereš v aplikaciji, s svojim računom.',
    mk: 'Книгата ја читаш во апликацијата, со својата сметка.',
    sq: 'Librin e lexon në aplikacion, me llogarinë tënde.',
    bg: 'Книгата се чете в приложението, с твоя профил.',
    ro: 'Cartea o citești în aplicație, cu contul tău.',
    el: 'Το βιβλίο το διαβάζεις στην εφαρμογή, με τον λογαριασμό σου.',
    tr: 'Kitabı uygulamada, kendi hesabınla okursun.',
  },
  signIn: {
    me: 'Prijavi se',
    en: 'Sign In',
    sr: 'Prijavi se',
    hr: 'Prijavi se',
    bs: 'Prijavi se',
    sl: 'Prijava',
    mk: 'Најави се',
    sq: 'Identifikohu',
    bg: 'Вход',
    ro: 'Conectare',
    el: 'Σύνδεση',
    tr: 'Giriş Yap',
  },
  loading: {
    me: 'Učitavanje…',
    en: 'Loading…',
    sr: 'Učitavanje…',
    hr: 'Učitavanje…',
    bs: 'Učitavanje…',
    sl: 'Nalaganje …',
    mk: 'Се вчитува…',
    sq: 'Po ngarkohet…',
    bg: 'Зарежда се…',
    ro: 'Se încarcă…',
    el: 'Φόρτωση…',
    tr: 'Yükleniyor…',
  },
  loadError: {
    me: 'Knjigu trenutno ne možemo učitati.',
    en: "We can't load the book right now.",
    sr: 'Knjigu trenutno ne možemo da učitamo.',
    hr: 'Knjigu trenutačno ne možemo učitati.',
    bs: 'Knjigu trenutno ne možemo učitati.',
    sl: 'Knjige trenutno ne moremo naložiti.',
    mk: 'Книгата во моментов не можеме да ја вчитаме.',
    sq: 'Për momentin nuk mund ta ngarkojmë librin.',
    bg: 'В момента не можем да заредим книгата.',
    ro: 'Nu putem încărca acum cartea.',
    el: 'Δεν μπορούμε να φορτώσουμε το βιβλίο αυτή τη στιγμή.',
    tr: 'Kitabı şu anda yükleyemiyoruz.',
  },
  retry: {
    me: 'Pokušaj ponovo',
    en: 'Try again',
    sr: 'Pokušaj ponovo',
    hr: 'Pokušaj ponovno',
    bs: 'Pokušaj ponovo',
    sl: 'Poskusi znova',
    mk: 'Обиди се повторно',
    sq: 'Provo përsëri',
    bg: 'Опитай отново',
    ro: 'Încearcă din nou',
    el: 'Δοκίμασε ξανά',
    tr: 'Tekrar dene',
  },
  unavailable: {
    me: 'Knjiga još nije dostupna u aplikaciji.',
    en: 'The book is not available in the app yet.',
    sr: 'Knjiga još nije dostupna u aplikaciji.',
    hr: 'Knjiga još nije dostupna u aplikaciji.',
    bs: 'Knjiga još nije dostupna u aplikaciji.',
    sl: 'Knjiga v aplikaciji še ni na voljo.',
    mk: 'Книгата сè уште не е достапна во апликацијата.',
    sq: 'Libri ende nuk është i disponueshëm në aplikacion.',
    bg: 'Книгата все още не е налична в приложението.',
    ro: 'Cartea nu este încă disponibilă în aplicație.',
    el: 'Το βιβλίο δεν είναι ακόμη διαθέσιμο στην εφαρμογή.',
    tr: 'Kitap henüz uygulamada yok.',
  },
  locked: {
    me: 'Zaključano',
    en: 'Locked',
    sr: 'Zaključano',
    hr: 'Zaključano',
    bs: 'Zaključano',
    sl: 'Zaklenjeno',
    mk: 'Заклучено',
    sq: 'E kyçur',
    bg: 'Заключено',
    ro: 'Blocat',
    el: 'Κλειδωμένο',
    tr: 'Kilitli',
  },
};

export const BOOK_TRANSLATION_KEYS = Object.keys(BOOK_TRANSLATIONS) as BookTranslationKey[];

/**
 * Get a translated book card/overview string; `{percent}` is filled by the caller.
 */
export function getBookTranslation(key: BookTranslationKey, language: Language): string {
  return BOOK_TRANSLATIONS[key][language];
}

/**
 * In-app book reader (ebook-plan § 3.13, § 4.3): reader bars, settings, figure viewer, footnotes, errors, watermark, preview end.
 */
export type BookReaderTranslationKey =
  | 'toc'
  | 'settings'
  | 'backToBook'
  | 'close'
  | 'prevChapter'
  | 'nextChapter'
  | 'chapterNav'
  | 'skipToText'
  | 'theme'
  | 'themeNight'
  | 'themeSepia'
  | 'themeDay'
  | 'fontSize'
  | 'smaller'
  | 'larger'
  | 'lineHeight'
  | 'lineHeightTight'
  | 'lineHeightNormal'
  | 'lineHeightLoose'
  | 'font'
  | 'tapToPage'
  | 'tapToPageHint'
  | 'watermark'
  | 'previewEndTitle'
  | 'previewEndText'
  | 'openFigure'
  | 'figureText'
  | 'zoomIn'
  | 'zoomOut'
  | 'zoomReset'
  | 'footnote'
  | 'backToText'
  | 'rateLimited'
  | 'chapterError'
  | 'table';

/**
 * Book reader strings for all 12 supported languages.
 */
const BOOK_READER_TRANSLATIONS: Record<BookReaderTranslationKey, Record<Language, string>> = {
  toc: {
    me: 'Sadržaj',
    en: 'Contents',
    sr: 'Sadržaj',
    hr: 'Sadržaj',
    bs: 'Sadržaj',
    sl: 'Kazalo',
    mk: 'Содржина',
    sq: 'Përmbajtja',
    bg: 'Съдържание',
    ro: 'Cuprins',
    el: 'Περιεχόμενα',
    tr: 'İçindekiler',
  },
  settings: {
    me: 'Podešavanja čitanja',
    en: 'Reading settings',
    sr: 'Podešavanja čitanja',
    hr: 'Postavke čitanja',
    bs: 'Postavke čitanja',
    sl: 'Nastavitve branja',
    mk: 'Поставки за читање',
    sq: 'Cilësimet e leximit',
    bg: 'Настройки за четене',
    ro: 'Setări de lectură',
    el: 'Ρυθμίσεις ανάγνωσης',
    tr: 'Okuma ayarları',
  },
  backToBook: {
    me: 'Nazad na knjigu',
    en: 'Back to the book',
    sr: 'Nazad na knjigu',
    hr: 'Natrag na knjigu',
    bs: 'Nazad na knjigu',
    sl: 'Nazaj na knjigo',
    mk: 'Назад кон книгата',
    sq: 'Kthehu te libri',
    bg: 'Обратно към книгата',
    ro: 'Înapoi la carte',
    el: 'Πίσω στο βιβλίο',
    tr: 'Kitaba dön',
  },
  close: {
    me: 'Zatvori',
    en: 'Close',
    sr: 'Zatvori',
    hr: 'Zatvori',
    bs: 'Zatvori',
    sl: 'Zapri',
    mk: 'Затвори',
    sq: 'Mbyll',
    bg: 'Затвори',
    ro: 'Închide',
    el: 'Κλείσιμο',
    tr: 'Kapat',
  },
  prevChapter: {
    me: 'Prethodno poglavlje',
    en: 'Previous chapter',
    sr: 'Prethodno poglavlje',
    hr: 'Prethodno poglavlje',
    bs: 'Prethodno poglavlje',
    sl: 'Prejšnje poglavje',
    mk: 'Претходно поглавје',
    sq: 'Kapitulli i mëparshëm',
    bg: 'Предишна глава',
    ro: 'Capitolul anterior',
    el: 'Προηγούμενο κεφάλαιο',
    tr: 'Önceki bölüm',
  },
  nextChapter: {
    me: 'Sljedeće poglavlje',
    en: 'Next chapter',
    sr: 'Sledeće poglavlje',
    hr: 'Sljedeće poglavlje',
    bs: 'Sljedeće poglavlje',
    sl: 'Naslednje poglavje',
    mk: 'Следно поглавје',
    sq: 'Kapitulli tjetër',
    bg: 'Следваща глава',
    ro: 'Capitolul următor',
    el: 'Επόμενο κεφάλαιο',
    tr: 'Sonraki bölüm',
  },
  chapterNav: {
    me: 'Kretanje kroz knjigu',
    en: 'Book navigation',
    sr: 'Kretanje kroz knjigu',
    hr: 'Kretanje kroz knjigu',
    bs: 'Kretanje kroz knjigu',
    sl: 'Premikanje po knjigi',
    mk: 'Движење низ книгата',
    sq: 'Lëvizja në libër',
    bg: 'Придвижване в книгата',
    ro: 'Navigare în carte',
    el: 'Πλοήγηση στο βιβλίο',
    tr: 'Kitapta gezinme',
  },
  skipToText: {
    me: 'Preskoči na tekst',
    en: 'Skip to the text',
    sr: 'Preskoči na tekst',
    hr: 'Preskoči na tekst',
    bs: 'Preskoči na tekst',
    sl: 'Preskoči na besedilo',
    mk: 'Прескокни до текстот',
    sq: 'Kalo te teksti',
    bg: 'Към текста',
    ro: 'Sari la text',
    el: 'Μετάβαση στο κείμενο',
    tr: 'Metne geç',
  },
  theme: {
    me: 'Tema',
    en: 'Theme',
    sr: 'Tema',
    hr: 'Tema',
    bs: 'Tema',
    sl: 'Tema',
    mk: 'Тема',
    sq: 'Tema',
    bg: 'Тема',
    ro: 'Temă',
    el: 'Θέμα',
    tr: 'Tema',
  },
  themeNight: {
    me: 'Noć',
    en: 'Night',
    sr: 'Noć',
    hr: 'Noć',
    bs: 'Noć',
    sl: 'Noč',
    mk: 'Ноќ',
    sq: 'Natë',
    bg: 'Нощ',
    ro: 'Noapte',
    el: 'Νύχτα',
    tr: 'Gece',
  },
  themeSepia: {
    me: 'Sepija',
    en: 'Sepia',
    sr: 'Sepija',
    hr: 'Sepija',
    bs: 'Sepija',
    sl: 'Sepija',
    mk: 'Сепија',
    sq: 'Sepia',
    bg: 'Сепия',
    ro: 'Sepia',
    el: 'Σέπια',
    tr: 'Sepya',
  },
  themeDay: {
    me: 'Dan',
    en: 'Day',
    sr: 'Dan',
    hr: 'Dan',
    bs: 'Dan',
    sl: 'Dan',
    mk: 'Ден',
    sq: 'Ditë',
    bg: 'Ден',
    ro: 'Zi',
    el: 'Ημέρα',
    tr: 'Gündüz',
  },
  fontSize: {
    me: 'Veličina slova',
    en: 'Text size',
    sr: 'Veličina slova',
    hr: 'Veličina slova',
    bs: 'Veličina slova',
    sl: 'Velikost črk',
    mk: 'Големина на буквите',
    sq: 'Madhësia e shkronjave',
    bg: 'Размер на буквите',
    ro: 'Mărimea textului',
    el: 'Μέγεθος γραμμάτων',
    tr: 'Yazı boyutu',
  },
  smaller: {
    me: 'Manja slova',
    en: 'Smaller text',
    sr: 'Manja slova',
    hr: 'Manja slova',
    bs: 'Manja slova',
    sl: 'Manjše črke',
    mk: 'Помали букви',
    sq: 'Shkronja më të vogla',
    bg: 'По-малки букви',
    ro: 'Text mai mic',
    el: 'Μικρότερα γράμματα',
    tr: 'Daha küçük yazı',
  },
  larger: {
    me: 'Veća slova',
    en: 'Larger text',
    sr: 'Veća slova',
    hr: 'Veća slova',
    bs: 'Veća slova',
    sl: 'Večje črke',
    mk: 'Поголеми букви',
    sq: 'Shkronja më të mëdha',
    bg: 'По-големи букви',
    ro: 'Text mai mare',
    el: 'Μεγαλύτερα γράμματα',
    tr: 'Daha büyük yazı',
  },
  lineHeight: {
    me: 'Prored',
    en: 'Line spacing',
    sr: 'Prored',
    hr: 'Prored',
    bs: 'Prored',
    sl: 'Razmik med vrsticami',
    mk: 'Проред',
    sq: 'Hapësira mes rreshtave',
    bg: 'Междуредие',
    ro: 'Spațiere între rânduri',
    el: 'Διάστιχο',
    tr: 'Satır aralığı',
  },
  lineHeightTight: {
    me: 'Uži',
    en: 'Narrow',
    sr: 'Uži',
    hr: 'Uži',
    bs: 'Uži',
    sl: 'Ožji',
    mk: 'Потесен',
    sq: 'Më i ngushtë',
    bg: 'По-тясно',
    ro: 'Mai mică',
    el: 'Μικρότερο',
    tr: 'Dar',
  },
  lineHeightNormal: {
    me: 'Srednji',
    en: 'Medium',
    sr: 'Srednji',
    hr: 'Srednji',
    bs: 'Srednji',
    sl: 'Srednji',
    mk: 'Среден',
    sq: 'Mesatar',
    bg: 'Средно',
    ro: 'Medie',
    el: 'Μεσαίο',
    tr: 'Orta',
  },
  lineHeightLoose: {
    me: 'Širi',
    en: 'Wide',
    sr: 'Širi',
    hr: 'Širi',
    bs: 'Širi',
    sl: 'Širši',
    mk: 'Поширок',
    sq: 'Më i gjerë',
    bg: 'По-широко',
    ro: 'Mai mare',
    el: 'Μεγαλύτερο',
    tr: 'Geniş',
  },
  font: {
    me: 'Font',
    en: 'Font',
    sr: 'Font',
    hr: 'Font',
    bs: 'Font',
    sl: 'Pisava',
    mk: 'Фонт',
    sq: 'Shkronjat',
    bg: 'Шрифт',
    ro: 'Font',
    el: 'Γραμματοσειρά',
    tr: 'Yazı tipi',
  },
  tapToPage: {
    me: 'Listanje dodirom',
    en: 'Tap to turn the page',
    sr: 'Listanje dodirom',
    hr: 'Listanje dodirom',
    bs: 'Listanje dodirom',
    sl: 'Listanje z dotikom',
    mk: 'Листање со допир',
    sq: 'Shfletim me prekje',
    bg: 'Прелистване с докосване',
    ro: 'Răsfoire prin atingere',
    el: 'Γύρισμα σελίδας με άγγιγμα',
    tr: 'Dokunarak sayfa çevirme',
  },
  tapToPageHint: {
    me: 'Dodir uz lijevu ili desnu ivicu ekrana pomjera tekst za jedan ekran.',
    en: 'A tap near the left or right edge of the screen moves the text by one screen.',
    sr: 'Dodir uz levu ili desnu ivicu ekrana pomera tekst za jedan ekran.',
    hr: 'Dodir uz lijevi ili desni rub zaslona pomiče tekst za jedan zaslon.',
    bs: 'Dodir uz lijevu ili desnu ivicu ekrana pomjera tekst za jedan ekran.',
    sl: 'Dotik ob levem ali desnem robu zaslona premakne besedilo za en zaslon.',
    mk: 'Допир до левиот или десниот раб на екранот го поместува текстот за еден екран.',
    sq: 'Një prekje pranë skajit të majtë ose të djathtë të ekranit e lëviz tekstin me një ekran.',
    bg: 'Докосване до левия или десния край на екрана премества текста с един екран.',
    ro: 'O atingere lângă marginea din stânga sau din dreapta a ecranului mută textul cu un ecran.',
    el: 'Ένα άγγιγμα κοντά στην αριστερή ή τη δεξιά άκρη της οθόνης μετακινεί το κείμενο κατά μία οθόνη.',
    tr: 'Ekranın sol ya da sağ kenarına dokunmak metni bir ekran kaydırır.',
  },
  watermark: {
    me: 'Primjerak za {email} · {code}',
    en: 'Copy for {email} · {code}',
    sr: 'Primerak za {email} · {code}',
    hr: 'Primjerak za {email} · {code}',
    bs: 'Primjerak za {email} · {code}',
    sl: 'Izvod za {email} · {code}',
    mk: 'Примерок за {email} · {code}',
    sq: 'Kopje për {email} · {code}',
    bg: 'Екземпляр за {email} · {code}',
    ro: 'Exemplar pentru {email} · {code}',
    el: 'Αντίτυπο για {email} · {code}',
    tr: '{email} için kopya · {code}',
  },
  previewEndTitle: {
    me: 'Ovdje se završava besplatan odlomak.',
    en: 'This is the end of the free excerpt.',
    sr: 'Ovde se završava besplatan odlomak.',
    hr: 'Ovdje završava besplatni ulomak.',
    bs: 'Ovdje se završava besplatan odlomak.',
    sl: 'Tu se konča brezplačni odlomek.',
    mk: 'Овде завршува бесплатниот извадок.',
    sq: 'Këtu mbaron fragmenti falas.',
    bg: 'Тук свършва безплатният откъс.',
    ro: 'Aici se încheie fragmentul gratuit.',
    el: 'Εδώ τελειώνει το δωρεάν απόσπασμα.',
    tr: 'Ücretsiz bölüm burada bitiyor.',
  },
  previewEndText: {
    me: 'Za ostatak knjige potrebna je kupovina e-knjige.',
    en: 'Reading the rest of the book requires buying the e-book.',
    sr: 'Za ostatak knjige potrebna je kupovina e-knjige.',
    hr: 'Za ostatak knjige potrebna je kupnja e-knjige.',
    bs: 'Za ostatak knjige potrebna je kupovina e-knjige.',
    sl: 'Za preostanek knjige je potreben nakup e-knjige.',
    mk: 'За остатокот од книгата е потребно купување на е-книгата.',
    sq: 'Për pjesën tjetër të librit duhet të blesh librin elektronik.',
    bg: 'За останалата част от книгата е нужна покупка на електронната книга.',
    ro: 'Pentru restul cărții este nevoie de cumpărarea cărții electronice.',
    el: 'Για το υπόλοιπο βιβλίο χρειάζεται αγορά του ηλεκτρονικού βιβλίου.',
    tr: 'Kitabın geri kalanı için e-kitabı satın alman gerekir.',
  },
  openFigure: {
    me: 'Otvori sliku u punoj veličini',
    en: 'Open the image full size',
    sr: 'Otvori sliku u punoj veličini',
    hr: 'Otvori sliku u punoj veličini',
    bs: 'Otvori sliku u punoj veličini',
    sl: 'Odpri sliko v polni velikosti',
    mk: 'Отвори ја сликата во полна големина',
    sq: 'Hape figurën në madhësi të plotë',
    bg: 'Отвори изображението в цял размер',
    ro: 'Deschide imaginea la dimensiune completă',
    el: 'Άνοιγμα της εικόνας σε πλήρες μέγεθος',
    tr: 'Görseli tam boyutta aç',
  },
  figureText: {
    me: 'Tekst infografike',
    en: 'Infographic text',
    sr: 'Tekst infografike',
    hr: 'Tekst infografike',
    bs: 'Tekst infografike',
    sl: 'Besedilo infografike',
    mk: 'Текст на инфографиката',
    sq: 'Teksti i infografikës',
    bg: 'Текст на инфографиката',
    ro: 'Textul infograficului',
    el: 'Κείμενο του γραφήματος',
    tr: 'İnfografik metni',
  },
  zoomIn: {
    me: 'Uvećaj',
    en: 'Zoom in',
    sr: 'Uvećaj',
    hr: 'Povećaj',
    bs: 'Uvećaj',
    sl: 'Povečaj',
    mk: 'Зголеми',
    sq: 'Zmadho',
    bg: 'Увеличи',
    ro: 'Mărește',
    el: 'Μεγέθυνση',
    tr: 'Yakınlaştır',
  },
  zoomOut: {
    me: 'Umanji',
    en: 'Zoom out',
    sr: 'Umanji',
    hr: 'Smanji',
    bs: 'Umanji',
    sl: 'Pomanjšaj',
    mk: 'Намали',
    sq: 'Zvogëlo',
    bg: 'Намали',
    ro: 'Micșorează',
    el: 'Σμίκρυνση',
    tr: 'Uzaklaştır',
  },
  zoomReset: {
    me: 'Vrati početnu veličinu',
    en: 'Reset zoom',
    sr: 'Vrati početnu veličinu',
    hr: 'Vrati početnu veličinu',
    bs: 'Vrati početnu veličinu',
    sl: 'Ponastavi povečavo',
    mk: 'Врати ја почетната големина',
    sq: 'Rikthe madhësinë fillestare',
    bg: 'Върни началния размер',
    ro: 'Revino la dimensiunea inițială',
    el: 'Επαναφορά μεγέθους',
    tr: 'Boyutu sıfırla',
  },
  footnote: {
    me: 'Napomena',
    en: 'Footnote',
    sr: 'Napomena',
    hr: 'Bilješka',
    bs: 'Napomena',
    sl: 'Opomba',
    mk: 'Фуснота',
    sq: 'Shënim',
    bg: 'Бележка',
    ro: 'Notă',
    el: 'Υποσημείωση',
    tr: 'Dipnot',
  },
  backToText: {
    me: 'Nazad na tekst',
    en: 'Back to the text',
    sr: 'Nazad na tekst',
    hr: 'Natrag na tekst',
    bs: 'Nazad na tekst',
    sl: 'Nazaj na besedilo',
    mk: 'Назад кон текстот',
    sq: 'Kthehu te teksti',
    bg: 'Обратно към текста',
    ro: 'Înapoi la text',
    el: 'Πίσω στο κείμενο',
    tr: 'Metne dön',
  },
  rateLimited: {
    me: 'Previše zahtjeva u kratkom roku. Pokušaj ponovo za nekoliko minuta.',
    en: 'Too many requests in a short time. Try again in a few minutes.',
    sr: 'Previše zahteva u kratkom roku. Pokušaj ponovo za nekoliko minuta.',
    hr: 'Previše zahtjeva u kratkom roku. Pokušaj ponovno za nekoliko minuta.',
    bs: 'Previše zahtjeva u kratkom roku. Pokušaj ponovo za nekoliko minuta.',
    sl: 'Preveč zahtev v kratkem času. Poskusi znova čez nekaj minut.',
    mk: 'Премногу барања за кратко време. Обиди се повторно за неколку минути.',
    sq: 'Shumë kërkesa në kohë të shkurtër. Provo përsëri pas disa minutash.',
    bg: 'Твърде много заявки за кратко време. Опитай отново след няколко минути.',
    ro: 'Prea multe cereri într-un timp scurt. Încearcă din nou peste câteva minute.',
    el: 'Πάρα πολλά αιτήματα σε λίγο χρόνο. Δοκίμασε ξανά σε λίγα λεπτά.',
    tr: 'Kısa sürede çok fazla istek. Birkaç dakika sonra tekrar dene.',
  },
  chapterError: {
    me: 'Poglavlje trenutno ne možemo učitati.',
    en: "We can't load this chapter right now.",
    sr: 'Poglavlje trenutno ne možemo da učitamo.',
    hr: 'Poglavlje trenutačno ne možemo učitati.',
    bs: 'Poglavlje trenutno ne možemo učitati.',
    sl: 'Poglavja trenutno ne moremo naložiti.',
    mk: 'Поглавјето во моментов не можеме да го вчитаме.',
    sq: 'Për momentin nuk mund ta ngarkojmë kapitullin.',
    bg: 'В момента не можем да заредим главата.',
    ro: 'Nu putem încărca acum capitolul.',
    el: 'Δεν μπορούμε να φορτώσουμε το κεφάλαιο αυτή τη στιγμή.',
    tr: 'Bu bölümü şu anda yükleyemiyoruz.',
  },
  table: {
    me: 'Tabela',
    en: 'Table',
    sr: 'Tabela',
    hr: 'Tablica',
    bs: 'Tabela',
    sl: 'Tabela',
    mk: 'Табела',
    sq: 'Tabela',
    bg: 'Таблица',
    ro: 'Tabel',
    el: 'Πίνακας',
    tr: 'Tablo',
  },
};

export const BOOK_READER_TRANSLATION_KEYS = Object.keys(BOOK_READER_TRANSLATIONS) as BookReaderTranslationKey[];

/**
 * Get a translated reader string; `{email}` and `{code}` are filled by the caller.
 */
export function getBookReaderTranslation(key: BookReaderTranslationKey, language: Language): string {
  return BOOK_READER_TRANSLATIONS[key][language];
}

/**
 * Auth error messages for all 12 supported languages, keyed by `AuthErrorKey`
 * (see `authErrors.ts`, which maps the backend's English details and status codes).
 */
const AUTH_ERROR_TRANSLATIONS: Record<AuthErrorKey, Record<Language, string>> = {
  invalidCredentials: {
    me: 'Pogrešan email ili lozinka.',
    en: 'Incorrect email or password.',
    sr: 'Pogrešan email ili lozinka.',
    hr: 'Pogrešan email ili lozinka.',
    bs: 'Pogrešan email ili lozinka.',
    sl: 'Napačen e-poštni naslov ali geslo.',
    mk: 'Погрешна е-пошта или лозинка.',
    sq: 'Email-i ose fjalëkalimi është i gabuar.',
    bg: 'Грешен имейл или парола.',
    ro: 'Email sau parolă greșită.',
    el: 'Λάθος email ή κωδικός.',
    tr: 'E-posta veya şifre hatalı.',
  },
  emailAlreadyRegistered: {
    me: 'Nalog sa ovom email adresom već postoji.',
    en: 'An account with this email already exists.',
    sr: 'Nalog sa ovom email adresom već postoji.',
    hr: 'Račun s ovom email adresom već postoji.',
    bs: 'Nalog s ovom email adresom već postoji.',
    sl: 'Račun s tem e-poštnim naslovom že obstaja.',
    mk: 'Веќе постои сметка со оваа е-пошта.',
    sq: 'Ekziston tashmë një llogari me këtë email.',
    bg: 'Вече има акаунт с този имейл.',
    ro: 'Există deja un cont cu acest email.',
    el: 'Υπάρχει ήδη λογαριασμός με αυτό το email.',
    tr: 'Bu e-postayla kayıtlı bir hesap zaten var.',
  },
  passwordTooShort: {
    me: 'Lozinka mora imati najmanje 8 karaktera.',
    en: 'Password must be at least 8 characters.',
    sr: 'Lozinka mora imati najmanje 8 karaktera.',
    hr: 'Lozinka mora imati najmanje 8 znakova.',
    bs: 'Lozinka mora imati najmanje 8 znakova.',
    sl: 'Geslo mora imeti vsaj 8 znakov.',
    mk: 'Лозинката мора да има најмалку 8 знаци.',
    sq: 'Fjalëkalimi duhet të ketë të paktën 8 karaktere.',
    bg: 'Паролата трябва да е поне 8 знака.',
    ro: 'Parola trebuie să aibă cel puțin 8 caractere.',
    el: 'Ο κωδικός πρέπει να έχει τουλάχιστον 8 χαρακτήρες.',
    tr: 'Şifre en az 8 karakter olmalı.',
  },
  passwordTooLong: {
    me: 'Lozinka je preduga.',
    en: 'Password is too long.',
    sr: 'Lozinka je predugačka.',
    hr: 'Lozinka je preduga.',
    bs: 'Lozinka je predugačka.',
    sl: 'Geslo je predolgo.',
    mk: 'Лозинката е предолга.',
    sq: 'Fjalëkalimi është shumë i gjatë.',
    bg: 'Паролата е твърде дълга.',
    ro: 'Parola este prea lungă.',
    el: 'Ο κωδικός είναι πολύ μεγάλος.',
    tr: 'Şifre çok uzun.',
  },
  invalidName: {
    me: 'Ime mora imati od 1 do 100 karaktera.',
    en: 'Name must be between 1 and 100 characters.',
    sr: 'Ime mora imati od 1 do 100 karaktera.',
    hr: 'Ime mora imati od 1 do 100 znakova.',
    bs: 'Ime mora imati od 1 do 100 znakova.',
    sl: 'Ime mora imeti od 1 do 100 znakov.',
    mk: 'Името мора да има од 1 до 100 знаци.',
    sq: 'Emri duhet të ketë nga 1 deri në 100 karaktere.',
    bg: 'Името трябва да е от 1 до 100 знака.',
    ro: 'Numele trebuie să aibă între 1 și 100 de caractere.',
    el: 'Το όνομα πρέπει να έχει από 1 έως 100 χαρακτήρες.',
    tr: 'Ad 1 ile 100 karakter arasında olmalı.',
  },
  invalidEmail: {
    me: 'Unesi ispravnu email adresu.',
    en: 'Enter a valid email address.',
    sr: 'Unesi ispravnu email adresu.',
    hr: 'Unesi ispravnu email adresu.',
    bs: 'Unesi ispravnu email adresu.',
    sl: 'Vnesi veljaven e-poštni naslov.',
    mk: 'Внеси важечка е-пошта.',
    sq: 'Shkruaj një adresë email të vlefshme.',
    bg: 'Въведи валиден имейл адрес.',
    ro: 'Introdu o adresă de email validă.',
    el: 'Γράψε μια έγκυρη διεύθυνση email.',
    tr: 'Geçerli bir e-posta adresi gir.',
  },
  resetLinkInvalid: {
    me: 'Link za resetovanje je nevažeći ili je istekao.',
    en: 'This reset link is invalid or has expired.',
    sr: 'Link za resetovanje je nevažeći ili je istekao.',
    hr: 'Poveznica za resetiranje je nevažeća ili je istekla.',
    bs: 'Link za resetovanje je nevažeći ili je istekao.',
    sl: 'Povezava za ponastavitev ni veljavna ali je potekla.',
    mk: 'Линкот за ресетирање е неважечки или истечен.',
    sq: 'Linku i rivendosjes është i pavlefshëm ose ka skaduar.',
    bg: 'Линкът за промяна е невалиден или е изтекъл.',
    ro: 'Linkul de resetare este invalid sau a expirat.',
    el: 'Ο σύνδεσμος επαναφοράς δεν είναι έγκυρος ή έχει λήξει.',
    tr: 'Sıfırlama bağlantısı geçersiz veya süresi dolmuş.',
  },
  resetLinkUsed: {
    me: 'Ovaj link za resetovanje je već iskorišćen.',
    en: 'This reset link has already been used.',
    sr: 'Ovaj link za resetovanje je već iskorišćen.',
    hr: 'Ova poveznica za resetiranje već je iskorištena.',
    bs: 'Ovaj link za resetovanje je već iskorišten.',
    sl: 'Ta povezava za ponastavitev je že bila uporabljena.',
    mk: 'Овој линк за ресетирање е веќе искористен.',
    sq: 'Ky link rivendosjeje është përdorur tashmë.',
    bg: 'Този линк за промяна вече е използван.',
    ro: 'Acest link de resetare a fost deja folosit.',
    el: 'Αυτός ο σύνδεσμος επαναφοράς έχει ήδη χρησιμοποιηθεί.',
    tr: 'Bu sıfırlama bağlantısı zaten kullanıldı.',
  },
  resetLinkExpired: {
    me: 'Ovaj link za resetovanje je istekao. Zatraži novi.',
    en: 'This reset link has expired. Request a new one.',
    sr: 'Ovaj link za resetovanje je istekao. Zatraži novi.',
    hr: 'Ova poveznica za resetiranje je istekla. Zatraži novu.',
    bs: 'Ovaj link za resetovanje je istekao. Zatraži novi.',
    sl: 'Ta povezava za ponastavitev je potekla. Zahtevaj novo.',
    mk: 'Овој линк за ресетирање истече. Побарај нов.',
    sq: 'Ky link rivendosjeje ka skaduar. Kërko një të ri.',
    bg: 'Този линк за промяна е изтекъл. Поискай нов.',
    ro: 'Acest link de resetare a expirat. Cere unul nou.',
    el: 'Αυτός ο σύνδεσμος επαναφοράς έληξε. Ζήτησε νέο.',
    tr: 'Bu sıfırlama bağlantısının süresi doldu. Yeni bir tane iste.',
  },
  sessionExpired: {
    me: 'Prijava je istekla. Prijavi se ponovo.',
    en: 'Your sign-in has expired. Please sign in again.',
    sr: 'Prijava je istekla. Prijavi se ponovo.',
    hr: 'Prijava je istekla. Prijavi se ponovno.',
    bs: 'Prijava je istekla. Prijavi se ponovo.',
    sl: 'Prijava je potekla. Prijavi se znova.',
    mk: 'Најавата истече. Најави се повторно.',
    sq: 'Identifikimi ka skaduar. Identifikohu përsëri.',
    bg: 'Входът ти е изтекъл. Влез отново.',
    ro: 'Conectarea a expirat. Conectează-te din nou.',
    el: 'Η σύνδεσή σου έληξε. Συνδέσου ξανά.',
    tr: 'Oturumunun süresi doldu. Tekrar giriş yap.',
  },
  googleUnavailable: {
    me: 'Prijava preko Google naloga trenutno nije dostupna.',
    en: 'Google sign-in is not available right now.',
    sr: 'Prijava putem Google naloga trenutno nije dostupna.',
    hr: 'Prijava putem Google računa trenutačno nije dostupna.',
    bs: 'Prijava putem Google računa trenutno nije dostupna.',
    sl: 'Prijava z Googlom trenutno ni na voljo.',
    mk: 'Најавата со Google моментално не е достапна.',
    sq: 'Identifikimi me Google nuk është i disponueshëm tani.',
    bg: 'Входът с Google в момента не е достъпен.',
    ro: 'Conectarea cu Google nu este disponibilă acum.',
    el: 'Η σύνδεση με Google δεν είναι διαθέσιμη αυτή τη στιγμή.',
    tr: 'Google ile giriş şu anda kullanılamıyor.',
  },
  googleFailed: {
    me: 'Prijava preko Google naloga nije uspjela. Pokušaj ponovo.',
    en: 'Signing in with Google did not work. Try again.',
    sr: 'Prijava preko Google naloga nije uspela. Pokušaj ponovo.',
    hr: 'Prijava putem Google računa nije uspjela. Pokušaj ponovno.',
    bs: 'Prijava putem Google računa nije uspjela. Pokušaj ponovo.',
    sl: 'Prijava z Googlom ni uspela. Poskusi znova.',
    mk: 'Најавата преку Google не успеа. Обиди се повторно.',
    sq: 'Identifikimi me Google nuk funksionoi. Provo përsëri.',
    bg: 'Влизането с Google не успя. Опитай отново.',
    ro: 'Conectarea cu Google nu a reușit. Încearcă din nou.',
    el: 'Η σύνδεση με Google δεν ολοκληρώθηκε. Δοκίμασε ξανά.',
    tr: 'Google ile giriş yapılamadı. Tekrar dene.',
  },
  tooManyRequests: {
    me: 'Previše pokušaja. Sačekaj malo pa pokušaj ponovo.',
    en: 'Too many attempts. Wait a moment and try again.',
    sr: 'Previše pokušaja. Sačekaj malo pa pokušaj ponovo.',
    hr: 'Previše pokušaja. Pričekaj malo pa pokušaj ponovno.',
    bs: 'Previše pokušaja. Sačekaj malo pa pokušaj ponovo.',
    sl: 'Preveč poskusov. Malo počakaj in poskusi znova.',
    mk: 'Премногу обиди. Почекај малку и обиди се повторно.',
    sq: 'Shumë përpjekje. Prit pak dhe provo përsëri.',
    bg: 'Твърде много опити. Изчакай малко и опитай отново.',
    ro: 'Prea multe încercări. Așteaptă puțin și încearcă din nou.',
    el: 'Πάρα πολλές προσπάθειες. Περίμενε λίγο και δοκίμασε ξανά.',
    tr: 'Çok fazla deneme. Biraz bekle ve tekrar dene.',
  },
  networkError: {
    me: 'Nema veze sa serverom. Provjeri internet vezu i pokušaj ponovo.',
    en: "Can't reach the server. Check your connection and try again.",
    sr: 'Nema veze sa serverom. Proveri internet vezu i pokušaj ponovo.',
    hr: 'Nema veze s poslužiteljem. Provjeri internetsku vezu i pokušaj ponovno.',
    bs: 'Nema veze sa serverom. Provjeri internet vezu i pokušaj ponovo.',
    sl: 'Ni povezave s strežnikom. Preveri internetno povezavo in poskusi znova.',
    mk: 'Нема врска со серверот. Провери ја интернет врската и обиди се повторно.',
    sq: 'Nuk ka lidhje me serverin. Kontrollo lidhjen me internetin dhe provo përsëri.',
    bg: 'Няма връзка със сървъра. Провери интернет връзката си и опитай отново.',
    ro: 'Nu există conexiune la server. Verifică-ți conexiunea la internet și încearcă din nou.',
    el: 'Δεν υπάρχει σύνδεση με τον διακομιστή. Έλεγξε τη σύνδεσή σου και δοκίμασε ξανά.',
    tr: 'Sunucuya ulaşılamıyor. İnternet bağlantını kontrol et ve tekrar dene.',
  },
  serverError: {
    me: 'Došlo je do greške na serveru. Pokušaj ponovo kasnije.',
    en: 'Something went wrong on our side. Please try again later.',
    sr: 'Došlo je do greške na serveru. Pokušaj ponovo kasnije.',
    hr: 'Došlo je do pogreške na poslužitelju. Pokušaj ponovno kasnije.',
    bs: 'Došlo je do greške na serveru. Pokušaj ponovo kasnije.',
    sl: 'Na strežniku je prišlo do napake. Poskusi znova pozneje.',
    mk: 'Настана грешка на серверот. Обиди се повторно подоцна.',
    sq: 'Ndodhi një gabim në server. Provo përsëri më vonë.',
    bg: 'Възникна грешка на сървъра. Опитай отново по-късно.',
    ro: 'A apărut o eroare pe server. Încearcă din nou mai târziu.',
    el: 'Παρουσιάστηκε σφάλμα στον διακομιστή. Δοκίμασε ξανά αργότερα.',
    tr: 'Sunucuda bir hata oluştu. Daha sonra tekrar dene.',
  },
  unknown: {
    me: 'Nešto je pošlo po zlu. Pokušaj ponovo.',
    en: 'Something went wrong. Please try again.',
    sr: 'Nešto je pošlo po zlu. Pokušaj ponovo.',
    hr: 'Nešto je pošlo po zlu. Pokušaj ponovno.',
    bs: 'Nešto je pošlo po zlu. Pokušaj ponovo.',
    sl: 'Nekaj je šlo narobe. Poskusi znova.',
    mk: 'Нешто тргна наопаку. Обиди се повторно.',
    sq: 'Diçka shkoi keq. Provo përsëri.',
    bg: 'Нещо се обърка. Опитай отново.',
    ro: 'Ceva nu a funcționat. Încearcă din nou.',
    el: 'Κάτι πήγε στραβά. Δοκίμασε ξανά.',
    tr: 'Bir şeyler ters gitti. Tekrar dene.',
  },
};

/**
 * Get the translated message for an auth error key in the given language
 */
export function getAuthErrorTranslation(key: AuthErrorKey, language: Language): string {
  return AUTH_ERROR_TRANSLATIONS[key][language];
}

/**
 * Akustične page (A4, APP2) translation keys
 */
export type AcousticTranslationKey =
  | 'intro'
  | 'instructionsTitle'
  | 'instructionsStep1'
  | 'instructionsStep2'
  | 'instructionsStep3'
  | 'instructionsNote'
  | 'durationLabel'
  | 'intentionLabel'
  | 'intentionPlaceholder'
  | 'yourIntention'
  | 'noIntention'
  | 'startSession'
  | 'emptyState';

/**
 * Akustične page translations for all 12 supported languages. Luka's APP2 wording in the ti form
 * (Q12); the instructions and the empty state are drafts listed in copy-for-luka.md (Q15).
 */
const ACOUSTIC_TRANSLATIONS: Record<AcousticTranslationKey, Record<Language, string>> = {
  intro: {
    me: 'Izaberi zvučni ambijent, postavi svoju namjeru i posveti vrijeme sebi.',
    en: 'Choose a soundscape, set your intention and take time for yourself.',
    sr: 'Izaberi zvučni ambijent, postavi svoju nameru i posveti vreme sebi.',
    hr: 'Odaberi zvučni ambijent, postavi svoju namjeru i posveti vrijeme sebi.',
    bs: 'Izaberi zvučni ambijent, postavi svoju namjeru i posveti vrijeme sebi.',
    sl: 'Izberi zvočno okolje, določi svojo namero in si vzemi čas zase.',
    mk: 'Избери звучен амбиент, постави ја својата намера и посвети време на себе.',
    sq: 'Zgjidh një ambient tingujsh, vendos qëllimin tënd dhe kushtoji kohë vetes.',
    bg: 'Избери звукова атмосфера, задай намерението си и отдели време за себе си.',
    ro: 'Alege un ambient sonor, stabilește-ți intenția și acordă-ți timp pentru tine.',
    el: 'Διάλεξε ένα ηχητικό τοπίο, όρισε την πρόθεσή σου και αφιέρωσε χρόνο στον εαυτό σου.',
    tr: 'Bir ses ortamı seç, niyetini belirle ve kendine zaman ayır.',
  },
  instructionsTitle: {
    me: 'Kratko uputstvo',
    en: 'Quick guide',
    sr: 'Kratko uputstvo',
    hr: 'Kratke upute',
    bs: 'Kratko uputstvo',
    sl: 'Kratka navodila',
    mk: 'Кратко упатство',
    sq: 'Udhëzim i shkurtër',
    bg: 'Кратки указания',
    ro: 'Scurt ghid',
    el: 'Σύντομες οδηγίες',
    tr: 'Kısa rehber',
  },
  instructionsStep1: {
    me: 'Izaberi trajanje i namjeru.',
    en: 'Choose the length and your intention.',
    sr: 'Izaberi trajanje i nameru.',
    hr: 'Odaberi trajanje i namjeru.',
    bs: 'Izaberi trajanje i namjeru.',
    sl: 'Izberi trajanje in namero.',
    mk: 'Избери траење и намера.',
    sq: 'Zgjidh kohëzgjatjen dhe qëllimin.',
    bg: 'Избери продължителност и намерение.',
    ro: 'Alege durata și intenția.',
    el: 'Διάλεξε διάρκεια και πρόθεση.',
    tr: 'Süreyi ve niyetini seç.',
  },
  instructionsStep2: {
    me: 'Pronađi mirno mjesto i smjesti se udobno, bez potrebe da bilo šta forsiraš.',
    en: 'Find a quiet place and get comfortable, without forcing anything.',
    sr: 'Pronađi mirno mesto i smesti se udobno, bez potrebe da bilo šta forsiraš.',
    hr: 'Pronađi mirno mjesto i smjesti se udobno, bez potrebe da išta forsiraš.',
    bs: 'Pronađi mirno mjesto i smjesti se udobno, bez potrebe da bilo šta forsiraš.',
    sl: 'Poišči miren kraj in se udobno namesti, brez siljenja.',
    mk: 'Најди мирно место и смести се удобно, без потреба ништо да присилуваш.',
    sq: 'Gjej një vend të qetë dhe rehatohu, pa pasur nevojë të detyrosh asgjë.',
    bg: 'Намери тихо място и се настани удобно, без да насилваш каквото и да е.',
    ro: 'Găsește un loc liniștit și așază-te comod, fără să forțezi nimic.',
    el: 'Βρες ένα ήσυχο μέρος και βολέψου άνετα, χωρίς να πιέζεις τίποτα.',
    tr: 'Sessiz bir yer bul ve rahatça yerleş, hiçbir şeyi zorlamadan.',
  },
  instructionsStep3: {
    me: 'Slušaj zvuk i, kad pažnja odluta, nježno je vrati svojoj namjeri.',
    en: 'Listen to the sound and, when your attention wanders, gently bring it back to your intention.',
    sr: 'Slušaj zvuk i, kad pažnja odluta, nežno je vrati svojoj nameri.',
    hr: 'Slušaj zvuk i, kad pažnja odluta, nježno je vrati svojoj namjeri.',
    bs: 'Slušaj zvuk i, kad pažnja odluta, nježno je vrati svojoj namjeri.',
    sl: 'Poslušaj zvok in, ko pozornost odtava, jo nežno vrni k svoji nameri.',
    mk: 'Слушај го звукот и, кога вниманието ќе одлута, нежно врати го кон својата намера.',
    sq: 'Dëgjo tingullin dhe, kur vëmendja të largohet, ktheje butësisht te qëllimi yt.',
    bg: 'Слушай звука и когато вниманието ти се отплесне, нежно го върни към намерението си.',
    ro: 'Ascultă sunetul și, când atenția îți rătăcește, readu-o cu blândețe la intenția ta.',
    el: 'Άκου τον ήχο και, όταν η προσοχή σου ξεφεύγει, φέρ’ την απαλά πίσω στην πρόθεσή σου.',
    tr: 'Sesi dinle ve dikkatin dağıldığında onu nazikçe niyetine geri getir.',
  },
  instructionsNote: {
    me: 'Namjera ne mijenja zvučni zapis: ona je tvoj lični fokus prije slušanja.',
    en: 'Your intention does not change the recording: it is your personal focus before you listen.',
    sr: 'Namera ne menja zvučni zapis: ona je tvoj lični fokus pre slušanja.',
    hr: 'Namjera ne mijenja zvučni zapis: ona je tvoj osobni fokus prije slušanja.',
    bs: 'Namjera ne mijenja zvučni zapis: ona je tvoj lični fokus prije slušanja.',
    sl: 'Namera ne spremeni posnetka: je tvoj osebni fokus pred poslušanjem.',
    mk: 'Намерата не ја менува снимката: таа е твојот личен фокус пред слушањето.',
    sq: 'Qëllimi nuk e ndryshon regjistrimin: është fokusi yt personal para dëgjimit.',
    bg: 'Намерението не променя записа: то е твоят личен фокус преди слушането.',
    ro: 'Intenția nu schimbă înregistrarea: este punctul tău personal de concentrare înainte de ascultare.',
    el: 'Η πρόθεση δεν αλλάζει την ηχογράφηση: είναι η προσωπική σου εστίαση πριν ακούσεις.',
    tr: 'Niyet kaydı değiştirmez: dinlemeden önceki kişisel odağındır.',
  },
  durationLabel: {
    me: 'Trajanje sesije',
    en: 'Session length',
    sr: 'Trajanje sesije',
    hr: 'Trajanje sesije',
    bs: 'Trajanje sesije',
    sl: 'Trajanje seje',
    mk: 'Траење на сесијата',
    sq: 'Kohëzgjatja e seancës',
    bg: 'Продължителност на сесията',
    ro: 'Durata sesiunii',
    el: 'Διάρκεια συνεδρίας',
    tr: 'Seans süresi',
  },
  intentionLabel: {
    me: 'Postavi svoju namjeru',
    en: 'Set your intention',
    sr: 'Postavi svoju nameru',
    hr: 'Postavi svoju namjeru',
    bs: 'Postavi svoju namjeru',
    sl: 'Določi svojo namero',
    mk: 'Постави ја својата намера',
    sq: 'Vendos qëllimin tënd',
    bg: 'Задай намерението си',
    ro: 'Stabilește-ți intenția',
    el: 'Όρισε την πρόθεσή σου',
    tr: 'Niyetini belirle',
  },
  intentionPlaceholder: {
    me: 'Izaberi namjeru',
    en: 'Choose an intention',
    sr: 'Izaberi nameru',
    hr: 'Odaberi namjeru',
    bs: 'Izaberi namjeru',
    sl: 'Izberi namero',
    mk: 'Избери намера',
    sq: 'Zgjidh një qëllim',
    bg: 'Избери намерение',
    ro: 'Alege o intenție',
    el: 'Διάλεξε μια πρόθεση',
    tr: 'Bir niyet seç',
  },
  yourIntention: {
    me: 'Tvoja namjera',
    en: 'Your intention',
    sr: 'Tvoja namera',
    hr: 'Tvoja namjera',
    bs: 'Tvoja namjera',
    sl: 'Tvoja namera',
    mk: 'Твојата намера',
    sq: 'Qëllimi yt',
    bg: 'Твоето намерение',
    ro: 'Intenția ta',
    el: 'Η πρόθεσή σου',
    tr: 'Niyetin',
  },
  noIntention: {
    me: 'Izaberi namjeru iz padajućeg menija.',
    en: 'Choose an intention from the drop-down menu.',
    sr: 'Izaberi nameru iz padajućeg menija.',
    hr: 'Odaberi namjeru iz padajućeg izbornika.',
    bs: 'Izaberi namjeru iz padajućeg menija.',
    sl: 'Izberi namero v spustnem meniju.',
    mk: 'Избери намера од паѓачкото мени.',
    sq: 'Zgjidh një qëllim nga menyja rënëse.',
    bg: 'Избери намерение от падащото меню.',
    ro: 'Alege o intenție din meniul derulant.',
    el: 'Διάλεξε μια πρόθεση από το αναπτυσσόμενο μενού.',
    tr: 'Açılır menüden bir niyet seç.',
  },
  startSession: {
    me: 'Pokreni sesiju',
    en: 'Start session',
    sr: 'Pokreni sesiju',
    hr: 'Pokreni sesiju',
    bs: 'Pokreni sesiju',
    sl: 'Zaženi sejo',
    mk: 'Пушти ја сесијата',
    sq: 'Nis seancën',
    bg: 'Пусни сесията',
    ro: 'Pornește sesiunea',
    el: 'Ξεκίνα τη συνεδρία',
    tr: 'Seansı başlat',
  },
  emptyState: {
    me: 'Snimci akustičnih sesija uskoro stižu.',
    en: 'The acoustic session recordings are coming soon.',
    sr: 'Snimci akustičnih sesija uskoro stižu.',
    hr: 'Snimke akustičnih sesija uskoro stižu.',
    bs: 'Snimci akustičnih sesija uskoro stižu.',
    sl: 'Posnetki akustičnih sej bodo kmalu na voljo.',
    mk: 'Снимките од акустичните сесии наскоро ќе бидат достапни.',
    sq: 'Regjistrimet e seancave akustike vijnë së shpejti.',
    bg: 'Записите на акустичните сесии идват скоро.',
    ro: 'Înregistrările sesiunilor acustice vor fi disponibile în curând.',
    el: 'Οι ηχογραφήσεις των ακουστικών συνεδριών έρχονται σύντομα.',
    tr: 'Akustik seans kayıtları yakında geliyor.',
  },
};

export function getAcousticTranslation(key: AcousticTranslationKey, language: Language): string {
  return ACOUSTIC_TRANSLATIONS[key][language];
}

/**
 * The Q26 intentions of the Akustične programs: first person, gender-neutral (Q13), not saved.
 * Luka's APP2 wording, except `relaxSlowDown` (his "…i budem prisutan" is masculine).
 */
export type AcousticIntentionKey =
  | 'focusClarity'
  | 'focusIdeas'
  | 'focusWhatMatters'
  | 'focusCreativity'
  | 'relaxCalm'
  | 'relaxTension'
  | 'relaxRest'
  | 'relaxSlowDown';

const ACOUSTIC_INTENTION_TRANSLATIONS: Record<AcousticIntentionKey, Record<Language, string>> = {
  focusClarity: {
    me: 'Želim više jasnoće i koncentracije.',
    en: 'I want more clarity and concentration.',
    sr: 'Želim više jasnoće i koncentracije.',
    hr: 'Želim više jasnoće i koncentracije.',
    bs: 'Želim više jasnoće i koncentracije.',
    sl: 'Želim si več jasnosti in zbranosti.',
    mk: 'Сакам повеќе јасност и концентрација.',
    sq: 'Dua më shumë qartësi dhe përqendrim.',
    bg: 'Искам повече яснота и концентрация.',
    ro: 'Îmi doresc mai multă claritate și concentrare.',
    el: 'Θέλω περισσότερη διαύγεια και συγκέντρωση.',
    tr: 'Daha fazla netlik ve konsantrasyon istiyorum.',
  },
  focusIdeas: {
    me: 'Otvaram se novim idejama i inspiraciji.',
    en: 'I open myself to new ideas and inspiration.',
    sr: 'Otvaram se novim idejama i inspiraciji.',
    hr: 'Otvaram se novim idejama i nadahnuću.',
    bs: 'Otvaram se novim idejama i inspiraciji.',
    sl: 'Odpiram se novim idejam in navdihu.',
    mk: 'Се отворам за нови идеи и инспирација.',
    sq: 'Hapem ndaj ideve të reja dhe frymëzimit.',
    bg: 'Отварям се за нови идеи и вдъхновение.',
    ro: 'Mă deschid către idei noi și inspirație.',
    el: 'Ανοίγομαι σε νέες ιδέες και έμπνευση.',
    tr: 'Kendimi yeni fikirlere ve ilhama açıyorum.',
  },
  focusWhatMatters: {
    me: 'Usmjeravam pažnju na ono što mi je važno.',
    en: 'I turn my attention to what matters to me.',
    sr: 'Usmeravam pažnju na ono što mi je važno.',
    hr: 'Usmjeravam pažnju na ono što mi je važno.',
    bs: 'Usmjeravam pažnju na ono što mi je važno.',
    sl: 'Pozornost usmerjam na to, kar mi je pomembno.',
    mk: 'Го насочувам вниманието кон она што ми е важно.',
    sq: 'E drejtoj vëmendjen te ajo që është e rëndësishme për mua.',
    bg: 'Насочвам вниманието си към това, което е важно за мен.',
    ro: 'Îmi îndrept atenția spre ceea ce contează pentru mine.',
    el: 'Στρέφω την προσοχή μου σε ό,τι είναι σημαντικό για μένα.',
    tr: 'Dikkatimi benim için önemli olana yöneltiyorum.',
  },
  focusCreativity: {
    me: 'Želim da razvijam svoj kreativni potencijal.',
    en: 'I want to develop my creative potential.',
    sr: 'Želim da razvijam svoj kreativni potencijal.',
    hr: 'Želim razvijati svoj kreativni potencijal.',
    bs: 'Želim da razvijam svoj kreativni potencijal.',
    sl: 'Želim razvijati svoj ustvarjalni potencial.',
    mk: 'Сакам да го развивам својот креативен потенцијал.',
    sq: 'Dua të zhvilloj potencialin tim krijues.',
    bg: 'Искам да развивам творческия си потенциал.',
    ro: 'Vreau să-mi dezvolt potențialul creativ.',
    el: 'Θέλω να αναπτύξω το δημιουργικό μου δυναμικό.',
    tr: 'Yaratıcı potansiyelimi geliştirmek istiyorum.',
  },
  relaxCalm: {
    me: 'Biram mir i opuštanje.',
    en: 'I choose calm and relaxation.',
    sr: 'Biram mir i opuštanje.',
    hr: 'Biram mir i opuštanje.',
    bs: 'Biram mir i opuštanje.',
    sl: 'Izberem mir in sprostitev.',
    mk: 'Избирам мир и опуштање.',
    sq: 'Zgjedh qetësinë dhe çlodhjen.',
    bg: 'Избирам спокойствие и отпускане.',
    ro: 'Aleg liniștea și relaxarea.',
    el: 'Επιλέγω τη γαλήνη και τη χαλάρωση.',
    tr: 'Huzuru ve rahatlamayı seçiyorum.',
  },
  relaxTension: {
    me: 'Otpuštam napetost koju danas osjećam.',
    en: 'I let go of the tension I feel today.',
    sr: 'Otpuštam napetost koju danas osećam.',
    hr: 'Otpuštam napetost koju danas osjećam.',
    bs: 'Otpuštam napetost koju danas osjećam.',
    sl: 'Spuščam napetost, ki jo danes čutim.',
    mk: 'Ја отпуштам напнатоста што ја чувствувам денес.',
    sq: 'Lëshoj tensionin që ndiej sot.',
    bg: 'Освобождавам напрежението, което усещам днес.',
    ro: 'Eliberez încordarea pe care o simt astăzi.',
    el: 'Αφήνω την ένταση που νιώθω σήμερα.',
    tr: 'Bugün hissettiğim gerginliği bırakıyorum.',
  },
  relaxRest: {
    me: 'Posvećujem vrijeme odmoru i sebi.',
    en: 'I give time to rest and to myself.',
    sr: 'Posvećujem vreme odmoru i sebi.',
    hr: 'Posvećujem vrijeme odmoru i sebi.',
    bs: 'Posvećujem vrijeme odmoru i sebi.',
    sl: 'Čas namenjam počitku in sebi.',
    mk: 'Посветувам време на одморот и на себе.',
    sq: 'I kushtoj kohë pushimit dhe vetes.',
    bg: 'Отделям време за почивка и за себе си.',
    ro: 'Îmi acord timp pentru odihnă și pentru mine.',
    el: 'Αφιερώνω χρόνο στην ξεκούραση και στον εαυτό μου.',
    tr: 'Dinlenmeye ve kendime zaman ayırıyorum.',
  },
  relaxSlowDown: {
    me: 'Dozvoljavam sebi da usporim i budem u sadašnjem trenutku.',
    en: 'I allow myself to slow down and be in the present moment.',
    sr: 'Dozvoljavam sebi da usporim i budem u sadašnjem trenutku.',
    hr: 'Dopuštam si usporiti i biti u sadašnjem trenutku.',
    bs: 'Dozvoljavam sebi da usporim i budem u sadašnjem trenutku.',
    sl: 'Dovolim si upočasniti in biti v sedanjem trenutku.',
    mk: 'Си дозволувам да забавам и да бидам во сегашниот момент.',
    sq: 'I lejoj vetes të ngadalësohem dhe të jem në çastin e tanishëm.',
    bg: 'Позволявам си да забавя темпото и да бъда в настоящия момент.',
    ro: 'Îmi permit să încetinesc și să fiu în momentul prezent.',
    el: 'Επιτρέπω στον εαυτό μου να επιβραδύνει και να μένει στο παρόν.',
    tr: 'Yavaşlamaya ve şu anda kalmaya kendime izin veriyorum.',
  },
};

export function getAcousticIntentionTranslation(key: AcousticIntentionKey, language: Language): string {
  return ACOUSTIC_INTENTION_TRANSLATIONS[key][language];
}

/** CLDR plural category of a minute count; every language lists every form, unused ones repeat `other`. */
type MinutesForm = 'one' | 'two' | 'few' | 'other';

/**
 * "{count} minuta" on the Akustične duration toggle (APP2 "15 minuta | 45 minuta"). `two` is
 * Slovenian only; `few` is used by me/sr/hr/bs/sl/ro (ro: 2–19, "de" from 20).
 */
const ACOUSTIC_MINUTES_TRANSLATIONS: Record<MinutesForm, Record<Language, string>> = {
  one: {
    me: '{count} minut',
    en: '{count} minute',
    sr: '{count} minut',
    hr: '{count} minuta',
    bs: '{count} minut',
    sl: '{count} minuta',
    mk: '{count} минута',
    sq: '{count} minutë',
    bg: '{count} минута',
    ro: '{count} minut',
    el: '{count} λεπτό',
    tr: '{count} dakika',
  },
  two: {
    me: '{count} minuta',
    en: '{count} minutes',
    sr: '{count} minuta',
    hr: '{count} minuta',
    bs: '{count} minuta',
    sl: '{count} minuti',
    mk: '{count} минути',
    sq: '{count} minuta',
    bg: '{count} минути',
    ro: '{count} de minute',
    el: '{count} λεπτά',
    tr: '{count} dakika',
  },
  few: {
    me: '{count} minuta',
    en: '{count} minutes',
    sr: '{count} minuta',
    hr: '{count} minute',
    bs: '{count} minuta',
    sl: '{count} minute',
    mk: '{count} минути',
    sq: '{count} minuta',
    bg: '{count} минути',
    ro: '{count} minute',
    el: '{count} λεπτά',
    tr: '{count} dakika',
  },
  other: {
    me: '{count} minuta',
    en: '{count} minutes',
    sr: '{count} minuta',
    hr: '{count} minuta',
    bs: '{count} minuta',
    sl: '{count} minut',
    mk: '{count} минути',
    sq: '{count} minuta',
    bg: '{count} минути',
    ro: '{count} de minute',
    el: '{count} λεπτά',
    tr: '{count} dakika',
  },
};

/** "15 minuta", "45 minuta": the count in the language's plural form. */
export function getMinutesLabel(count: number, language: Language): string {
  const forms = {
    one: ACOUSTIC_MINUTES_TRANSLATIONS.one[language],
    two: ACOUSTIC_MINUTES_TRANSLATIONS.two[language],
    few: ACOUSTIC_MINUTES_TRANSLATIONS.few[language],
    other: ACOUSTIC_MINUTES_TRANSLATIONS.other[language],
  };
  return selectPlural(count, language, forms).replace('{count}', String(count));
}

/**
 * In-app e-book purchase (ebook-plan § 3.14, § 4.3): the purchase sheet, the success page, the
 * site-buyer code and claim, downloads. `consent` is the ONE source of the withdrawal-waiver
 * text (CRD art. 16(m)): `node tools/i18n/export-consent.mjs` copies it to
 * `backend/app/book/consent_texts.json`, the server records the SHA-256 of the exact string
 * shown (critic CR-5), and a test fails on any drift. Changing that value needs a new consent
 * version (`--version ebook-consent-vN`). `disclaimer` is the site's reviewed
 * `pages.book.content.disclaimer` (Q11, WEB3), word for word. Legal and price keys are tagged in
 * tools/i18n/tags.json. The book itself is in Montenegrin (D9).
 */
export type BookPurchaseTranslationKey =
  | 'sheetTitle'
  | 'priceLabel'
  | 'oneTime'
  | 'includes'
  | 'functionality'
  | 'disclaimer'
  | 'sellerTitle'
  | 'consent'
  | 'consentHint'
  | 'buyCta'
  | 'continueToPayment'
  | 'paymentNote'
  | 'termsLink'
  | 'duplicateNote'
  | 'haveSiteCode'
  | 'redeemLabel'
  | 'redeemCta'
  | 'redeemInvalid'
  | 'claimBanner'
  | 'claimCta'
  | 'verifyToClaim'
  | 'owned'
  | 'successText'
  | 'processing'
  | 'checking'
  | 'refunded'
  | 'purchaseError'
  | 'consentStale'
  | 'downloadsTitle'
  | 'downloadPdf'
  | 'downloadEpub'
  | 'downloadError'
  | 'upsellTitle'
  | 'openBook';

const BOOK_PURCHASE_TRANSLATIONS: Record<BookPurchaseTranslationKey, Record<Language, string>> = {
  sheetTitle: {
    me: 'Kupovina e-knjige',
    en: 'Buy the e-book',
    sr: 'Kupovina e-knjige',
    hr: 'Kupnja e-knjige',
    bs: 'Kupovina e-knjige',
    sl: 'Nakup e-knjige',
    mk: 'Купување на е-книгата',
    sq: 'Blerja e librit elektronik',
    bg: 'Покупка на електронната книга',
    ro: 'Cumpărarea cărții electronice',
    el: 'Αγορά του ηλεκτρονικού βιβλίου',
    tr: 'E-kitap satın alma',
  },
  priceLabel: {
    me: 'Cijena: {price}',
    en: 'Price: {price}',
    sr: 'Cena: {price}',
    hr: 'Cijena: {price}',
    bs: 'Cijena: {price}',
    sl: 'Cena: {price}',
    mk: 'Цена: {price}',
    sq: 'Çmimi: {price}',
    bg: 'Цена: {price}',
    ro: 'Preț: {price}',
    el: 'Τιμή: {price}',
    tr: 'Fiyat: {price}',
  },
  oneTime: {
    me: 'Jednokratna kupovina, bez pretplate i automatske obnove. E-knjiga nije uključena ni u jedan paket pretplate.',
    en: 'One-time purchase, with no subscription and no automatic renewal. The e-book is not included in any subscription plan.',
    sr: 'Jednokratna kupovina, bez pretplate i automatskog obnavljanja. E-knjiga nije uključena ni u jedan paket pretplate.',
    hr: 'Jednokratna kupnja, bez pretplate i automatske obnove. E-knjiga nije uključena ni u jedan paket pretplate.',
    bs: 'Jednokratna kupovina, bez pretplate i automatske obnove. E-knjiga nije uključena ni u jedan paket pretplate.',
    sl: 'Enkraten nakup, brez naročnine in samodejnega podaljšanja. E-knjiga ni vključena v noben naročniški paket.',
    mk: 'Еднократно купување, без претплата и автоматско обновување. Е-книгата не е вклучена во ниту еден пакет на претплата.',
    sq: 'Blerje e njëhershme, pa abonim dhe pa rinovim automatik. Libri elektronik nuk përfshihet në asnjë paketë abonimi.',
    bg: 'Еднократна покупка, без абонамент и автоматично подновяване. Електронната книга не е включена в нито един абонаментен пакет.',
    ro: 'Cumpărare unică, fără abonament și fără reînnoire automată. Cartea electronică nu este inclusă în niciun pachet de abonament.',
    el: 'Εφάπαξ αγορά, χωρίς συνδρομή και χωρίς αυτόματη ανανέωση. Το ηλεκτρονικό βιβλίο δεν περιλαμβάνεται σε κανένα πακέτο συνδρομής.',
    tr: 'Tek seferlik satın alma; abonelik ve otomatik yenileme yok. E-kitap hiçbir abonelik paketine dahil değil.',
  },
  includes: {
    me: 'Dobijaš čitanje u aplikaciji i e-knjigu kao PDF i EPUB datoteku za preuzimanje.',
    en: 'You get reading in the app, and the e-book as a PDF and an EPUB file to download.',
    sr: 'Dobijaš čitanje u aplikaciji i e-knjigu kao PDF i EPUB datoteku za preuzimanje.',
    hr: 'Dobivaš čitanje u aplikaciji i e-knjigu kao PDF i EPUB datoteku za preuzimanje.',
    bs: 'Dobijaš čitanje u aplikaciji i e-knjigu kao PDF i EPUB datoteku za preuzimanje.',
    sl: 'Dobiš branje v aplikaciji in e-knjigo kot datoteki PDF in EPUB za prenos.',
    mk: 'Добиваш читање во апликацијата и е-книгата како PDF и EPUB датотека за преземање.',
    sq: 'Merr leximin në aplikacion dhe librin elektronik si skedar PDF dhe EPUB për ta shkarkuar.',
    bg: 'Получаваш четене в приложението и електронната книга като PDF и EPUB файл за изтегляне.',
    ro: 'Primești citirea în aplicație și cartea electronică în fișiere PDF și EPUB de descărcat.',
    el: 'Παίρνεις ανάγνωση στην εφαρμογή και το ηλεκτρονικό βιβλίο σε αρχείο PDF και EPUB για λήψη.',
    tr: 'Uygulamada okuma ve e-kitabı indirilebilir PDF ve EPUB dosyası olarak alırsın.',
  },
  functionality: {
    me: 'Za čitanje u aplikaciji potrebna je internet veza. Na početku i na kraju svakog poglavlja prikazuju se tvoja email adresa i kod primjerka. Datoteke za preuzimanje nemaju te oznake.',
    en: 'Reading in the app needs an internet connection. Your email address and a copy code are shown at the start and at the end of each chapter. The download files do not carry these marks.',
    sr: 'Za čitanje u aplikaciji potrebna je internet veza. Na početku i na kraju svakog poglavlja prikazuju se tvoja email adresa i kod primerka. Datoteke za preuzimanje nemaju te oznake.',
    hr: 'Za čitanje u aplikaciji potrebna je internetska veza. Na početku i na kraju svakog poglavlja prikazuju se tvoja email adresa i kod primjerka. Datoteke za preuzimanje nemaju te oznake.',
    bs: 'Za čitanje u aplikaciji potrebna je internet veza. Na početku i na kraju svakog poglavlja prikazuju se tvoja email adresa i kod primjerka. Datoteke za preuzimanje nemaju te oznake.',
    sl: 'Za branje v aplikaciji potrebuješ internetno povezavo. Na začetku in na koncu vsakega poglavja sta prikazana tvoj e-poštni naslov in koda izvoda. Datoteke za prenos teh oznak nimajo.',
    mk: 'За читање во апликацијата потребна е интернет врска. На почетокот и на крајот на секое поглавје се прикажуваат твојата е-пошта и кодот на примерокот. Датотеките за преземање ги немаат тие ознаки.',
    sq: 'Për leximin në aplikacion nevojitet lidhje interneti. Në fillim dhe në fund të çdo kapitulli shfaqen adresa jote e email-it dhe kodi i kopjes. Skedarët për shkarkim nuk i kanë këto shenja.',
    bg: 'За четене в приложението е нужна интернет връзка. В началото и в края на всяка глава се показват твоят имейл адрес и кодът на екземпляра. Изтеглените файлове нямат тези означения.',
    ro: 'Pentru citirea în aplicație ai nevoie de o conexiune la internet. La începutul și la sfârșitul fiecărui capitol apar adresa ta de email și un cod al exemplarului. Fișierele de descărcat nu au aceste marcaje.',
    el: 'Για την ανάγνωση στην εφαρμογή χρειάζεται σύνδεση στο διαδίκτυο. Στην αρχή και στο τέλος κάθε κεφαλαίου εμφανίζονται η διεύθυνση email σου και ένας κωδικός αντιτύπου. Τα αρχεία για λήψη δεν έχουν αυτές τις σημάνσεις.',
    tr: 'Uygulamada okumak için internet bağlantısı gerekir. Her bölümün başında ve sonunda e-posta adresin ve bir kopya kodu gösterilir. İndirilen dosyalarda bu işaretler yoktur.',
  },
  disclaimer: {
    me: 'Lukaaji je metoda ličnog razvoja i energetske prakse. Ne zamjenjuje medicinsku ili psihološku pomoć.',
    en: 'Lukaaji is a method of personal development and energy practice. It does not replace medical or psychological help.',
    sr: 'Lukaaji je metoda ličnog razvoja i energetske prakse. Ne zamenjuje medicinsku ili psihološku pomoć.',
    hr: 'Lukaaji je metoda osobnog razvoja i energetske prakse. Ne zamjenjuje medicinsku ili psihološku pomoć.',
    bs: 'Lukaaji je metoda ličnog razvoja i energetske prakse. Ne zamjenjuje medicinsku ili psihološku pomoć.',
    sl: 'Lukaaji je metoda osebnostne rasti in energijske prakse. Ne nadomešča zdravniške ali psihološke pomoči.',
    mk: 'Lukaaji е метод за личен развој и енергетска практика. Не ја заменува медицинската или психолошката помош.',
    sq: 'Lukaaji është një metodë e zhvillimit personal dhe e praktikës energjetike. Nuk e zëvendëson ndihmën mjekësore ose psikologjike.',
    bg: 'Lukaaji е метод за личностно развитие и енергийна практика. Не замества медицинската или психологическата помощ.',
    ro: 'Lukaaji este o metodă de dezvoltare personală și de practică energetică. Nu înlocuiește ajutorul medical sau psihologic.',
    el: 'Το Lukaaji είναι μια μέθοδος προσωπικής ανάπτυξης και ενεργειακής πρακτικής. Δεν αντικαθιστά την ιατρική ή ψυχολογική βοήθεια.',
    tr: 'Lukaaji, kişisel gelişim ve enerji pratiği yöntemidir. Tıbbi veya psikolojik yardımın yerini tutmaz.',
  },
  sellerTitle: {
    me: 'Prodavac',
    en: 'Seller',
    sr: 'Prodavac',
    hr: 'Prodavatelj',
    bs: 'Prodavac',
    sl: 'Prodajalec',
    mk: 'Продавач',
    sq: 'Shitësi',
    bg: 'Продавач',
    ro: 'Vânzător',
    el: 'Πωλητής',
    tr: 'Satıcı',
  },
  consent: {
    me: 'Tražim da mi pristup e-knjizi bude omogućen odmah nakon plaćanja i potvrđujem da znam da time, kada pristup počne, gubim pravo na odustanak od ugovora.',
    en: 'I ask for access to the e-book to start immediately after payment, and I acknowledge that, once access has started, I lose my right of withdrawal.',
    sr: 'Tražim da mi pristup e-knjizi bude omogućen odmah nakon plaćanja i potvrđujem da znam da time, kada pristup počne, gubim pravo na odustanak od ugovora.',
    hr: 'Tražim da mi pristup e-knjizi bude omogućen odmah nakon plaćanja i potvrđujem da znam da time, kada pristup započne, gubim pravo na jednostrani raskid ugovora.',
    bs: 'Tražim da mi pristup e-knjizi bude omogućen odmah nakon plaćanja i potvrđujem da znam da time, kada pristup počne, gubim pravo na odustanak od ugovora.',
    sl: 'Zahtevam, da mi je dostop do e-knjige omogočen takoj po plačilu, in potrjujem, da vem, da s tem, ko se dostop začne, izgubim pravico do odstopa od pogodbe.',
    mk: 'Барам пристапот до е-книгата да ми биде овозможен веднаш по плаќањето и потврдувам дека знам дека со тоа, кога пристапот ќе започне, го губам правото на откажување од договорот.',
    sq: 'Kërkoj që qasja në librin elektronik të më mundësohet menjëherë pas pagesës dhe konfirmoj se e di që, sapo të fillojë qasja, humb të drejtën e tërheqjes nga kontrata.',
    bg: 'Искам достъпът до електронната книга да ми бъде предоставен веднага след плащането и потвърждавам, че знам, че след като достъпът започне, губя правото си на отказ от договора.',
    ro: 'Solicit ca accesul la cartea electronică să îmi fie oferit imediat după plată și confirm că știu că, odată ce accesul a început, îmi pierd dreptul de retragere.',
    el: 'Ζητώ να μου δοθεί πρόσβαση στο ηλεκτρονικό βιβλίο αμέσως μετά την πληρωμή και επιβεβαιώνω ότι γνωρίζω πως, μόλις ξεκινήσει η πρόσβαση, χάνω το δικαίωμα υπαναχώρησης.',
    tr: 'E-kitaba erişimin ödemeden hemen sonra açılmasını istiyorum ve böylece erişim başladığında cayma hakkımı kaybedeceğimi bildiğimi onaylıyorum.',
  },
  consentHint: {
    me: 'Za nastavak na plaćanje označi polje iznad.',
    en: 'To continue to payment, tick the box above.',
    sr: 'Za nastavak na plaćanje označi polje iznad.',
    hr: 'Za nastavak na plaćanje označi polje iznad.',
    bs: 'Za nastavak na plaćanje označi polje iznad.',
    sl: 'Za nadaljevanje na plačilo označi polje zgoraj.',
    mk: 'За да продолжиш кон плаќање, означи го полето погоре.',
    sq: 'Për të vazhduar te pagesa, shëno kutinë më sipër.',
    bg: 'За да продължиш към плащането, отбележи полето отгоре.',
    ro: 'Ca să continui la plată, bifează căsuța de mai sus.',
    el: 'Για να συνεχίσεις στην πληρωμή, τσέκαρε το πλαίσιο παραπάνω.',
    tr: 'Ödemeye devam etmek için yukarıdaki kutuyu işaretle.',
  },
  buyCta: {
    me: 'Kupi e-knjigu — {price}',
    en: 'Buy the e-book — {price}',
    sr: 'Kupi e-knjigu — {price}',
    hr: 'Kupi e-knjigu — {price}',
    bs: 'Kupi e-knjigu — {price}',
    sl: 'Kupi e-knjigo — {price}',
    mk: 'Купи ја е-книгата — {price}',
    sq: 'Bli librin elektronik — {price}',
    bg: 'Купи електронната книга — {price}',
    ro: 'Cumpără cartea electronică — {price}',
    el: 'Αγόρασε το ηλεκτρονικό βιβλίο — {price}',
    tr: 'E-kitabı satın al — {price}',
  },
  continueToPayment: {
    me: 'Nastavi na plaćanje',
    en: 'Continue to payment',
    sr: 'Nastavi na plaćanje',
    hr: 'Nastavi na plaćanje',
    bs: 'Nastavi na plaćanje',
    sl: 'Nadaljuj na plačilo',
    mk: 'Продолжи кон плаќање',
    sq: 'Vazhdo te pagesa',
    bg: 'Продължи към плащането',
    ro: 'Continuă la plată',
    el: 'Συνέχισε στην πληρωμή',
    tr: 'Ödemeye devam et',
  },
  paymentNote: {
    me: 'Plaćaš karticom na Stripe stranici za plaćanje. Podatke o tvojoj kartici ne vidimo i ne čuvamo.',
    en: "You pay by card on Stripe's payment page. We do not see or store your card details.",
    sr: 'Plaćaš karticom na Stripe stranici za plaćanje. Podatke o tvojoj kartici ne vidimo i ne čuvamo.',
    hr: 'Plaćaš karticom na Stripeovoj stranici za plaćanje. Podatke o tvojoj kartici ne vidimo i ne pohranjujemo.',
    bs: 'Plaćaš karticom na Stripe stranici za plaćanje. Podatke o tvojoj kartici ne vidimo i ne čuvamo.',
    sl: 'Plačaš s kartico na Stripovi plačilni strani. Podatkov o tvoji kartici ne vidimo in jih ne hranimo.',
    mk: 'Плаќаш со картичка на страницата за плаќање на Stripe. Податоците за твојата картичка не ги гледаме и не ги чуваме.',
    sq: 'Paguan me kartë në faqen e pagesës së Stripe. Të dhënat e kartës sate nuk i shohim dhe nuk i ruajmë.',
    bg: 'Плащаш с карта на страницата за плащане на Stripe. Не виждаме и не съхраняваме данните на картата ти.',
    ro: 'Plătești cu cardul pe pagina de plată Stripe. Nu vedem și nu păstrăm datele cardului tău.',
    el: 'Πληρώνεις με κάρτα στη σελίδα πληρωμής της Stripe. Δεν βλέπουμε και δεν αποθηκεύουμε τα στοιχεία της κάρτας σου.',
    tr: 'Stripe ödeme sayfasında kartla ödersin. Kart bilgilerini görmüyor ve saklamıyoruz.',
  },
  termsLink: {
    me: 'Uslovi kupovine e-knjige',
    en: 'E-book terms of sale',
    sr: 'Uslovi kupovine e-knjige',
    hr: 'Uvjeti kupnje e-knjige',
    bs: 'Uslovi kupovine e-knjige',
    sl: 'Pogoji nakupa e-knjige',
    mk: 'Услови за купување на е-книгата',
    sq: 'Kushtet e blerjes së librit elektronik',
    bg: 'Условия за покупка на електронната книга',
    ro: 'Condițiile de cumpărare a cărții electronice',
    el: 'Όροι αγοράς του ηλεκτρονικού βιβλίου',
    tr: 'E-kitap satın alma koşulları',
  },
  duplicateNote: {
    me: 'Ako e-knjigu greškom platiš dva puta, vraćamo ti novac za drugu uplatu.',
    en: 'If you pay for the e-book twice by mistake, we return the money for the second payment.',
    sr: 'Ako e-knjigu greškom platiš dva puta, vraćamo ti novac za drugu uplatu.',
    hr: 'Ako e-knjigu greškom platiš dvaput, vraćamo ti novac za drugu uplatu.',
    bs: 'Ako e-knjigu greškom platiš dva puta, vraćamo ti novac za drugu uplatu.',
    sl: 'Če e-knjigo pomotoma plačaš dvakrat, ti vrnemo znesek drugega plačila.',
    mk: 'Ако по грешка ја платиш е-книгата двапати, ти го враќаме износот од второто плаќање.',
    sq: 'Nëse e paguan librin elektronik dy herë gabimisht, ta kthejmë shumën e pagesës së dytë.',
    bg: 'Ако по погрешка платиш електронната книга два пъти, връщаме ти сумата на второто плащане.',
    ro: 'Dacă plătești din greșeală cartea electronică de două ori, îți returnăm suma celei de-a doua plăți.',
    el: 'Αν πληρώσεις κατά λάθος το ηλεκτρονικό βιβλίο δύο φορές, σου επιστρέφουμε το ποσό της δεύτερης πληρωμής.',
    tr: 'E-kitabın parasını yanlışlıkla iki kez ödersen ikinci ödemenin tutarını sana geri öderiz.',
  },
  haveSiteCode: {
    me: 'Već imaš e-knjigu sa lukaaji.com? Ne plaćaj ponovo: unesi kod iz emaila kojim smo ti je poslali.',
    en: 'Already have the e-book from lukaaji.com? Do not pay again: enter the code from the email we sent it with.',
    sr: 'Već imaš e-knjigu sa lukaaji.com? Ne plaćaj ponovo: unesi kod iz emaila kojim smo ti je poslali.',
    hr: 'Već imaš e-knjigu s lukaaji.com? Ne plaćaj ponovno: unesi kod iz emaila kojim smo ti je poslali.',
    bs: 'Već imaš e-knjigu sa lukaaji.com? Ne plaćaj ponovo: unesi kod iz emaila kojim smo ti je poslali.',
    sl: 'Že imaš e-knjigo z lukaaji.com? Ne plačaj znova: vnesi kodo iz e-pošte, s katero smo ti jo poslali.',
    mk: 'Веќе ја имаш е-книгата од lukaaji.com? Не плаќај повторно: внеси го кодот од е-поштата со која ти ја испративме.',
    sq: 'E ke tashmë librin elektronik nga lukaaji.com? Mos paguaj sërish: fut kodin nga email-i me të cilin ta dërguam.',
    bg: 'Вече имаш електронната книга от lukaaji.com? Не плащай отново: въведи кода от имейла, с който ти я изпратихме.',
    ro: 'Ai deja cartea electronică de pe lukaaji.com? Nu plăti din nou: introdu codul din emailul cu care ți-am trimis-o.',
    el: 'Έχεις ήδη το ηλεκτρονικό βιβλίο από το lukaaji.com; Μην πληρώσεις ξανά: πληκτρολόγησε τον κωδικό από το email με το οποίο σου το στείλαμε.',
    tr: "E-kitabı lukaaji.com'dan zaten aldın mı? Yeniden ödeme yapma: kitabı gönderdiğimiz e-postadaki kodu gir.",
  },
  redeemLabel: {
    me: 'Kod iz emaila',
    en: 'Code from the email',
    sr: 'Kod iz emaila',
    hr: 'Kod iz emaila',
    bs: 'Kod iz emaila',
    sl: 'Koda iz e-pošte',
    mk: 'Код од е-поштата',
    sq: 'Kodi nga email-i',
    bg: 'Код от имейла',
    ro: 'Codul din email',
    el: 'Κωδικός από το email',
    tr: 'E-postadaki kod',
  },
  redeemCta: {
    me: 'Iskoristi kod',
    en: 'Use the code',
    sr: 'Iskoristi kod',
    hr: 'Iskoristi kod',
    bs: 'Iskoristi kod',
    sl: 'Unovči kodo',
    mk: 'Искористи го кодот',
    sq: 'Përdor kodin',
    bg: 'Използвай кода',
    ro: 'Folosește codul',
    el: 'Χρησιμοποίησε τον κωδικό',
    tr: 'Kodu kullan',
  },
  redeemInvalid: {
    me: 'Kod nije prihvaćen. Provjeri ga i pokušaj ponovo.',
    en: 'The code was not accepted. Check it and try again.',
    sr: 'Kod nije prihvaćen. Proveri ga i pokušaj ponovo.',
    hr: 'Kod nije prihvaćen. Provjeri ga i pokušaj ponovno.',
    bs: 'Kod nije prihvaćen. Provjeri ga i pokušaj ponovo.',
    sl: 'Koda ni bila sprejeta. Preveri jo in poskusi znova.',
    mk: 'Кодот не е прифатен. Провери го и обиди се повторно.',
    sq: 'Kodi nuk u pranua. Kontrolloje dhe provo përsëri.',
    bg: 'Кодът не е приет. Провери го и опитай отново.',
    ro: 'Codul nu a fost acceptat. Verifică-l și încearcă din nou.',
    el: 'Ο κωδικός δεν έγινε δεκτός. Έλεγξέ τον και δοκίμασε ξανά.',
    tr: 'Kod kabul edilmedi. Kontrol et ve tekrar dene.',
  },
  claimBanner: {
    me: 'Za tvoju email adresu postoji kupovina e-knjige na lukaaji.com. Dodaj je na svoj nalog bez ponovnog plaćanja.',
    en: 'There is an e-book purchase on lukaaji.com for your email address. Add it to your account without paying again.',
    sr: 'Za tvoju email adresu postoji kupovina e-knjige na lukaaji.com. Dodaj je na svoj nalog bez ponovnog plaćanja.',
    hr: 'Za tvoju email adresu postoji kupnja e-knjige na lukaaji.com. Dodaj je na svoj račun bez ponovnog plaćanja.',
    bs: 'Za tvoju email adresu postoji kupovina e-knjige na lukaaji.com. Dodaj je na svoj nalog bez ponovnog plaćanja.',
    sl: 'Za tvoj e-poštni naslov obstaja nakup e-knjige na lukaaji.com. Dodaj jo v svoj račun brez ponovnega plačila.',
    mk: 'За твојата е-пошта постои купување на е-книгата на lukaaji.com. Додај ја на својата сметка без повторно плаќање.',
    sq: 'Për adresën tënde të email-it ekziston një blerje e librit elektronik në lukaaji.com. Shtoje në llogarinë tënde pa paguar sërish.',
    bg: 'За твоя имейл адрес има покупка на електронната книга от lukaaji.com. Добави я към акаунта си, без да плащаш отново.',
    ro: 'Pentru adresa ta de email există o cumpărare a cărții electronice pe lukaaji.com. Adaug-o în contul tău fără să plătești din nou.',
    el: 'Για τη διεύθυνση email σου υπάρχει αγορά του ηλεκτρονικού βιβλίου στο lukaaji.com. Πρόσθεσέ το στον λογαριασμό σου χωρίς να πληρώσεις ξανά.',
    tr: "E-posta adresin için lukaaji.com'da bir e-kitap satın alımı var. Yeniden ödemeden hesabına ekle.",
  },
  claimCta: {
    me: 'Dodaj e-knjigu na svoj nalog',
    en: 'Add the e-book to your account',
    sr: 'Dodaj e-knjigu na svoj nalog',
    hr: 'Dodaj e-knjigu na svoj račun',
    bs: 'Dodaj e-knjigu na svoj nalog',
    sl: 'Dodaj e-knjigo v svoj račun',
    mk: 'Додај ја е-книгата на својата сметка',
    sq: 'Shto librin elektronik në llogarinë tënde',
    bg: 'Добави електронната книга към профила си',
    ro: 'Adaugă cartea electronică în contul tău',
    el: 'Πρόσθεσε το ηλεκτρονικό βιβλίο στον λογαριασμό σου',
    tr: 'E-kitabı hesabına ekle',
  },
  verifyToClaim: {
    me: 'Potvrdi svoju email adresu, pa ćemo ti dodati e-knjigu kupljenu na lukaaji.com.',
    en: 'Confirm your email address, and we will add the e-book bought on lukaaji.com to your account.',
    sr: 'Potvrdi svoju email adresu, pa ćemo ti dodati e-knjigu kupljenu na lukaaji.com.',
    hr: 'Potvrdi svoju email adresu i dodat ćemo ti e-knjigu kupljenu na lukaaji.com.',
    bs: 'Potvrdi svoju email adresu, pa ćemo ti dodati e-knjigu kupljenu na lukaaji.com.',
    sl: 'Potrdi svoj e-poštni naslov in dodali ti bomo e-knjigo, kupljeno na lukaaji.com.',
    mk: 'Потврди ја својата е-пошта и ќе ти ја додадеме е-книгата купена на lukaaji.com.',
    sq: 'Konfirmo adresën tënde të email-it dhe do të ta shtojmë librin elektronik të blerë në lukaaji.com.',
    bg: 'Потвърди имейл адреса си и ще ти добавим електронната книга, купена от lukaaji.com.',
    ro: 'Confirmă-ți adresa de email și îți adăugăm cartea electronică cumpărată pe lukaaji.com.',
    el: 'Επιβεβαίωσε τη διεύθυνση email σου και θα σου προσθέσουμε το ηλεκτρονικό βιβλίο που αγοράστηκε στο lukaaji.com.',
    tr: "E-posta adresini onayla, lukaaji.com'dan alınan e-kitabı hesabına ekleyelim.",
  },
  owned: {
    me: 'E-knjiga je tvoja.',
    en: 'The e-book is yours.',
    sr: 'E-knjiga je tvoja.',
    hr: 'E-knjiga je tvoja.',
    bs: 'E-knjiga je tvoja.',
    sl: 'E-knjiga je tvoja.',
    mk: 'Е-книгата е твоја.',
    sq: 'Libri elektronik është yti.',
    bg: 'Електронната книга е твоя.',
    ro: 'Cartea electronică este a ta.',
    el: 'Το ηλεκτρονικό βιβλίο είναι δικό σου.',
    tr: 'E-kitap artık senin.',
  },
  successText: {
    me: 'Potvrdu kupovine smo ti poslali emailom.',
    en: 'We have sent you the purchase confirmation by email.',
    sr: 'Potvrdu kupovine smo ti poslali emailom.',
    hr: 'Potvrdu kupnje poslali smo ti emailom.',
    bs: 'Potvrdu kupovine smo ti poslali emailom.',
    sl: 'Potrdilo o nakupu smo ti poslali po e-pošti.',
    mk: 'Потврдата за купувањето ти ја испративме по е-пошта.',
    sq: 'Konfirmimin e blerjes ta dërguam me email.',
    bg: 'Изпратихме ти потвърждение за покупката по имейл.',
    ro: 'Ți-am trimis confirmarea cumpărării pe email.',
    el: 'Σου στείλαμε την επιβεβαίωση της αγοράς με email.',
    tr: 'Satın alma onayını sana e-postayla gönderdik.',
  },
  processing: {
    me: 'Plaćanje se još obrađuje. Osvježi stranicu za minut.',
    en: 'Your payment is still being processed. Refresh the page in a minute.',
    sr: 'Plaćanje se još obrađuje. Osveži stranicu za minut.',
    hr: 'Plaćanje se još obrađuje. Osvježi stranicu za minutu.',
    bs: 'Plaćanje se još obrađuje. Osvježi stranicu za minut.',
    sl: 'Plačilo se še obdeluje. Čez minuto osveži stran.',
    mk: 'Плаќањето сè уште се обработува. Освежи ја страницата за една минута.',
    sq: 'Pagesa ende po përpunohet. Rifresko faqen pas një minute.',
    bg: 'Плащането все още се обработва. Опресни страницата след минута.',
    ro: 'Plata este încă în curs de procesare. Reîncarcă pagina peste un minut.',
    el: 'Η πληρωμή επεξεργάζεται ακόμη. Ανανέωσε τη σελίδα σε ένα λεπτό.',
    tr: 'Ödeme hâlâ işleniyor. Bir dakika sonra sayfayı yenile.',
  },
  checking: {
    me: 'Provjeravamo plaćanje…',
    en: 'Checking your payment…',
    sr: 'Proveravamo plaćanje…',
    hr: 'Provjeravamo plaćanje…',
    bs: 'Provjeravamo plaćanje…',
    sl: 'Preverjamo plačilo …',
    mk: 'Го проверуваме плаќањето…',
    sq: 'Po kontrollojmë pagesën…',
    bg: 'Проверяваме плащането…',
    ro: 'Verificăm plata…',
    el: 'Ελέγχουμε την πληρωμή…',
    tr: 'Ödeme kontrol ediliyor…',
  },
  refunded: {
    me: 'Ova uplata je vraćena, pa e-knjiga nije na tvom nalogu.',
    en: 'The money for this payment was returned, so the e-book is not on your account.',
    sr: 'Ova uplata je vraćena, pa e-knjiga nije na tvom nalogu.',
    hr: 'Ova uplata je vraćena pa e-knjiga nije na tvom računu.',
    bs: 'Ova uplata je vraćena, pa e-knjiga nije na tvom nalogu.',
    sl: 'To plačilo je bilo vrnjeno, zato e-knjige ni v tvojem računu.',
    mk: 'Ова плаќање е вратено, па е-книгата не е на твојата сметка.',
    sq: 'Kjo pagesë është kthyer, prandaj libri elektronik nuk është në llogarinë tënde.',
    bg: 'Това плащане е възстановено, затова електронната книга не е в акаунта ти.',
    ro: 'Suma acestei plăți a fost returnată, așa că cartea electronică nu este în contul tău.',
    el: "Αυτή η πληρωμή επιστράφηκε, γι' αυτό το ηλεκτρονικό βιβλίο δεν είναι στον λογαριασμό σου.",
    tr: 'Bu ödemenin tutarı sana geri ödendi, bu yüzden e-kitap hesabında değil.',
  },
  purchaseError: {
    me: 'Plaćanje trenutno ne može da počne. Pokušaj ponovo za nekoliko minuta.',
    en: 'The payment cannot start right now. Try again in a few minutes.',
    sr: 'Plaćanje trenutno ne može da počne. Pokušaj ponovo za nekoliko minuta.',
    hr: 'Plaćanje trenutno ne može započeti. Pokušaj ponovno za nekoliko minuta.',
    bs: 'Plaćanje trenutno ne može početi. Pokušaj ponovo za nekoliko minuta.',
    sl: 'Plačila trenutno ni mogoče začeti. Poskusi znova čez nekaj minut.',
    mk: 'Плаќањето моментално не може да започне. Обиди се повторно за неколку минути.',
    sq: 'Pagesa nuk mund të nisë tani. Provo përsëri pas disa minutash.',
    bg: 'Плащането не може да започне в момента. Опитай отново след няколко минути.',
    ro: 'Plata nu poate începe acum. Încearcă din nou peste câteva minute.',
    el: 'Η πληρωμή δεν μπορεί να ξεκινήσει αυτή τη στιγμή. Δοκίμασε ξανά σε λίγα λεπτά.',
    tr: 'Ödeme şu anda başlatılamıyor. Birkaç dakika sonra tekrar dene.',
  },
  consentStale: {
    me: 'Saglasnost više ne važi. Pročitaj tekst ponovo i označi polje.',
    en: 'The consent is no longer valid. Read the text again and tick the box.',
    sr: 'Saglasnost više ne važi. Pročitaj tekst ponovo i označi polje.',
    hr: 'Suglasnost više ne vrijedi. Pročitaj tekst ponovno i označi polje.',
    bs: 'Saglasnost više ne važi. Pročitaj tekst ponovo i označi polje.',
    sl: 'Soglasje ne velja več. Ponovno preberi besedilo in označi polje.',
    mk: 'Согласноста повеќе не важи. Прочитај го текстот повторно и означи го полето.',
    sq: 'Pëlqimi nuk është më i vlefshëm. Lexoje tekstin përsëri dhe shëno kutinë.',
    bg: 'Съгласието вече не е валидно. Прочети текста отново и отбележи полето.',
    ro: 'Acordul nu mai este valabil. Citește din nou textul și bifează căsuța.',
    el: 'Η συγκατάθεση δεν ισχύει πια. Διάβασε ξανά το κείμενο και τσέκαρε το πλαίσιο.',
    tr: 'Onay artık geçerli değil. Metni yeniden oku ve kutuyu işaretle.',
  },
  downloadsTitle: {
    me: 'Preuzimanje',
    en: 'Downloads',
    sr: 'Preuzimanje',
    hr: 'Preuzimanje',
    bs: 'Preuzimanje',
    sl: 'Prenos',
    mk: 'Преземање',
    sq: 'Shkarkimi',
    bg: 'Изтегляне',
    ro: 'Descărcare',
    el: 'Λήψη',
    tr: 'İndirme',
  },
  downloadPdf: {
    me: 'Preuzmi PDF',
    en: 'Download the PDF',
    sr: 'Preuzmi PDF',
    hr: 'Preuzmi PDF',
    bs: 'Preuzmi PDF',
    sl: 'Prenesi PDF',
    mk: 'Преземи PDF',
    sq: 'Shkarko PDF-në',
    bg: 'Изтегли PDF',
    ro: 'Descarcă PDF-ul',
    el: 'Κατέβασε το PDF',
    tr: "PDF'yi indir",
  },
  downloadEpub: {
    me: 'Preuzmi EPUB',
    en: 'Download the EPUB',
    sr: 'Preuzmi EPUB',
    hr: 'Preuzmi EPUB',
    bs: 'Preuzmi EPUB',
    sl: 'Prenesi EPUB',
    mk: 'Преземи EPUB',
    sq: 'Shkarko EPUB-in',
    bg: 'Изтегли EPUB',
    ro: 'Descarcă EPUB-ul',
    el: 'Κατέβασε το EPUB',
    tr: "EPUB'u indir",
  },
  downloadError: {
    me: 'Preuzimanje trenutno nije moguće. Pokušaj ponovo.',
    en: 'The download is not available right now. Try again.',
    sr: 'Preuzimanje trenutno nije moguće. Pokušaj ponovo.',
    hr: 'Preuzimanje trenutno nije moguće. Pokušaj ponovno.',
    bs: 'Preuzimanje trenutno nije moguće. Pokušaj ponovo.',
    sl: 'Prenos trenutno ni mogoč. Poskusi znova.',
    mk: 'Преземањето моментално не е можно. Обиди се повторно.',
    sq: 'Shkarkimi nuk është i mundur tani. Provo përsëri.',
    bg: 'Изтеглянето не е възможно в момента. Опитай отново.',
    ro: 'Descărcarea nu este posibilă acum. Încearcă din nou.',
    el: 'Η λήψη δεν είναι δυνατή αυτή τη στιγμή. Δοκίμασε ξανά.',
    tr: 'İndirme şu anda mümkün değil. Tekrar dene.',
  },
  upsellTitle: {
    me: 'Dodaj e-knjigu',
    en: 'Add the e-book',
    sr: 'Dodaj e-knjigu',
    hr: 'Dodaj e-knjigu',
    bs: 'Dodaj e-knjigu',
    sl: 'Dodaj e-knjigo',
    mk: 'Додај ја е-книгата',
    sq: 'Shto librin elektronik',
    bg: 'Добави електронната книга',
    ro: 'Adaugă cartea electronică',
    el: 'Πρόσθεσε το ηλεκτρονικό βιβλίο',
    tr: 'E-kitabı ekle',
  },
  openBook: {
    me: 'Otvori knjigu',
    en: 'Open the book',
    sr: 'Otvori knjigu',
    hr: 'Otvori knjigu',
    bs: 'Otvori knjigu',
    sl: 'Odpri knjigo',
    mk: 'Отвори ја книгата',
    sq: 'Hap librin',
    bg: 'Отвори книгата',
    ro: 'Deschide cartea',
    el: 'Άνοιξε το βιβλίο',
    tr: 'Kitabı aç',
  },
};

export const BOOK_PURCHASE_TRANSLATION_KEYS = Object.keys(BOOK_PURCHASE_TRANSLATIONS) as BookPurchaseTranslationKey[];

/**
 * Get a translated e-book purchase string for the given language.
 */
export function getBookPurchaseTranslation(key: BookPurchaseTranslationKey, language: Language): string {
  return BOOK_PURCHASE_TRANSLATIONS[key][language];
}
